import { makeApi, Zodios, type ZodiosOptions } from "@zodios/core";
import { z } from "zod";

const AdasDto = z
  .object({
    createdAt: z.string().datetime({ offset: true }),
    createdById: z.string().uuid(),
    description: z.string(),
    id: z.string().uuid(),
    name: z.string(),
    updatedAt: z.string().datetime({ offset: true }),
    updatedById: z.string().uuid(),
  })
  .passthrough();
const CreateAdasDto = z.object({ description: z.string(), idempotencyKey: z.string(), name: z.string() });
const UpdateAdasDto = z.object({ description: z.string(), name: z.string() }).partial();
const executeAdminAction_Body = z
  .object({ action: z.string(), payload: z.object({}).partial().passthrough().optional() })
  .passthrough();
const createSepaDebitMandate_Body = z
  .object({
    bic: z.string(),
    city: z.string(),
    firstName: z.string(),
    iban: z.string(),
    lastName: z.string(),
    placeAndDate: z.string(),
    postalCode: z.string(),
    quoteId: z.string(),
    sepaConsent: z.boolean(),
    street: z.string(),
  })
  .passthrough();
const createVogelAppointment_Body = z
  .object({ autovioCalendarEventId: z.string(), autovioUserId: z.string() })
  .passthrough();
const createVogelExportAuth_Body = z
  .object({
    apiBaseUrl: z.string(),
    apiKey: z.string(),
    drivingSchoolId: z.string(),
    jwt: z.string(),
    referer: z.string(),
    userAgent: z.string(),
  })
  .passthrough();
const updateVogelExportAuth_Body = z
  .object({
    apiBaseUrl: z.string(),
    apiKey: z.string(),
    drivingSchoolId: z.string(),
    jwt: z.string(),
    referer: z.string(),
    userAgent: z.string(),
  })
  .partial()
  .passthrough();
const AppliedReferralCodeDto = z
  .object({
    drivingSchool: z.object({ id: z.string(), name: z.string() }).passthrough(),
    lastAppliedAt: z.string().datetime({ offset: true }),
    numApplied: z.number(),
    referralCode: z.string(),
    student: z
      .object({
        id: z.string(),
        name: z.string(),
        status: z.enum(["active", "cancelled", "inactive", "onHold", "outstandingPayments", "completed", "unknown"]),
        verboseStatus: z.string(),
      })
      .passthrough(),
  })
  .passthrough();
const AttachmentDto = z
  .object({
    createdAt: z.string().datetime({ offset: true }),
    createdById: z.string().uuid(),
    id: z.string().uuid(),
    mimeType: z.string(),
    name: z.string(),
    noteId: z.string().uuid().nullish(),
    path: z.string(),
    postId: z.string().uuid().nullish(),
    size: z.number().int().gte(-9007199254740991).lte(9007199254740991),
    updatedAt: z.string().datetime({ offset: true }),
    updatedById: z.string().uuid(),
  })
  .passthrough();
const CreateDrivingSchoolAutoFormDto = z.object({
  description: z.string().nullish(),
  drivingSchoolId: z.string(),
  idempotencyKey: z.string(),
  isForRegistration: z.boolean(),
  name: z.string(),
});
const JsonValue = z.unknown();
const DrivingSchoolAutoFormDto = z
  .object({
    deleted: z.boolean(),
    deletedAt: z.string().datetime({ offset: true }).nullish(),
    deletedById: z.string().uuid().nullish(),
    description: z.string().nullish(),
    drivingSchoolId: z.string().nullish(),
    id: z.string().uuid(),
    isForRegistration: z.boolean(),
    name: z.string(),
    pdfTemplate: JsonValue.nullable(),
    pdfTemplateBaseFile: z
      .object({
        attachmentId: z.string().uuid(),
        fileName: z.string(),
        fileSize: z.number().int().gte(-9007199254740991).lte(9007199254740991),
      })
      .passthrough(),
  })
  .passthrough();
const UpdateDrivingSchoolAutoFormDto = z
  .object({
    description: z.string().nullable(),
    isForRegistration: z.boolean(),
    name: z.string(),
    pdfTemplate: JsonValue.nullable(),
  })
  .partial();
const UserDto = z
  .object({
    autovioUserId: z.string().optional(),
    avatarUrl: z.string().optional(),
    createdAt: z.string().datetime({ offset: true }),
    disabled: z.boolean(),
    firebaseAuthUserUid: z.string().optional(),
    firstName: z.string(),
    isAutovioStaff: z.boolean().optional(),
    lastName: z.string(),
    phoneNumber: z.string().nullish(),
    providerData: z
      .array(
        z
          .object({
            displayName: z.string().optional(),
            email: z.string().optional(),
            phoneNumber: z.string().optional(),
            photoURL: z.string().optional(),
            providerId: z.string(),
            uid: z.string(),
          })
          .passthrough(),
      )
      .optional()
      .default([]),
    updatedAt: z.string().datetime({ offset: true }),
  })
  .passthrough();
const BranchDto = z
  .object({
    active: z.boolean(),
    address: z
      .object({
        city: z.string(),
        company: z.string(),
        country: z.string(),
        email: z.string().nullish(),
        geoError: z.string().nullish(),
        geoLat: z.unknown().nullish(),
        geoLng: z.unknown().nullish(),
        phone: z.string().nullish(),
        postalCode: z.string(),
        state: z.string().nullish(),
        street: z.string(),
        type: z.enum(["InvoiceAddress", "PostalAddress"]),
      })
      .passthrough(),
    addressId: z.string().uuid().nullish(),
    contractAddress: z
      .object({
        city: z.string(),
        company: z.string(),
        country: z.string(),
        email: z.string().nullish(),
        geoError: z.string().nullish(),
        geoLat: z.unknown().nullish(),
        geoLng: z.unknown().nullish(),
        phone: z.string().nullish(),
        postalCode: z.string(),
        state: z.string().nullish(),
        street: z.string(),
        type: z.enum(["InvoiceAddress", "PostalAddress"]),
      })
      .passthrough()
      .nullish(),
    contractAddressId: z.string().uuid().nullish(),
    createdAt: z.string().datetime({ offset: true }),
    createdById: z.string().uuid(),
    datevId: z.string().nullish(),
    defaultDrivingSchoolManagerId: z.string().nullish(),
    drivingSchoolId: z.string(),
    endDate: z.string().nullish(),
    fahrschuleLiveId: z.number().int().gte(-9007199254740991).lte(9007199254740991).nullish(),
    id: z.string().uuid(),
    invoiceAddress: z
      .object({
        city: z.string(),
        company: z.string(),
        country: z.string(),
        email: z.string().nullish(),
        geoError: z.string().nullish(),
        geoLat: z.unknown().nullish(),
        geoLng: z.unknown().nullish(),
        phone: z.string().nullish(),
        postalCode: z.string(),
        state: z.string().nullish(),
        street: z.string(),
        type: z.enum(["InvoiceAddress", "PostalAddress"]),
      })
      .passthrough()
      .nullish(),
    invoiceAddressId: z.string().uuid().nullish(),
    isDefaultBranch: z.boolean(),
    location: z.string().nullish(),
    maxStudentsForTheory: z.number().int().gte(-9007199254740991).lte(9007199254740991),
    name: z.string(),
    paymentTerminalId: z.string().uuid().nullish(),
    publicHolidays: z.array(z.object({ date: z.number(), name: z.string() }).passthrough()).optional(),
    startDate: z.string(),
    updatedAt: z.string().datetime({ offset: true }),
    updatedById: z.string().uuid(),
    vogelId: z.string().uuid().nullish(),
  })
  .passthrough();
const CreateBranchDto = z.object({
  active: z.boolean().optional(),
  address: z
    .object({ city: z.string(), company: z.string(), postalCode: z.string(), street: z.string() })
    .passthrough(),
  contractAddress: z
    .object({ city: z.string(), company: z.string(), postalCode: z.string(), street: z.string() })
    .passthrough()
    .nullish(),
  datevId: z.string().nullish(),
  defaultDrivingSchoolManagerId: z.string().nullish(),
  drivingSchoolId: z.string(),
  endDate: z.string().nullish(),
  invoiceAddress: z
    .object({ city: z.string(), company: z.string(), postalCode: z.string(), street: z.string() })
    .passthrough()
    .nullish(),
  isDefaultBranch: z.boolean().optional(),
  location: z.string().nullish(),
  maxStudentsForTheory: z.number().int().gte(-9007199254740991).lte(9007199254740991),
  name: z.string(),
  paymentTerminalId: z.string().uuid().nullish(),
  startDate: z.string().optional(),
});
const UpdateBranchDto = z
  .object({
    active: z.boolean(),
    address: z
      .object({ city: z.string(), company: z.string(), postalCode: z.string(), street: z.string() })
      .partial()
      .passthrough(),
    contractAddress: z
      .object({ city: z.string(), company: z.string(), postalCode: z.string(), street: z.string() })
      .passthrough()
      .nullable(),
    datevId: z.string().nullable(),
    defaultDrivingSchoolManagerId: z.string().nullable(),
    endDate: z.string().nullable(),
    invoiceAddress: z
      .object({ city: z.string(), company: z.string(), postalCode: z.string(), street: z.string() })
      .passthrough()
      .nullable(),
    isDefaultBranch: z.boolean(),
    location: z.string().nullable(),
    maxStudentsForTheory: z.number().int().gte(-9007199254740991).lte(9007199254740991),
    name: z.string(),
    paymentTerminalId: z.string().uuid().nullable(),
    startDate: z.string(),
  })
  .partial();
const MoneyDto = z.object({ amount: z.union([z.number(), z.string()]), currency: z.literal("EUR") }).passthrough();
const CancellationPolicyFixedFeeDto = z.object({ fixed: MoneyDto });
const CancellationPolicyPercentageFeeDto = z.object({ percentage: z.number().int().gte(0).lte(100) });
const CancellationPolicyFeeDto = z.union([CancellationPolicyFixedFeeDto, CancellationPolicyPercentageFeeDto]);
const CancellationPolicyPreconditionDto = z.object({
  op: z.literal("<"),
  value: z.string().regex(/^([1-9][0-9]*)([hd])$/),
  var: z.enum(["timeSinceBooked", "timeBeforeStart"]),
});
const CancellationPolicyRuleDto = z.object({
  fee: CancellationPolicyFeeDto,
  precondition: CancellationPolicyPreconditionDto,
});
const CancellationPolicyDto = z.object({
  noShowFee: CancellationPolicyFeeDto.optional(),
  rules: z.array(CancellationPolicyRuleDto).min(1),
});
const AddStudentToCourseDto = z
  .object({
    agreedCancellationPolicy: CancellationPolicyDto.optional(),
    courseUid: z.string().min(1),
    studentUid: z.string().min(1),
  })
  .passthrough();
const PostalAddressDto = z.object({ city: z.string(), postalCode: z.string(), street: z.string() }).passthrough();
const ASFCourseSessionTypeEnum = z.enum(["sitzung1", "fahrprobe", "sitzung2", "sitzung3", "sitzung4"]);
const ASFCourseSessionDto = z
  .object({
    end: z.string().datetime({ offset: true }),
    instructor: z.object({ avatarUrl: z.string().optional(), name: z.string(), uid: z.string() }).passthrough(),
    location: PostalAddressDto,
    sessionType: ASFCourseSessionTypeEnum,
    start: z.string().datetime({ offset: true }),
    uid: z.string(),
  })
  .passthrough();
const ASFCourseDto = z
  .object({
    appointments: z.array(ASFCourseSessionDto),
    numFreeSeats: z.number().int().gte(-9007199254740991).lte(9007199254740991),
    type: z.literal("ASFCourse"),
    uid: z.string(),
  })
  .passthrough();
const BookTheoryLessonsDto = z.object({
  agreedCancellationPolicy: CancellationPolicyDto.optional(),
  theoryLessonsIds: z.array(z.string()).min(1),
});
const AcceptInviteDto = z.object({ agreedCancellationPolicy: CancellationPolicyDto }).partial();
const TheoryLessonAttendanceEnum = z.enum(["booked", "cancelled", "attended", "no-show"]);
const UpdateAttendanceDto = z.object({ attendance: z.record(z.union([z.boolean(), TheoryLessonAttendanceEnum])) });
const CancelBookedAppointmentDto = z.object({ cancelReason: z.string() }).partial();
const UpdateExamResultDto = z.object({ result: z.enum(["passed", "failed", "canceledByStudent", "studentNoShow"]) });
const UpdateTheoryLessonStudentBookedTrainingsDto = z.object({ bookedTrainingIds: z.array(z.string()).min(1) });
const SingleFilter = z
  .object({
    field: z.string(),
    type: z.enum([
      "=",
      "<",
      "<=",
      ">",
      ">=",
      "!=",
      "like",
      "ilike",
      "contains",
      "icontains",
      "search",
      "isearch",
      "startswith",
      "istartswith",
      "endswith",
      "iendswith",
      "eqstring",
      "nestring",
      "in",
      "notin",
      "instrings",
      "notinstrings",
      "eqnull",
      "nenull",
      "array_contains",
      "array_starts_with",
      "array_ends_with",
      "has",
      "has_string",
      "has_some",
      "has_some_string",
      "has_every",
      "has_every_string",
    ]),
    value: z.union([z.string(), z.array(z.union([z.string(), z.number()])), z.object({}).partial().passthrough()]),
  })
  .passthrough();
const SingleOrder = z.object({ dir: z.enum(["asc", "desc"]), field: z.string() }).passthrough();
const VehicleEventTypeEnum = z.enum(["maintenance", "charging", "blocking"]);
const VehicleEventDto = z
  .object({
    branchId: z.string().uuid().nullish(),
    createdAt: z.string().datetime({ offset: true }),
    createdBy: z.string().uuid(),
    drivingSchoolId: z.string(),
    end: z.string().datetime({ offset: true }),
    id: z.string().uuid(),
    noteForInstructor: z.string().nullish(),
    preparationTime: z.number().int().gte(0).lte(9007199254740991),
    start: z.string().datetime({ offset: true }),
    type: z.literal("VehicleEvent"),
    updatedAt: z.string().datetime({ offset: true }),
    vehicleEventType: VehicleEventTypeEnum,
    vehicleId: z.string(),
    wrapUpTime: z.number().int().gte(0).lte(9007199254740991),
  })
  .passthrough();
const BufferTimesDto = z
  .object({ preparationTime: z.number().int().gte(0), wrapUpTime: z.number().int().gte(0) })
  .passthrough();
const CourseLessonDto = z
  .object({
    branchId: z.string().uuid(),
    bufferTimes: BufferTimesDto,
    courseId: z.string().uuid(),
    courseModuleColor: z.string(),
    courseModuleName: z.string(),
    createdAt: z.string().datetime({ offset: true }),
    createdById: z.string().uuid(),
    deleted: z.boolean().optional(),
    deletedAt: z.string().datetime({ offset: true }).optional(),
    deletedById: z.string().uuid().optional(),
    drivingSchoolId: z.string(),
    end: z.string().datetime({ offset: true }),
    id: z.string().uuid(),
    instructorId: z.string().optional(),
    lecturerId: z.string().uuid().nullable(),
    noteForParticipants: z.string(),
    roomId: z.string().uuid().nullable(),
    start: z.string().datetime({ offset: true }),
    type: z.literal("CourseLesson"),
    updatedAt: z.string().datetime({ offset: true }),
    updatedById: z.string().uuid(),
  })
  .passthrough();
const CalendarEventDto = z.union([VehicleEventDto, CourseLessonDto]);
const CreateASFCourseDto = z.object({
  courseUid: z.string().uuid(),
  drivingSchoolUid: z.string().min(1),
  sessions: z
    .array(
      z.object({
        end: z.string().datetime({ offset: true }),
        instructorUid: z.string().min(1),
        location: z.object({ city: z.string().min(1), postalCode: z.string().min(1), street: z.string().min(1) }),
        start: z.string().datetime({ offset: true }),
        uid: z.string().uuid(),
      }),
    )
    .min(5)
    .max(5),
});
const TheoryLectureCategoryEnum = z.enum([
  "Grundstoff",
  "Zusatzstoff_A",
  "Zusatzstoff_B",
  "B96",
  "Zusatzstoff_C",
  "Zusatzstoff_C1",
  "Zusatzstoff_CE",
  "Zusatzstoff_D",
  "Zusatzstoff_D1",
  "Zusatzstoff_L",
  "Zusatzstoff_T",
]);
const TheoryLectureDto = z
  .object({ category: TheoryLectureCategoryEnum, lectureNumber: z.number().int().gte(1).lte(9007199254740991) })
  .passthrough();
const CreateTheoryLessonDto = z
  .object({
    branchId: z.string().min(1),
    bufferTimes: BufferTimesDto.optional(),
    courseUid: z.string().min(1),
    end: z.string().datetime({ offset: true }),
    instructorId: z.string().min(1),
    noteForInstructor: z.string().optional(),
    noteForStudents: z.string().optional(),
    start: z.string().datetime({ offset: true }),
    students: z
      .array(
        z
          .object({ bookedTrainingIds: z.array(z.string()), id: z.string().min(1), rsvp: TheoryLessonAttendanceEnum })
          .passthrough(),
      )
      .optional(),
    theoryLecture: TheoryLectureDto.optional(),
    theoryUnit: z.number().int().gte(-9007199254740991).lte(9007199254740991).optional(),
    type: z.literal("TheoryLesson"),
    uid: z.string().min(1),
    withZoomLink: z.boolean().optional(),
  })
  .passthrough();
const CreateTheoryExamDto = z
  .object({
    id: z.string().min(1),
    location: PostalAddressDto,
    manualStudentGross: MoneyDto.optional(),
    noteForInstructor: z.string().optional(),
    noteForStudent: z.string().optional(),
    start: z.string().datetime({ offset: true }),
    student: z
      .object({
        agreedCancellationPolicy: z.unknown().optional(),
        bookedTrainingIds: z.array(z.string()).min(1),
        id: z.string().min(1),
        rsvp: z.enum(["pending", "accepted"]),
      })
      .passthrough(),
    type: z.literal("TheoryExam"),
  })
  .passthrough();
const AdditionalContentEnum = z.enum(["instructionOnVehicle", "practicalIntroduction"]);
const DrivingLessonTypeEnum2 = z.enum([
  "normal",
  "ueberlandfahrt",
  "autobahnfahrt",
  "nachtfahrt",
  "simulator",
  "praktischePruefung",
  "schaltkompetenz",
  "theoretischePruefung",
  "fahrprobe",
  "unterweisungAmFahrzeug",
  "praktischeUnterweisung",
]);
const CreateDrivingLessonDto = z
  .object({
    additionalContent: AdditionalContentEnum.optional(),
    bufferTimes: BufferTimesDto.optional(),
    drivingLessonType: DrivingLessonTypeEnum2,
    end: z.string().datetime({ offset: true }),
    endedAt: z.unknown().optional(),
    id: z.string().min(1),
    instructorId: z.string().min(1).optional(),
    manualStudentGross: MoneyDto.optional(),
    noteForInstructor: z.string().optional(),
    noteForStudent: z.string().optional(),
    remarks: z.string().optional(),
    resourceIds: z.array(z.string()).optional(),
    start: z.string().datetime({ offset: true }),
    startLocation: PostalAddressDto,
    startedAt: z.unknown().optional(),
    student: z
      .object({
        agreedCancellationPolicy: z.unknown().optional(),
        bookedTrainingId: z.string().min(1),
        id: z.string().min(1),
        rsvp: z.enum(["pending", "accepted", "no-show"]),
      })
      .passthrough(),
    studentUsesOwnMotorcycle: z.boolean().optional(),
    type: z.literal("DrivingLesson"),
    vehicle2Id: z.string().min(1).nullish(),
    vehicleId: z.string().min(1).nullish(),
  })
  .passthrough();
const CreateRemoteLessonDto = z
  .object({
    bufferTimes: BufferTimesDto.optional(),
    end: z.string().datetime({ offset: true }),
    id: z.string().min(1),
    instructorId: z.string().min(1),
    manualStudentGross: MoneyDto.optional(),
    noteForInstructor: z.string().optional(),
    noteForStudent: z.string().optional(),
    remarks: z.string().optional(),
    start: z.string().datetime({ offset: true }),
    student: z
      .object({
        agreedCancellationPolicy: z.unknown().optional(),
        bookedTrainingId: z.string().min(1),
        id: z.string().min(1),
        rsvp: z.enum(["pending", "accepted"]),
      })
      .passthrough(),
    type: z.literal("RemoteLesson"),
  })
  .passthrough();
const CreateCourseLessonDto = z
  .object({
    bufferTimes: BufferTimesDto.optional(),
    courseId: z.string().uuid(),
    courseModuleColor: z.string(),
    courseModuleName: z.string(),
    end: z.string().datetime({ offset: true }),
    id: z.string().uuid(),
    instructorId: z.unknown().optional(),
    lecturerId: z.string().uuid().nullish(),
    noteForParticipants: z.string().optional(),
    roomId: z.string().uuid().nullish(),
    start: z.string().datetime({ offset: true }),
    type: z.literal("CourseLesson"),
  })
  .passthrough();
const CreateVehicleEventDto = z
  .object({
    end: z.string().datetime({ offset: true }),
    id: z.string().min(1),
    instructorId: z.unknown().optional(),
    noteForInstructor: z.string().optional(),
    start: z.string().datetime({ offset: true }),
    type: z.literal("VehicleEvent"),
    vehicleEventType: VehicleEventTypeEnum,
    vehicleId: z.string().min(1),
  })
  .passthrough();
const OtherEventDurationEnum = z.enum(["halfDay", "fullDay", "halfDayInTheMorning", "halfDayInTheAfternoon"]);
const CreateOtherCalendarEventDto = z
  .object({
    categoryId: z.string().min(1),
    description: z.string().optional(),
    end: z.string().datetime({ offset: true }).optional(),
    instructorId: z.string().min(1),
    notes: z.string().optional(),
    otherEventDuration: OtherEventDurationEnum.optional(),
    resources: z.array(z.string()).optional(),
    rrule: z.string().optional(),
    start: z.string().datetime({ offset: true }),
    type: z.literal("Other"),
    uid: z.string().min(1),
    vehicleId: z.string().min(1).optional(),
  })
  .passthrough();
const CreateCalendarEventDto = z.union([
  CreateTheoryLessonDto,
  CreateTheoryExamDto,
  CreateDrivingLessonDto,
  CreateRemoteLessonDto,
  CreateCourseLessonDto,
  CreateVehicleEventDto,
  CreateOtherCalendarEventDto,
]);
const PostalAddress = z
  .object({
    city: z.string().nullable(),
    country: z.string().nullable(),
    geoCoordinates: z.object({ lat: z.number(), lng: z.number() }).passthrough(),
    geoCoordinatesError: z.string(),
    postalCode: z.string().nullable(),
    state: z.string().nullable(),
    street: z.string().nullable(),
    type: z.literal("PostalAddress"),
  })
  .partial()
  .passthrough();
const OnlineMeeting = z.object({ type: z.literal("OnlineMeeting"), url: z.string() }).passthrough();
const CreateTheoryCourseDto = z
  .object({
    drivingSchoolId: z.string().min(1),
    theoryLessons: z.array(
      z
        .object({
          attendees: z.record(
            z.union([
              z.object({ attendeeType: z.literal("instructor"), rsvp: z.literal("accepted") }).passthrough(),
              z
                .object({
                  agreedCancellationPolicy: z.unknown().optional(),
                  attended: z.boolean().optional(),
                  attendeeType: z.literal("student"),
                  bookedAt: z.unknown().optional(),
                  bookedBy: z.string().min(1).optional(),
                  bookedTrainingIds: z.array(z.string()).optional(),
                  noShow: z.boolean().optional(),
                  rsvp: z.enum(["accepted", "rejected"]),
                })
                .passthrough(),
            ]),
          ),
          branchId: z.string().min(1).optional(),
          bufferTimes: BufferTimesDto.optional(),
          courseUid: z.string().min(1),
          description: z.string().optional(),
          end: z.string().datetime({ offset: true }),
          location: z.union([PostalAddress, OnlineMeeting]),
          maxStudents: z.number().int().gte(0).lte(9007199254740991),
          noteForInstructor: z.string().optional(),
          noteForStudents: z.string().optional(),
          onBehalfOfInstructorUid: z.string().min(1).optional(),
          resources: z.array(z.string()).optional(),
          start: z.string().datetime({ offset: true }),
          theoryLecture: TheoryLectureDto.optional(),
          theoryUnit: z.number().int().gte(-9007199254740991).lte(9007199254740991).optional(),
          type: z.literal("TheoryLesson"),
          uid: z.string().min(1),
          withZoomLink: z.boolean().optional(),
        })
        .passthrough(),
    ),
  })
  .passthrough();
const RemoveStudentFromCourseDto = z
  .object({ courseUid: z.string().min(1), studentUid: z.string().min(1) })
  .passthrough();
const EditDrivingLessonDto3 = z.object({
  bookedTrainingId: z.string().min(1).optional(),
  bufferTimes: BufferTimesDto.optional(),
  drivingLessonType: DrivingLessonTypeEnum2.optional(),
  editReason: z.string().optional(),
  end: z.string().datetime({ offset: true }).optional(),
  endedAt: z.string().datetime({ offset: true }).nullish(),
  handlePriceDifferenceStrategy: z.enum(["default", "ignore", "updatePrice"]).optional(),
  instructorId: z.string().min(1).optional(),
  location: PostalAddressDto.optional(),
  manualStudentGross: MoneyDto.optional(),
  noteForStudent: z.string().optional(),
  remarks: z.string().optional(),
  start: z.string().datetime({ offset: true }).optional(),
  startedAt: z.string().datetime({ offset: true }).nullish(),
  type: z.literal("DrivingLesson"),
  vehicle2Id: z.string().min(1).nullish(),
  vehicleId: z.string().min(1).nullish(),
});
const EditTheoryLessonDto = z.object({
  bufferTimes: BufferTimesDto.optional(),
  editReason: z.string().optional(),
  end: z.string().datetime({ offset: true }).optional(),
  instructorId: z.string().min(1).optional(),
  location: PostalAddressDto.optional(),
  start: z.string().datetime({ offset: true }).optional(),
  theoryLecture: TheoryLectureDto.optional(),
  theoryUnit: z.number().int().gte(-9007199254740991).lte(9007199254740991).optional(),
  type: z.literal("TheoryLesson"),
});
const EditOtherEventDto = z.object({
  bufferTimes: BufferTimesDto.optional(),
  categoryId: z.string().min(1).optional(),
  editReason: z.string().optional(),
  end: z.string().datetime({ offset: true }).optional(),
  notes: z.string().optional(),
  otherEventDuration: OtherEventDurationEnum.optional(),
  start: z.string().datetime({ offset: true }).optional(),
  type: z.literal("Other"),
  vehicleId: z.string().min(1).nullish(),
});
const EditRemoteLessonDto = z.object({
  bookedTrainingId: z.string().min(1).optional(),
  bufferTimes: BufferTimesDto.optional(),
  editReason: z.string().optional(),
  end: z.string().datetime({ offset: true }).optional(),
  instructorId: z.string().min(1).optional(),
  manualStudentGross: MoneyDto.optional(),
  noteForStudent: z.string().optional(),
  start: z.string().datetime({ offset: true }).optional(),
  type: z.literal("RemoteLesson"),
});
const EditCourseLessonDto = z.object({
  bufferTimes: BufferTimesDto.optional(),
  editReason: z.string().optional(),
  end: z.string().datetime({ offset: true }).optional(),
  lecturerId: z.string().uuid().nullish(),
  noteForParticipants: z.string().nullish(),
  roomId: z.string().uuid().nullish(),
  start: z.string().datetime({ offset: true }).optional(),
  type: z.literal("CourseLesson"),
});
const EditVehicleEventDto = z.object({
  end: z.string().datetime({ offset: true }).optional(),
  noteForInstructor: z.string().optional(),
  start: z.string().datetime({ offset: true }).optional(),
  type: z.literal("VehicleEvent"),
  vehicleEventType: VehicleEventTypeEnum.optional(),
});
const EditASFCourseSessionDto = z.object({
  bufferTimes: BufferTimesDto.optional(),
  editReason: z.string().optional(),
  end: z.string().datetime({ offset: true }).optional(),
  instructorId: z.string().min(1).optional(),
  start: z.string().datetime({ offset: true }).optional(),
  type: z.literal("ASFCourseSession"),
});
const EditCalendarEventDto3 = z.union([
  EditDrivingLessonDto3,
  EditTheoryLessonDto,
  EditOtherEventDto,
  EditRemoteLessonDto,
  EditCourseLessonDto,
  EditVehicleEventDto,
  EditASFCourseSessionDto,
]);
const TheoryCourseLecture = z
  .object({
    attendingStudents: z.number().int().gte(0).lte(9007199254740991),
    end: z.string().datetime({ offset: true }),
    imageUrl: z.string().url(),
    maxStudents: z.number().int().gte(1).lte(9007199254740991),
    start: z.string().datetime({ offset: true }),
    theoryLecture: TheoryLectureDto,
    theoryUnit: z.number().int().gte(1).lte(18).optional(),
    uid: z.string(),
  })
  .passthrough();
const DrivingSchoolTheoryCourseDto = z
  .object({
    branchId: z.string().nullish(),
    courseId: z.string(),
    end: z.string().datetime({ offset: true }),
    lectures: z.array(TheoryCourseLecture),
    schoolId: z.string(),
    start: z.string().datetime({ offset: true }),
  })
  .passthrough();
const DrivingSchoolTheoryCoursesDto = z.array(DrivingSchoolTheoryCourseDto);
const CreateContractForBookedTrainingDto = z.object({
  bookedTrainingId: z.string().min(1),
  studentId: z.string().min(1),
});
const GenerateContractPdfDto = z.object({
  additionalText: z.string().max(5000).optional(),
  quoteId: z.string().min(1),
  studentId: z.string().min(1),
});
const ListCostBearerDto = z.array(
  z
    .object({
      billingAddress: z
        .object({ city: z.string(), country: z.string(), postalCode: z.string(), street: z.string() })
        .passthrough(),
      drivingSchoolId: z.string(),
      id: z.string().uuid(),
      name: z.string(),
    })
    .passthrough(),
);
const CreateCostBearerDto = z
  .object({
    billingAddress: z
      .object({
        city: z.string(),
        country: z.string().optional().default("DE"),
        postalCode: z.string(),
        street: z.string(),
      })
      .passthrough(),
    drivingSchoolId: z.string(),
    name: z.string(),
  })
  .passthrough();
const CostBearerDto = z
  .object({
    billingAddress: z
      .object({ city: z.string(), country: z.string(), postalCode: z.string(), street: z.string() })
      .passthrough(),
    drivingSchoolId: z.string(),
    id: z.string().uuid(),
    name: z.string(),
  })
  .passthrough();
const UpdateCostBearerDto = z
  .object({
    billingAddress: z
      .object({
        city: z.string(),
        country: z.string().optional().default("DE"),
        postalCode: z.string(),
        street: z.string(),
      })
      .passthrough(),
    name: z.string(),
  })
  .partial();
const CourseLessonParticipantCheckInEnum = z.enum(["participantClaimedPresence", "participantReportedAbsence"]);
const CourseLessonAttendanceStatusEnum = z.enum(["instructorConfirmedAttendance", "instructorConfirmedAbsence"]);
const CourseLessonAttendancesDto = z.record(
  z
    .object({
      checkIn: CourseLessonParticipantCheckInEnum.nullable(),
      minutesTooLate: z.number().int().gte(-9007199254740991).lte(9007199254740991).optional(),
      status: CourseLessonAttendanceStatusEnum.nullable(),
    })
    .passthrough(),
);
const SetCourseLessonAttendancesDto = z.record(CourseLessonAttendanceStatusEnum);
const CourseLessonCheckInDto = z.object({ checkIn: CourseLessonParticipantCheckInEnum });
const DrivingLicenseClassEnum = z.enum([
  "A",
  "A1",
  "A2",
  "A80",
  "AM",
  "B",
  "B196",
  "B197",
  "B78",
  "B96",
  "BE",
  "C",
  "C1",
  "C1E",
  "CE",
  "D",
  "D1",
  "D1E",
  "DE",
  "L",
  "M",
  "T",
]);
const CourseTemplateDto = z
  .object({
    color: z.string(),
    createdAt: z.string().datetime({ offset: true }),
    createdById: z.string().uuid(),
    deleted: z.boolean(),
    deletedAt: z.string().datetime({ offset: true }).nullish(),
    deletedById: z.string().uuid().nullish(),
    drivingLicenseClasses: z.array(DrivingLicenseClassEnum),
    drivingSchoolId: z.string(),
    id: z.string().uuid(),
    modules: z
      .array(
        z
          .object({ color: z.string(), name: z.string(), ue: z.number().int().gte(1).lte(9007199254740991) })
          .passthrough(),
      )
      .min(1),
    name: z.string(),
    updatedAt: z.string().datetime({ offset: true }),
    updatedById: z.string().uuid(),
  })
  .passthrough();
const CreateCourseTemplateDto = z.object({
  color: z.string(),
  drivingLicenseClasses: z.array(DrivingLicenseClassEnum).optional(),
  drivingSchoolId: z.string(),
  idempotencyKey: z.string(),
  modules: z
    .array(
      z
        .object({ color: z.string(), name: z.string(), ue: z.number().int().gte(1).lte(9007199254740991) })
        .passthrough(),
    )
    .min(1),
  name: z.string(),
});
const UpdateCourseTemplateDto = z
  .object({
    color: z.string(),
    drivingLicenseClasses: z.array(DrivingLicenseClassEnum),
    modules: z
      .array(
        z
          .object({ color: z.string(), name: z.string(), ue: z.number().int().gte(1).lte(9007199254740991) })
          .passthrough(),
      )
      .min(1),
    name: z.string(),
  })
  .partial();
const CourseDto = z
  .object({
    branchId: z.string().uuid(),
    color: z.string(),
    courseTemplateId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
    createdById: z.string().uuid(),
    deleted: z.boolean().optional(),
    deletedAt: z.string().datetime({ offset: true }).optional(),
    deletedById: z.string().uuid().optional(),
    drivingSchoolId: z.string(),
    endDate: z.string(),
    events: z.array(CourseLessonDto),
    id: z.string(),
    location: PostalAddressDto,
    name: z.string(),
    startDate: z.string(),
    timetableId: z.string().uuid(),
    updatedAt: z.string().datetime({ offset: true }),
    updatedById: z.string().uuid(),
  })
  .passthrough();
const CreateCourseDto = z.object({
  autoCreateLessons: z.boolean().optional(),
  autoCreateLessonsLecturerId: z.string().uuid().nullish(),
  autoCreateLessonsRoomId: z.string().uuid().nullish(),
  branchId: z.string().uuid(),
  courseTemplateId: z.string().uuid(),
  drivingSchoolId: z.string(),
  endDate: z.string(),
  idempotencyKey: z.string(),
  startDate: z.string(),
  timetableId: z.string().uuid(),
});
const UpdateCourseDto = z.object({ endDate: z.string(), startDate: z.string() }).partial();
const CourseBundleDto = z.object({ id: z.string() }).passthrough();
const CloneCourseDto = z.object({ idempotencyKey: z.string(), startDate: z.string() });
const CourseParticipantDto = z.object({ bookedQuoteId: z.string().uuid(), studentId: z.string().min(1) }).passthrough();
const EnrollStudentInCourseDto = z.object({ quoteId: z.string().uuid(), studentId: z.string().min(1) });
const CustomCardDto = z
  .object({
    createdAt: z.string().datetime({ offset: true }),
    createdById: z.string().uuid(),
    ctaLabel: z.string().nullish(),
    ctaLink: z.string().nullish(),
    description: z.string(),
    drivingSchoolId: z.string(),
    id: z.string().uuid(),
    imageUrl: z.string(),
    title: z.string(),
    updatedAt: z.string().datetime({ offset: true }),
    updatedById: z.string().uuid(),
  })
  .passthrough();
const CreateCustomCardDto = z
  .object({
    ctaLabel: z.string().nullish(),
    ctaLink: z.string().nullish(),
    description: z.string(),
    drivingSchoolId: z.string(),
    title: z.string(),
  })
  .passthrough();
const createCustomCard_Body = CreateCustomCardDto.and(z.object({ image: z.instanceof(File) }).passthrough());
const UpdateCustomCardDto = z
  .object({
    ctaLabel: z.string().nullable(),
    ctaLink: z.string().nullable(),
    description: z.string(),
    title: z.string(),
  })
  .partial()
  .passthrough();
const updateCustomCard_Body = UpdateCustomCardDto.and(
  z
    .object({ image: z.instanceof(File) })
    .partial()
    .passthrough(),
);
const SchaltnachweisDataDto = z
  .object({
    dateOfTestfahrt: z.string().optional(),
    eductionFrom: z.string().optional(),
    eductionTo: z.string().optional(),
    numberOf45MinUnits: z.number().int().gte(0).lte(9007199254740991),
  })
  .passthrough();
const SignDocumentsDto = z
  .object({ signatureRequestIds: z.array(z.string()), signatureStorageRef: z.string().min(1) })
  .passthrough();
const SignDocumentsResultDto = z
  .object({ errors: z.record(z.string()).optional(), signed: z.array(z.string()) })
  .passthrough();
const SignByStudentDto = z
  .object({ signatureRequestId: z.string().min(1), signatureStorageRef: z.string().min(1) })
  .passthrough();
const CreateSignatureRequestDto = z
  .object({
    asfCourseId: z.string().uuid().optional(),
    assignedDrivingSchoolManagerId: z.string().min(1).nullish(),
    bookedTrainingId: z.string().min(1).optional(),
    data: z
      .object({
        dateOfTestfahrt: z.string(),
        eductionFrom: z.string(),
        eductionTo: z.string(),
        numberOf45MinUnits: z.number().int().gte(10).lte(9007199254740991),
      })
      .passthrough()
      .optional(),
    dataSnapshot: z.string().optional(),
    dateOfCreation: z.string().optional(),
    drivingLicenseClass: DrivingLicenseClassEnum.optional(),
    idempotencyKey: z.string().min(1),
    includePlannedAppointments: z.boolean().optional(),
    studentId: z.string().min(1),
    type: z.enum([
      "ausbildungsnachweis",
      "schaltnachweis",
      "asf_teilnahmebescheinigung",
      "b196_teilnahmebescheinigung",
      "b96_teilnahmebescheinigung",
    ]),
  })
  .passthrough();
const CreateSignatureRequestResultDto = z.object({ id: z.string().min(1) }).passthrough();
const DrivingLicenseAuthorityFormTypeEnum = z.enum([
  "FUEHRERSCHEINANTRAG",
  "BF17_ANTRAG",
  "BF17_BEGLEITPERSON_ANTRAG",
  "SONSTIGES",
]);
const DrivingLicenseAuthorityDto = z
  .object({
    address: PostalAddressDto,
    assignedBranches: z.array(z.object({ drivingSchoolId: z.string(), id: z.string().uuid() }).passthrough()),
    email: z.string(),
    forms: z.array(
      z
        .object({
          drivingLicenseClasses: z.array(DrivingLicenseClassEnum),
          id: z.string().uuid(),
          name: z.string(),
          pdfTemplate: JsonValue.nullable(),
          pdfTemplateBaseFileId: z.string().uuid().nullish(),
          type: DrivingLicenseAuthorityFormTypeEnum,
          url: z.string().nullish(),
        })
        .passthrough(),
    ),
    furtherNotes: z.string(),
    id: z.string().uuid(),
    instructions: z.array(
      z
        .object({
          drivingLicenseClasses: z.array(DrivingLicenseClassEnum),
          html: z.string(),
          id: z.string().uuid(),
          title: z.string(),
        })
        .passthrough(),
    ),
    name: z.string(),
    openingHoursUrl: z.string(),
    phone: z.string(),
    websiteUrl: z.string(),
  })
  .passthrough();
const CreateDrivingLicenseAuthorityDto = z.object({
  address: z.object({ city: z.string(), postalCode: z.string(), street: z.string() }),
  email: z.string().optional(),
  furtherNotes: z.string().optional(),
  name: z.string(),
  openingHoursUrl: z.string().optional(),
  phone: z.string().optional(),
  websiteUrl: z.string().optional(),
});
const DrivingLicenseAuthorityFormDto = z
  .object({
    drivingLicenseAuthorityId: z.string().uuid(),
    drivingLicenseClasses: z.array(DrivingLicenseClassEnum),
    id: z.string().uuid(),
    name: z.string(),
    pdfTemplate: JsonValue.nullish(),
    pdfTemplateBaseFile: AttachmentDto.nullish(),
    type: DrivingLicenseAuthorityFormTypeEnum,
    url: z.string().nullish(),
  })
  .passthrough();
const CreateDrivingLicenseAuthorityFormDto = z
  .object({
    drivingLicenseClasses: z.array(DrivingLicenseClassEnum),
    idempotencyKey: z.string().optional(),
    name: z.string(),
    pdfTemplate: JsonValue.nullish(),
    type: DrivingLicenseAuthorityFormTypeEnum,
    url: z.string().nullish(),
  })
  .passthrough();
const UpdateDrivingLicenseAuthorityFormDto = z
  .object({
    drivingLicenseClasses: z.array(DrivingLicenseClassEnum),
    name: z.string(),
    pdfTemplate: JsonValue.nullable(),
    type: DrivingLicenseAuthorityFormTypeEnum,
    url: z.string().nullable(),
  })
  .partial()
  .passthrough();
const UpdateDrivingLicenseAuthorityInstructionDto = z
  .object({ drivingLicenseClasses: z.array(DrivingLicenseClassEnum), html: z.string(), title: z.string() })
  .partial();
const UpdateDrivingLicenseAuthorityDto = z
  .object({
    address: z.object({ city: z.string(), postalCode: z.string(), street: z.string() }),
    email: z.string(),
    furtherNotes: z.string(),
    name: z.string(),
    openingHoursUrl: z.string(),
    phone: z.string(),
    websiteUrl: z.string(),
  })
  .partial();
const CreateDrivingLicenseAuthorityInstructionDto = z.object({
  drivingLicenseClasses: z.array(DrivingLicenseClassEnum),
  html: z.string(),
  title: z.string(),
});
const CreateDrivingSchoolDto = z
  .object({
    defaultBranch: z.object({
      address: z.object({ city: z.string(), postalCode: z.string(), street: z.string() }).passthrough(),
      maxStudentsForTheory: z.number().int().gte(-9007199254740991).lte(9007199254740991),
    }),
    id: z.string(),
    name: z.string(),
    startDate: z.string(),
  })
  .passthrough();
const ExamLocationWeekdayEnum = z.enum(["monday", "tuesday", "wednesday", "thursday", "friday", "saturday"]);
const ExamTypeEnum = z.enum(["theoryExam", "practicalExam"]);
const ExamLocationDto = z
  .object({
    deleted: z.boolean().optional(),
    name: z.string(),
    postalAddress: PostalAddress,
    practicalExamDaysInAdvance: z.number().gt(0).optional(),
    practicalExamWeekdays: z.array(ExamLocationWeekdayEnum).optional(),
    theoryExamDaysInAdvance: z.number().gt(0).optional(),
    theoryExamWeekdays: z.array(ExamLocationWeekdayEnum).optional(),
    types: z.array(ExamTypeEnum),
    uid: z.string().min(1),
  })
  .passthrough();
const CreateExamLocationDto = z
  .object({
    name: z.string(),
    postalAddress: PostalAddress,
    practicalExamDaysInAdvance: z.number().gt(0).optional(),
    practicalExamWeekdays: z.array(ExamLocationWeekdayEnum).optional(),
    theoryExamDaysInAdvance: z.number().gt(0).optional(),
    theoryExamWeekdays: z.array(ExamLocationWeekdayEnum).optional(),
    types: z.array(ExamTypeEnum),
    uid: z.string().min(1),
  })
  .passthrough();
const UpdateExamLocationDto = z
  .object({
    name: z.string(),
    postalAddress: PostalAddress,
    practicalExamDaysInAdvance: z.number().gt(0),
    practicalExamWeekdays: z.array(ExamLocationWeekdayEnum),
    theoryExamDaysInAdvance: z.number().gt(0),
    theoryExamWeekdays: z.array(ExamLocationWeekdayEnum),
    types: z.array(ExamTypeEnum),
  })
  .partial()
  .passthrough();
const ListDrivingSchoolLabelDto = z.array(
  z
    .object({
      color: z.enum([
        "greenUltraLight",
        "greyUltraLight",
        "orangeUltraLight",
        "redUltraLight",
        "violetUltraLight",
        "blueUltraLight",
        "yellowUltraLight",
      ]),
      createdAt: z.string().datetime({ offset: true }),
      createdById: z.string().uuid(),
      description: z.string(),
      drivingSchoolId: z.string(),
      id: z.string().uuid(),
      name: z.string(),
      updatedAt: z.string().datetime({ offset: true }),
      updatedById: z.string().uuid(),
      usageCount: z.number().gte(0),
    })
    .passthrough(),
);
const DrivingSchoolLabelColorEnum = z.enum([
  "greenUltraLight",
  "greyUltraLight",
  "orangeUltraLight",
  "redUltraLight",
  "violetUltraLight",
  "blueUltraLight",
  "yellowUltraLight",
]);
const CreateDrivingSchoolLabelDto = z
  .object({ color: DrivingSchoolLabelColorEnum, description: z.string(), name: z.string() })
  .passthrough();
const DrivingSchoolLabelDto = z
  .object({
    color: DrivingSchoolLabelColorEnum,
    createdAt: z.string().datetime({ offset: true }),
    createdById: z.string().uuid(),
    description: z.string(),
    drivingSchoolId: z.string(),
    id: z.string().uuid(),
    name: z.string(),
    updatedAt: z.string().datetime({ offset: true }),
    updatedById: z.string().uuid(),
  })
  .passthrough();
const UpdateDrivingSchoolLabelDto = z
  .object({ color: DrivingSchoolLabelColorEnum, description: z.string(), name: z.string() })
  .partial()
  .passthrough();
const ListOtherEventCategoryDto = z.array(
  z
    .object({
      availableDurations: z.array(z.enum(["fullDay", "halfDayInTheMorning", "halfDayInTheAfternoon"])),
      createdAt: z.string().datetime({ offset: true }),
      createdById: z.string().uuid(),
      description: z.string().nullish(),
      drivingSchoolId: z.string().nullish(),
      id: z.string().uuid(),
      isPaid: z.boolean().nullish(),
      isRestTime: z.boolean(),
      isSystemCategory: z.boolean(),
      isWorkTime: z.boolean(),
      name: z.string(),
      type: z.enum([
        "publicHoliday",
        "journeyFromAndToCustomer",
        "preparationTheory",
        "sickness",
        "vacation",
        "training",
        "custom",
      ]),
      updatedAt: z.string().datetime({ offset: true }),
      updatedById: z.string().uuid(),
      utilizationCategory: z.enum(["transferTimes", "publicHolidays", "unavailable", "overhead"]),
      workedHoursCategory: z.enum(["transferTimes", "publicHolidays", "vacation", "sickness", "training", "other"]),
    })
    .passthrough(),
);
const OtherEventCategoryDurationOptionEnum = z.enum(["fullDay", "halfDayInTheMorning", "halfDayInTheAfternoon"]);
const UtilizationCategoryEnum = z.enum(["transferTimes", "publicHolidays", "unavailable", "overhead"]);
const CreateOtherEventCategoryDto = z
  .object({
    availableDurations: z.array(OtherEventCategoryDurationOptionEnum),
    description: z.string().nullish(),
    isPaid: z.boolean().nullish(),
    isRestTime: z.boolean(),
    isWorkTime: z.boolean(),
    name: z.string(),
    utilizationCategory: UtilizationCategoryEnum,
    workedHoursCategory: z.enum(["transferTimes", "publicHolidays", "vacation", "sickness", "training", "other"]),
  })
  .passthrough();
const OtherCalendarEventCategoryTypeEnum = z.enum([
  "publicHoliday",
  "journeyFromAndToCustomer",
  "preparationTheory",
  "sickness",
  "vacation",
  "training",
  "custom",
]);
const OtherEventCategoryDto = z
  .object({
    availableDurations: z.array(OtherEventCategoryDurationOptionEnum),
    createdAt: z.string().datetime({ offset: true }),
    createdById: z.string().uuid(),
    description: z.string().nullish(),
    drivingSchoolId: z.string().nullish(),
    id: z.string().uuid(),
    isPaid: z.boolean().nullish(),
    isRestTime: z.boolean(),
    isSystemCategory: z.boolean(),
    isWorkTime: z.boolean(),
    name: z.string(),
    type: OtherCalendarEventCategoryTypeEnum,
    updatedAt: z.string().datetime({ offset: true }),
    updatedById: z.string().uuid(),
    utilizationCategory: UtilizationCategoryEnum,
    workedHoursCategory: z.enum(["transferTimes", "publicHolidays", "vacation", "sickness", "training", "other"]),
  })
  .passthrough();
const UpdateOtherEventCategoryDto = z
  .object({
    availableDurations: z.array(OtherEventCategoryDurationOptionEnum),
    description: z.string().nullable(),
    isPaid: z.boolean().nullable(),
    isRestTime: z.boolean(),
    isWorkTime: z.boolean(),
    name: z.string(),
    utilizationCategory: UtilizationCategoryEnum,
    workedHoursCategory: z.enum(["transferTimes", "publicHolidays", "vacation", "sickness", "training", "other"]),
  })
  .partial();
const RoomDto = z
  .object({
    branchId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
    createdById: z.string().uuid(),
    deleted: z.boolean(),
    deletedAt: z.string().datetime({ offset: true }).nullish(),
    deletedById: z.string().uuid().nullish(),
    description: z.string().nullish(),
    drivingSchoolId: z.string(),
    id: z.string(),
    name: z.string(),
    numberOfSeats: z.number().int().gte(-9007199254740991).lte(9007199254740991),
    updatedAt: z.string().datetime({ offset: true }),
    updatedById: z.string().uuid(),
  })
  .passthrough();
const CreateRoomDto = z
  .object({
    branchId: z.string().min(1),
    description: z.string().optional(),
    name: z.string(),
    numberOfSeats: z.number().gte(0),
  })
  .passthrough();
const UpdateRoomDto = z
  .object({ branchId: z.string().min(1), description: z.string(), name: z.string(), numberOfSeats: z.number().gte(0) })
  .partial()
  .passthrough();
const StatsOverviewDto = z
  .object({
    activeStudents: z
      .object({ currentPeriod: z.number(), delta: z.number(), trend: z.enum(["up", "down", "stable"]) })
      .passthrough(),
    openSignatures: z.object({ count: z.number() }).passthrough(),
    practicalExams: z
      .object({
        thisWeek: z.number(),
        upcoming: z
          .object({
            countOnNextExamDate: z.number(),
            nextExamDate: z.string().nullable(),
            urgencyLabel: z.enum(["heute", "morgen"]).nullable(),
          })
          .passthrough()
          .nullish(),
      })
      .passthrough(),
    practicalPassRate: z
      .object({
        counts: z
          .object({
            currentPeriod: z.object({ passed: z.number(), total: z.number() }).passthrough(),
            previousPeriod: z.object({ passed: z.number(), total: z.number() }).passthrough(),
          })
          .passthrough(),
        currentPeriod: z.number().nullable(),
        delta: z.number().nullable(),
        trend: z.enum(["up", "down", "stable"]).nullable(),
      })
      .passthrough(),
    registrations: z
      .object({ currentPeriod: z.number(), delta: z.number(), trend: z.enum(["up", "down", "stable"]) })
      .passthrough(),
  })
  .passthrough();
const StudentSignupStatsDto = z
  .object({ results: z.record(z.number()), total: z.number(), trend: z.enum(["up", "down", "stable"]) })
  .passthrough();
const DrivingSchoolSchemaDto = z.object({
  createdAt: z.string().datetime({ offset: true }),
  createdById: z.string().uuid().nullish(),
  datevId: z.string().nullish(),
  endDate: z.string().datetime({ offset: true }).nullish(),
  hubspotId: z.string().nullish(),
  id: z.string(),
  minimumSimulatorUE: z.number().int().gte(-9007199254740991).lte(9007199254740991).nullish(),
  name: z.string(),
  startDate: z.string().datetime({ offset: true }),
  stripeConnectedAccountId: z.string().nullish(),
  threadId: z.string().uuid().nullish(),
  updatedAt: z.string().datetime({ offset: true }),
  updatedById: z.string().uuid().nullish(),
});
const UpdateDrivingSchoolDto = z
  .object({
    createdById: z.string().uuid().nullable(),
    datevId: z.string().nullable(),
    endDate: z.string().datetime({ offset: true }).nullable(),
    hubspotId: z.string().nullable(),
    minimumSimulatorUE: z.number().int().gte(-9007199254740991).lte(9007199254740991).nullable(),
    name: z.string(),
    startDate: z.string().datetime({ offset: true }),
    stripeConnectedAccountId: z.string().nullable(),
    threadId: z.string().uuid().nullable(),
    updatedById: z.string().uuid().nullable(),
  })
  .partial()
  .passthrough();
const EmployeeDto = z
  .object({
    autovioUserId: z.string(),
    disabled: z.boolean(),
    drivingSchoolId: z.string().optional(),
    email: z.string().optional(),
    entitledForDrivingSchools: z.array(z.string()).optional(),
    firebaseAuthUserUid: z.string(),
    firstName: z.string(),
    grants: z.array(z.string()).optional(),
    lastName: z.string(),
    phoneNumber: z.string().optional(),
    roles: z.array(z.string()),
  })
  .passthrough();
const ServiceTypeDto = z
  .object({
    category: z
      .enum(["DRIVING_LESSON", "THEORY_LESSON", "AUTHORITY_FEE", "BASE_FEE", "TEACHING_MATERIAL", "EXAM_FEE", "OTHER"])
      .nullish(),
    createdById: z.string().uuid().nullish(),
    drivingSchoolId: z.string().nullish(),
    id: z.string().uuid(),
    price: z.union([z.number(), z.string()]).nullish(),
    title: z.string(),
    updatedById: z.string().uuid().nullish(),
    vatRate: z.union([z.number(), z.string()]),
  })
  .passthrough();
const SignupDrivingSchoolAutoFormDto = z
  .object({ description: z.string().nullish(), id: z.string().uuid(), name: z.string(), pdfUrl: z.string().url() })
  .passthrough();
const BlockStudentAndSendPaymentReminderRequest = z
  .object({
    dunningProcessId: z.string().uuid(),
    emailSubject: z.string(),
    emailText: z.string(),
    studentUid: z.string(),
  })
  .passthrough();
const SendDunningNoticeRequest = z
  .object({ dunningProcessId: z.string().uuid(), emailSubject: z.string(), emailText: z.string() })
  .passthrough();
const SendSammelquittungEmailDto = z
  .object({
    html: z.string(),
    invoices: z.array(z.string()).min(1),
    recipient: z.string().email(),
    subject: z.string(),
    text: z.string(),
  })
  .passthrough();
const GenderEnum = z.enum(["Male", "Female", "Diverse"]);
const CreateEmployeeDto = z
  .object({
    birthName: z.string().nullish(),
    drivingSchoolId: z.string().min(1),
    email: z.string().email(),
    emailVerified: z.boolean().optional(),
    firstName: z.string(),
    gender: GenderEnum.nullish(),
    lastName: z.string(),
    password: z.string().min(8),
    phoneNumber: z.string().nullish(),
    role: z.enum(["drivingSchoolAccountant", "drivingSchoolCustomerService"]),
  })
  .passthrough();
const UpdateEmployeeDto = z
  .object({ firstName: z.string(), lastName: z.string(), phoneNumber: z.string().nullable() })
  .partial();
const ExamAuthorityFeeDto = z
  .object({
    createdAt: z.string().datetime({ offset: true }),
    createdById: z.string().uuid(),
    deleted: z.boolean(),
    deletedAt: z.string().datetime({ offset: true }).nullish(),
    deletedById: z.string().uuid().nullish(),
    drivingLicenseClass: DrivingLicenseClassEnum,
    id: z.string().uuid(),
    isDrivingLicenseClassAdvancement: z.boolean(),
    practicalExamFee: MoneyDto.nullable(),
    theoryExamFee: MoneyDto.nullable(),
    updatedAt: z.string().datetime({ offset: true }),
    updatedById: z.string().uuid(),
    validFrom: z.string(),
    validUntil: z.string().nullable(),
  })
  .passthrough();
const CreateExamAuthorityFeeDto = z.object({
  drivingLicenseClass: DrivingLicenseClassEnum,
  idempotencyKey: z.string(),
  isDrivingLicenseClassAdvancement: z.boolean(),
  practicalExamFee: MoneyDto.nullish(),
  theoryExamFee: MoneyDto.nullish(),
  validFrom: z.string(),
  validUntil: z.string().nullish(),
});
const UpdateExamAuthorityFeeDto = z
  .object({
    drivingLicenseClass: DrivingLicenseClassEnum,
    isDrivingLicenseClassAdvancement: z.boolean(),
    practicalExamFee: MoneyDto.nullable(),
    theoryExamFee: MoneyDto.nullable(),
    validFrom: z.string(),
    validUntil: z.string().nullable(),
  })
  .partial();
const CreateInstructorDto = z
  .object({
    birthName: z.string().nullish(),
    drivingLicenseClasses: z.array(DrivingLicenseClassEnum),
    drivingSchoolId: z.string(),
    email: z.string().email(),
    emailVerified: z.boolean().optional(),
    endDate: z.string().datetime({ offset: true }).optional(),
    firstName: z.string(),
    gender: GenderEnum.nullish(),
    lastName: z.string(),
    password: z.string().min(8),
    phoneNumber: z.string().nullish(),
    startDate: z.string().datetime({ offset: true }),
  })
  .passthrough();
const InstructorDto = z
  .object({
    assignedBranches: z.array(z.string()).optional(),
    autoSignSignatureRequests: z.boolean(),
    createdAt: z.string().datetime({ offset: true }),
    drivingLicenseClasses: z.array(DrivingLicenseClassEnum),
    drivingSchoolId: z.string(),
    endDate: z.string().datetime({ offset: true }),
    fahrschulcockpitId: z.number().int().gte(-9007199254740991).lte(9007199254740991).nullish(),
    fahrschuleLiveId: z.number().int().gte(-9007199254740991).lte(9007199254740991).nullish(),
    firstName: z.string(),
    gender: GenderEnum.nullish(),
    id: z.string(),
    lastName: z.string(),
    signatureStorageRef: z.string(),
    startDate: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    user: z
      .object({
        birthName: z.string().nullish(),
        createdAt: z.string().datetime({ offset: true }),
        email: z.string().nullish(),
        firstName: z.string(),
        gender: GenderEnum.nullish(),
        id: z.string().uuid(),
        lastName: z.string(),
        phoneNumber: z.string().nullish(),
        updatedAt: z.string().datetime({ offset: true }),
      })
      .passthrough(),
    userId: z.string().uuid(),
    vogelId: z.string().uuid().nullish(),
  })
  .passthrough();
const WorktimeStatsDto = z
  .object({
    canceledAtShortNoticeMinutes: z.number().int().gte(0).lte(9007199254740991),
    daysAbsent: z.number().gte(0),
    drivingLessonMinutes: z.number().int().gte(0).lte(9007199254740991),
    otherWorkMinutes: z.number().int().gte(0).lte(9007199254740991),
    potentialViolations: z.record(
      z
        .object({
          tooLittlePauses: z.boolean(),
          tooLittlePausesMinutes: z.number().int().gte(0).lte(9007199254740991),
          tooLittleRest: z.boolean(),
          tooLittleRestMinutes: z.number().int().gte(0).lte(9007199254740991),
          tooLongWorkday: z.boolean(),
          tooLongWorkdayMinutes: z.number().int().gte(0).lte(9007199254740991),
          tooManyLessons: z.boolean(),
          tooManyLessonsMinutes: z.number().int().gte(0).lte(9007199254740991),
        })
        .partial(),
    ),
    practicalExamMinutes: z.number().int().gte(0).lte(9007199254740991),
    publicHolidays: z.number().int().gte(0).lte(9007199254740991),
    theoryLessonMinutes: z.number().int().gte(0).lte(9007199254740991),
    violations: z.record(
      z
        .object({
          tooLittlePauses: z.boolean(),
          tooLittlePausesMinutes: z.number().int().gte(0).lte(9007199254740991),
          tooLittleRest: z.boolean(),
          tooLittleRestMinutes: z.number().int().gte(0).lte(9007199254740991),
          tooLongWorkday: z.boolean(),
          tooLongWorkdayMinutes: z.number().int().gte(0).lte(9007199254740991),
          tooManyLessons: z.boolean(),
          tooManyLessonsMinutes: z.number().int().gte(0).lte(9007199254740991),
        })
        .partial(),
    ),
  })
  .passthrough();
const WorktimeStatsDto2 = z
  .object({ data: WorktimeStatsDto, lastAggregatedAt: z.string().datetime({ offset: true }).optional() })
  .passthrough();
const UpdateInstructorDto = z
  .object({
    assignedBranches: z.array(z.string()),
    drivingLicenseClasses: z.array(DrivingLicenseClassEnum),
    email: z.string(),
    endDate: z.string().datetime({ offset: true }),
    firstName: z.string(),
    lastName: z.string(),
    phoneNumber: z.string(),
    startDate: z.string().datetime({ offset: true }),
  })
  .partial();
const RecommendationSettingsDto = z
  .object({
    allowOverbooking: z.boolean().optional(),
    maxDaysBookableInAdvance: z.number().int().gte(0).lte(9007199254740991),
    maxDrivingLessonLUsPerDay: z.number().int().gte(0).lte(9007199254740991).nullish(),
    maxDrivingLessonsPerWeek: z.number().int().gte(0).lte(9007199254740991),
    minHoursInAdvance: z.number().int().gte(0).lte(9007199254740991).optional().default(12),
    pauseBetweenDrivingLessonsMins: z.number().int().gte(0).lte(9007199254740991),
    preferredDurationMinsPerDrivingLessonType: z.object({ normal: z.array(z.number()) }).passthrough(),
    vehicles: z.record(z.array(z.string())).optional(),
  })
  .passthrough();
const UpdateSignatureSettingsDto = z
  .object({
    autoSignSignatureRequests: z.boolean(),
    signatureStorageRef: z.string().regex(/^uploads\/user_signatures\/[^/]+\/.+/),
  })
  .partial();
const GeofencedTimeRangeDto = z
  .object({
    end: z
      .object({
        hour: z.number().int().gte(0).lte(9007199254740991),
        minute: z.number().int().gte(0).lte(9007199254740991),
      })
      .passthrough(),
    geofence: z
      .object({
        circle: z
          .object({ center: z.object({ lat: z.number(), lng: z.number() }).passthrough(), radiusInKm: z.number() })
          .passthrough(),
        displayLocation: PostalAddress,
      })
      .passthrough()
      .optional(),
    start: z
      .object({
        hour: z.number().int().gte(0).lte(9007199254740991),
        minute: z.number().int().gte(0).lte(9007199254740991),
      })
      .passthrough(),
  })
  .passthrough();
const WorkTimesDto = z
  .object({
    friday: z.array(GeofencedTimeRangeDto),
    monday: z.array(GeofencedTimeRangeDto),
    saturday: z.array(GeofencedTimeRangeDto),
    sunday: z.array(GeofencedTimeRangeDto),
    thursday: z.array(GeofencedTimeRangeDto),
    tuesday: z.array(GeofencedTimeRangeDto),
    wednesday: z.array(GeofencedTimeRangeDto),
  })
  .partial();
const filter = z
  .object({
    createdById: z.string().uuid(),
    drivingSchoolId: z.string().nullish(),
    id: z.string().uuid(),
    studentId: z.string().nullish(),
    updatedById: z.string().uuid(),
  })
  .passthrough()
  .optional();
const UpdateNoteDto = z
  .object({
    attachments: z
      .array(
        z
          .object({
            mimeType: z.string(),
            name: z.string(),
            path: z.string(),
            size: z.number().int().gte(-9007199254740991).lte(9007199254740991),
          })
          .passthrough(),
      )
      .optional(),
    body: z.string().nullish(),
    id: z.string().uuid(),
  })
  .passthrough();
const NoteDto = z
  .object({
    attachments: z.array(AttachmentDto).optional(),
    body: z.string().nullish(),
    createdAt: z.string().datetime({ offset: true }),
    createdById: z.string().uuid(),
    deleted: z.boolean(),
    deletedAt: z.string().datetime({ offset: true }).nullish(),
    deletedById: z.string().uuid().nullish(),
    drivingSchoolId: z.string().nullish(),
    id: z.string().uuid(),
    studentId: z.string().nullish(),
    type: z.enum(["STUDENT", "DRIVING_SCHOOL"]),
    updatedAt: z.string().datetime({ offset: true }),
    updatedById: z.string().uuid(),
  })
  .passthrough();
const CreateNoteDto = z
  .object({
    attachments: z
      .array(
        z
          .object({
            mimeType: z.string(),
            name: z.string(),
            path: z.string(),
            size: z.number().int().gte(-9007199254740991).lte(9007199254740991),
          })
          .passthrough(),
      )
      .optional(),
    body: z.string().nullish(),
    drivingSchoolId: z.string().nullish(),
    studentId: z.string().nullish(),
    type: z.enum(["STUDENT", "DRIVING_SCHOOL"]),
  })
  .passthrough();
const CreateNotificationDto = z.union([
  z.object({ signatureRequestId: z.string().min(1), type: z.literal("documentAddedToSignatureFolder") }).passthrough(),
  z.object({ signatureRequestId: z.string().min(1), type: z.literal("documentSignedByStudent") }).passthrough(),
]);
const _PayBaseFeeRequestDto = z
  .object({ data: z.object({ idempotencyKey: z.string().min(1), quoteId: z.string().min(1) }).passthrough() })
  .passthrough();
const PayBaseFeeResponseDto = z
  .object({
    paidPositions: z.object({ baseFee: z.number(), teachingMaterialFee: z.number().optional() }).passthrough(),
  })
  .passthrough();
const _PayBaseFeeResponseDto = z.object({ data: PayBaseFeeResponseDto }).passthrough();
const CreatePostDto = z
  .object({
    attachments: z
      .array(
        z
          .object({
            mimeType: z.string(),
            name: z.string(),
            path: z.string(),
            size: z.number().int().gte(-9007199254740991).lte(9007199254740991),
          })
          .passthrough(),
      )
      .optional(),
    body: z.string().nullish(),
    threadId: z.string().uuid(),
  })
  .passthrough();
const PostDto = z
  .object({
    attachments: z.array(AttachmentDto).optional(),
    body: z.string().nullish(),
    createdAt: z.string().datetime({ offset: true }),
    createdById: z.string().uuid(),
    deleted: z.boolean(),
    deletedAt: z.string().datetime({ offset: true }).nullish(),
    deletedById: z.string().uuid().nullish(),
    id: z.string().uuid(),
    threadId: z.string().uuid(),
    updatedAt: z.string().datetime({ offset: true }),
    updatedById: z.string().uuid(),
  })
  .passthrough();
const UpdatePostDto = z.object({ body: z.string().nullable() }).partial().passthrough();
const PromoCodeDto = z
  .object({
    autovioShare: z.string(),
    code: z.string(),
    createdAt: z.string().datetime({ offset: true }),
    createdById: z.string().uuid(),
    description: z.string(),
    drivingSchoolId: z.string().nullish(),
    end: z.string().datetime({ offset: true }),
    id: z.string().uuid(),
    isEnabled: z.boolean(),
    maxUses: z.number().int().gte(-9007199254740991).lte(9007199254740991),
    normalizedCode: z.string(),
    numUsed: z.number(),
    start: z.string().datetime({ offset: true }),
    startCredits: z.string(),
    updatedAt: z.string().datetime({ offset: true }),
    updatedById: z.string().uuid(),
  })
  .passthrough();
const CreatePromoCodeDto = z.object({
  autovioShare: z.union([z.string(), z.number()]).optional(),
  code: z.string().min(1),
  description: z.string().optional(),
  drivingSchoolId: z.string().min(1).optional(),
  end: z.string().datetime({ offset: true }),
  isEnabled: z.boolean().optional().default(true),
  maxUses: z.number().int().gte(-9007199254740991).lte(9007199254740991),
  start: z.string().datetime({ offset: true }),
  startCredits: z.union([z.string(), z.number()]),
});
const ReservePromoCodeRequest = z.object({ code: z.string().min(1), drivingSchoolId: z.string().min(1) });
const ReservePromoCodeResponse = z
  .object({
    id: z.string().min(1),
    reservedUntil: z.string().datetime({ offset: true }),
    startCredits: z.object({ amount: z.string(), currency: z.literal("EUR") }).passthrough(),
  })
  .passthrough();
const PromoCodeExpiredErrorDto = z.object({ error: z.literal("expired") });
const TooManyUsagesErrorDto = z.object({ error: z.literal("too-many-usages") });
const UpdatePromoCodeDto = z
  .object({
    autovioShare: z.union([z.string(), z.number()]),
    code: z.string().min(1),
    description: z.string(),
    drivingSchoolId: z.string().min(1),
    end: z.string().datetime({ offset: true }),
    id: z.string().min(1),
    isEnabled: z.boolean().default(true),
    maxUses: z.number().int().gte(-9007199254740991).lte(9007199254740991),
    start: z.string().datetime({ offset: true }),
    startCredits: z.union([z.string(), z.number()]),
  })
  .partial();
const PublicInstructorDto = z
  .object({
    instructing: z
      .object({
        customTexts: z
          .object({ aboutMe: z.string(), disclaimer: z.string(), termsAndConditions: z.string() })
          .partial()
          .passthrough(),
        drivingLicenseClasses: z.array(DrivingLicenseClassEnum),
        gender: z.enum(["male", "female", "divers"]),
        imageGallery: z.array(z.string()),
      })
      .partial()
      .passthrough()
      .optional(),
    publicProfile: z
      .object({
        avatarOverrideUrl: z.string(),
        avatarUrl: z.string(),
        displayName: z.string(),
        firstName: z.string(),
        lastName: z.string(),
        profilePhotoTaken: z.unknown(),
      })
      .partial()
      .passthrough(),
  })
  .passthrough();
const AiPromptBriefingsDto = z.object({
  clarifyBusinessQuestionBriefing: z.string(),
  expandMetricSearchTermsBriefing: z.string(),
  normalizeBusinessQuestionBriefing: z.string(),
  productContextBriefing: z.string(),
  selectAnswerSurfaceBriefing: z.string(),
  updatedAt: z.string().nullable(),
  updatedBy: z.string().nullable(),
});
const AiPromptBriefingsItemDto = z.object({ item: AiPromptBriefingsDto });
const AiPromptBriefingsUpdateDto = z.object({
  clarifyBusinessQuestionBriefing: z.string(),
  expandMetricSearchTermsBriefing: z.string(),
  normalizeBusinessQuestionBriefing: z.string(),
  productContextBriefing: z.string(),
  selectAnswerSurfaceBriefing: z.string(),
});
const AiAdminProviderSettingsDto = z.object({
  active: z.boolean(),
  allowedDatasetId: z.string(),
  allowedProjectIds: z.array(z.string()),
  conversationModel: z.string(),
  fastModel: z.string(),
  keyConfigured: z.boolean(),
  maskedKey: z.string().nullable(),
  maxCatalogCards: z.number(),
  maxHistoryMessages: z.number(),
  maxPromptTokens: z.number(),
  maxQueryBytes: z.number(),
  maxRows: z.number(),
  provider: z.literal("openai"),
  semanticModel: z.string(),
  sessionTtlSeconds: z.number(),
  sqlPlannerModel: z.string(),
  transcriptionModel: z.string(),
  updatedAt: z.string().nullable(),
  updatedBy: z.string().nullable(),
});
const AiAdminProviderSettingsItemDto = z.object({ item: AiAdminProviderSettingsDto });
const AiAdminProviderSettingsUpdateDto = z.object({
  active: z.boolean(),
  allowedDatasetId: z.string(),
  allowedProjectIds: z.array(z.string()),
  clearApiKey: z.boolean().optional(),
  conversationModel: z.string(),
  fastModel: z.string(),
  maxCatalogCards: z.number(),
  maxHistoryMessages: z.number(),
  maxPromptTokens: z.number(),
  maxQueryBytes: z.number(),
  maxRows: z.number(),
  openAiApiKey: z.string().optional(),
  semanticModel: z.string(),
  sessionTtlSeconds: z.number(),
  sqlPlannerModel: z.string(),
  transcriptionModel: z.string(),
});
const AiSessionLogEntryDto = z.object({
  createdAt: z.string(),
  errorText: z.string().nullable(),
  eventType: z.string(),
  id: z.string(),
  payload: z.object({}).partial().passthrough(),
  sessionId: z.string(),
  userId: z.string(),
});
const AiSessionLogsResponseDto = z.object({ items: z.array(AiSessionLogEntryDto) });
const AiUsageControlsDto = z.object({
  allowAdHocSql: z.boolean(),
  dailyBudgetUsd: z.number(),
  enableAiMode: z.boolean(),
  enableVoiceMode: z.boolean(),
  maxEstimatedCostPerRequestUsd: z.number(),
  maxRequestsPerUserPerDay: z.number(),
  maxTranscriptionMinutesPerUserPerDay: z.number(),
  monthlyBudgetUsd: z.number(),
  showDebugSql: z.boolean(),
  updatedAt: z.string().nullable(),
  updatedBy: z.string().nullable(),
});
const AiUsageControlsItemDto = z.object({ item: AiUsageControlsDto });
const AiUsageControlsUpdateDto = z.object({
  allowAdHocSql: z.boolean(),
  dailyBudgetUsd: z.number(),
  enableAiMode: z.boolean(),
  enableVoiceMode: z.boolean(),
  maxEstimatedCostPerRequestUsd: z.number(),
  maxRequestsPerUserPerDay: z.number(),
  maxTranscriptionMinutesPerUserPerDay: z.number(),
  monthlyBudgetUsd: z.number(),
  showDebugSql: z.boolean(),
});
const AiUsageBudgetStateDto = z.object({
  dailyBudgetUsd: z.number(),
  dayBudgetExceeded: z.boolean(),
  daySpentUsd: z.number(),
  monthBudgetExceeded: z.boolean(),
  monthSpentUsd: z.number(),
  monthlyBudgetUsd: z.number(),
});
const AiUsageEventDto = z.object({
  completionTokens: z.number(),
  createdAt: z.string(),
  dryRunBytes: z.number(),
  estimatedCostUsd: z.number(),
  id: z.string(),
  model: z.string(),
  plannerMode: z.enum(["datasource_preview", "ad_hoc_sql"]),
  promptTokens: z.number(),
  sessionId: z.string(),
  totalTokens: z.number(),
  userId: z.string(),
});
const AiUsageEventsResponseDto = z.object({
  budget: AiUsageBudgetStateDto,
  controls: AiUsageControlsDto,
  items: z.array(AiUsageEventDto),
});
const AiWarehouseSchemaCatalogFileDto = z.object({
  filename: z.string(),
  sizeBytes: z.number(),
  updatedAt: z.string(),
});
const AiWarehouseSchemaCatalogResponseDto = z.object({ items: z.array(AiWarehouseSchemaCatalogFileDto) });
const AiWarehouseSchemaCatalogUploadDto = z.object({ content: z.string().min(1), filename: z.string().min(1) });
const PulseAnalysisDefinitionDto = z.object({
  access: z
    .object({
      consume: z
        .object({
          requiredGrants: z
            .array(
              z.enum([
                "PulseAudience:autovioAdmin",
                "PulseAudience:autovioStaff",
                "PulseAudience:school",
                "PulseDashboard:viewDraft",
                "PulseAnalysis:viewDraft",
                "PulseDatasource:viewDraft",
                "PulseMetric:viewDraft",
                "PulseScope:ownSchools",
                "PulseScope:global",
                "PulseDashboard:consume",
                "PulseDashboard:manage",
                "PulseDashboard:embed",
                "PulseAnalysis:consume",
                "PulseAnalysis:manage",
                "PulseDatasource:consume",
                "PulseDatasource:manage",
                "PulseMetric:consume",
                "PulseMetric:manage",
                "PulseAi:consume",
                "PulseAi:manage",
                "PulseApiClient:manage",
                "PulseTheme:manage",
                "PulsePermissions:manage",
              ]),
            )
            .default([]),
        })
        .partial(),
      manage: z
        .object({
          requiredGrants: z
            .array(
              z.enum([
                "PulseAudience:autovioAdmin",
                "PulseAudience:autovioStaff",
                "PulseAudience:school",
                "PulseDashboard:viewDraft",
                "PulseAnalysis:viewDraft",
                "PulseDatasource:viewDraft",
                "PulseMetric:viewDraft",
                "PulseScope:ownSchools",
                "PulseScope:global",
                "PulseDashboard:consume",
                "PulseDashboard:manage",
                "PulseDashboard:embed",
                "PulseAnalysis:consume",
                "PulseAnalysis:manage",
                "PulseDatasource:consume",
                "PulseDatasource:manage",
                "PulseMetric:consume",
                "PulseMetric:manage",
                "PulseAi:consume",
                "PulseAi:manage",
                "PulseApiClient:manage",
                "PulseTheme:manage",
                "PulsePermissions:manage",
              ]),
            )
            .default([]),
        })
        .partial(),
    })
    .partial()
    .optional(),
  analysisConfig: z
    .object({
      activeVersion: z.number().optional().default(1),
      analysisKey: z.string().min(1),
      elementOverrides: z
        .record(
          z
            .object({
              allowedFormats: z.array(z.enum(["rawdata", "tile-json", "web-component"])),
              exposed: z.boolean(),
              filterPolicy: z
                .object({
                  allowedRequestFilters: z.array(z.string()),
                  restrictedFilters: z.array(z.string()),
                  tokenBoundFilters: z
                    .array(
                      z.object({
                        claimKey: z.literal("drivingSchoolUids"),
                        fieldId: z.string().min(1),
                        operator: z.enum(["equals", "in"]).optional().default("in"),
                      }),
                    )
                    .default([]),
                })
                .partial(),
              note: z.string(),
            })
            .partial(),
        )
        .optional(),
      filterPolicy: z
        .object({
          allowedRequestFilters: z.array(z.string()),
          restrictedFilters: z.array(z.string()),
          tokenBoundFilters: z
            .array(
              z.object({
                claimKey: z.literal("drivingSchoolUids"),
                fieldId: z.string().min(1),
                operator: z.enum(["equals", "in"]).optional().default("in"),
              }),
            )
            .default([]),
        })
        .partial()
        .optional(),
      published: z.boolean().optional(),
    })
    .optional(),
  analysisKey: z.string().min(1),
  analysisKind: z.enum(["timeSeries", "period"]).optional().default("timeSeries"),
  category: z.string().optional().default("Custom"),
  chartConfig: z.object({
    analysisKind: z.enum(["timeSeries", "period"]).optional(),
    chartOmittedRowColumnKeys: z.array(z.string()).optional(),
    chartRowValueExclusion: z.record(z.array(z.string())).optional(),
    colorBySeriesKey: z.record(z.string()).optional(),
    colorBySeriesName: z.record(z.string()).optional(),
    colorPalette: z.array(z.string()).optional(),
    contentScroll: z.enum(["auto", "show", "hide"]).optional(),
    customColorPalette: z.array(z.string()).optional(),
    emptyValueHandling: z.enum(["hide", "show"]).optional(),
    gauge: z
      .object({
        max: z.number(),
        min: z.number(),
        segments: z.array(z.object({ color: z.string().optional(), label: z.string().optional(), to: z.number() })),
        style: z.enum(["progress", "grade"]),
        valueLabel: z.string(),
      })
      .partial()
      .optional(),
    heatmap: z
      .object({
        cellColorBy: z.enum(["scale", "x", "y", "value"]),
        cellColorIntensity: z.enum(["none", "value"]),
        cellColorMap: z.record(z.string()),
        cellLabelByValue: z.record(z.string()),
        cellValueCategories: z.array(
          z.object({
            source: z.object({
              aggregation: z.enum(["sum", "avg", "count", "countDistinct", "min", "max", "ratio", "custom"]),
              fieldId: z.string().optional(),
              label: z.string().optional(),
              metricId: z.string().min(1).optional(),
              periodRollupStrategy: z
                .enum(["metricDefault", "standard", "sum", "avg", "min", "max", "lastPeriod"])
                .optional(),
              sourceAlias: z.string().optional(),
            }),
            value: z.number(),
          }),
        ),
        segmentField: z.object({ fieldId: z.string(), sourceAlias: z.string() }).partial(),
        segmentLabel: z.string(),
        xDomain: z.enum(["auto", "hour", "weekday", "data"]),
        xDomainValues: z.array(z.string()),
        xLabelFormat: z.enum(["auto", "dateShort"]),
        yDomain: z.enum(["auto", "hour", "weekday", "data"]),
        yDomainValues: z.array(z.string()),
        yField: z.object({ fieldId: z.string(), sourceAlias: z.string() }).partial(),
      })
      .partial()
      .optional(),
    radarTargetValue: z.number().optional(),
    referenceLines: z
      .array(
        z.object({
          color: z.string().optional(),
          id: z.string().min(1),
          label: z.string().optional(),
          scope: z.enum(["total", "perSeries"]).optional().default("total"),
          source: z.object({
            aggregation: z.enum(["sum", "avg", "count", "countDistinct", "min", "max", "ratio", "custom"]),
            fieldId: z.string().optional(),
            label: z.string().optional(),
            metricId: z.string().min(1).optional(),
            periodRollupStrategy: z
              .enum(["metricDefault", "standard", "sum", "avg", "min", "max", "lastPeriod"])
              .optional(),
            sourceAlias: z.string().optional(),
          }),
          style: z.enum(["solid", "dashed", "dotted"]).optional().default("dashed"),
          value: z.number().optional(),
        }),
      )
      .optional(),
    series: z
      .array(
        z.object({
          id: z.string().min(1),
          source: z.object({
            aggregation: z.enum(["sum", "avg", "count", "countDistinct", "min", "max", "ratio", "custom"]),
            fieldId: z.string().optional(),
            label: z.string().optional(),
            metricId: z.string().min(1).optional(),
            periodRollupStrategy: z
              .enum(["metricDefault", "standard", "sum", "avg", "min", "max", "lastPeriod"])
              .optional(),
            sourceAlias: z.string().optional(),
          }),
          stackGroup: z.string().min(1).optional(),
        }),
      )
      .optional()
      .default([]),
    seriesNameByKey: z.record(z.string()).optional(),
    seriesOrder: z.array(z.string()).optional(),
    seriesSort: z.enum(["valueDesc", "valueAsc", "nameAsc", "nameDesc", "datasource"]).optional(),
    stackMode: z.enum(["absolute", "percent"]).optional(),
    topN: z.number().optional(),
    type: z.enum([
      "bar",
      "stackedBar",
      "line",
      "area",
      "stackedArea",
      "horizontalBar",
      "stackedHorizontalBar",
      "pie",
      "donut",
      "scatter",
      "bubble",
      "gauge",
      "heatmap",
      "radar",
      "stacked",
    ]),
    xField: z
      .object({ fieldId: z.string(), sourceAlias: z.string() })
      .partial()
      .optional()
      .default({ fieldId: "", sourceAlias: "" }),
  }),
  dashboardVariants: z
    .array(
      z.union([
        z.object({
          chartConfig: z.object({
            analysisKind: z.enum(["timeSeries", "period"]).optional(),
            chartOmittedRowColumnKeys: z.array(z.string()).optional(),
            chartRowValueExclusion: z.record(z.array(z.string())).optional(),
            colorBySeriesKey: z.record(z.string()).optional(),
            colorBySeriesName: z.record(z.string()).optional(),
            colorPalette: z.array(z.string()).optional(),
            contentScroll: z.enum(["auto", "show", "hide"]).optional(),
            customColorPalette: z.array(z.string()).optional(),
            emptyValueHandling: z.enum(["hide", "show"]).optional(),
            gauge: z
              .object({
                max: z.number(),
                min: z.number(),
                segments: z.array(
                  z.object({ color: z.string().optional(), label: z.string().optional(), to: z.number() }),
                ),
                style: z.enum(["progress", "grade"]),
                valueLabel: z.string(),
              })
              .partial()
              .optional(),
            heatmap: z
              .object({
                cellColorBy: z.enum(["scale", "x", "y", "value"]),
                cellColorIntensity: z.enum(["none", "value"]),
                cellColorMap: z.record(z.string()),
                cellLabelByValue: z.record(z.string()),
                cellValueCategories: z.array(
                  z.object({
                    source: z.object({
                      aggregation: z.enum(["sum", "avg", "count", "countDistinct", "min", "max", "ratio", "custom"]),
                      fieldId: z.string().optional(),
                      label: z.string().optional(),
                      metricId: z.string().min(1).optional(),
                      periodRollupStrategy: z
                        .enum(["metricDefault", "standard", "sum", "avg", "min", "max", "lastPeriod"])
                        .optional(),
                      sourceAlias: z.string().optional(),
                    }),
                    value: z.number(),
                  }),
                ),
                segmentField: z.object({ fieldId: z.string(), sourceAlias: z.string() }).partial(),
                segmentLabel: z.string(),
                xDomain: z.enum(["auto", "hour", "weekday", "data"]),
                xDomainValues: z.array(z.string()),
                xLabelFormat: z.enum(["auto", "dateShort"]),
                yDomain: z.enum(["auto", "hour", "weekday", "data"]),
                yDomainValues: z.array(z.string()),
                yField: z.object({ fieldId: z.string(), sourceAlias: z.string() }).partial(),
              })
              .partial()
              .optional(),
            radarTargetValue: z.number().optional(),
            referenceLines: z
              .array(
                z.object({
                  color: z.string().optional(),
                  id: z.string().min(1),
                  label: z.string().optional(),
                  scope: z.enum(["total", "perSeries"]).optional().default("total"),
                  source: z.object({
                    aggregation: z.enum(["sum", "avg", "count", "countDistinct", "min", "max", "ratio", "custom"]),
                    fieldId: z.string().optional(),
                    label: z.string().optional(),
                    metricId: z.string().min(1).optional(),
                    periodRollupStrategy: z
                      .enum(["metricDefault", "standard", "sum", "avg", "min", "max", "lastPeriod"])
                      .optional(),
                    sourceAlias: z.string().optional(),
                  }),
                  style: z.enum(["solid", "dashed", "dotted"]).optional().default("dashed"),
                  value: z.number().optional(),
                }),
              )
              .optional(),
            series: z
              .array(
                z.object({
                  id: z.string().min(1),
                  source: z.object({
                    aggregation: z.enum(["sum", "avg", "count", "countDistinct", "min", "max", "ratio", "custom"]),
                    fieldId: z.string().optional(),
                    label: z.string().optional(),
                    metricId: z.string().min(1).optional(),
                    periodRollupStrategy: z
                      .enum(["metricDefault", "standard", "sum", "avg", "min", "max", "lastPeriod"])
                      .optional(),
                    sourceAlias: z.string().optional(),
                  }),
                  stackGroup: z.string().min(1).optional(),
                }),
              )
              .optional()
              .default([]),
            seriesNameByKey: z.record(z.string()).optional(),
            seriesOrder: z.array(z.string()).optional(),
            seriesSort: z.enum(["valueDesc", "valueAsc", "nameAsc", "nameDesc", "datasource"]).optional(),
            stackMode: z.enum(["absolute", "percent"]).optional(),
            topN: z.number().optional(),
            type: z.enum([
              "bar",
              "stackedBar",
              "line",
              "area",
              "stackedArea",
              "horizontalBar",
              "stackedHorizontalBar",
              "pie",
              "donut",
              "scatter",
              "bubble",
              "gauge",
              "heatmap",
              "radar",
              "stacked",
            ]),
            xField: z
              .object({ fieldId: z.string(), sourceAlias: z.string() })
              .partial()
              .optional()
              .default({ fieldId: "", sourceAlias: "" }),
          }),
          dashboardDefaults: z
            .object({
              full: z
                .object({
                  axisFontSize: z.enum(["auto", "xs", "s", "m", "l"]),
                  axisLabels: z.enum(["auto", "show", "hide"]),
                  axisNumberDisplay: z.enum(["auto", "full", "compact", "custom"]),
                  barGap: z.enum(["auto", "tight", "normal", "wide"]),
                  barSize: z.enum(["auto", "slim", "normal", "wide"]),
                  barSizing: z.enum(["auto", "relative", "fixed"]),
                  density: z.enum(["auto", "comfortable", "compact"]),
                  gridLines: z.enum(["auto", "show", "hide"]),
                  heatmapAxisDomain: z.enum(["auto", "data", "full"]),
                  heatmapCellBorders: z.enum(["auto", "show", "hide", "subtle", "separated"]),
                  heatmapColorScale: z.enum(["auto", "standard", "accent", "custom"]),
                  heatmapZeroValueDisplay: z.enum(["auto", "scale", "background"]),
                  labelPriority: z.array(z.enum(["totals", "series", "axis", "legend"])),
                  layout: z.enum(["auto", "tight", "balanced", "roomy"]),
                  legend: z.enum(["auto", "show", "hide"]),
                  legendFontSize: z.enum(["auto", "xs", "s", "m", "l"]),
                  numberDisplay: z.enum(["auto", "full", "compact", "custom"]),
                  referenceLineLabelFontSize: z.enum(["auto", "xs", "s", "m", "l"]),
                  referenceLineLabelMode: z.enum(["labelAndValue", "valueOnly", "labelOnly"]),
                  referenceLineLabels: z.enum(["auto", "show", "hide"]),
                  referenceLineLegend: z.enum(["auto", "show", "hide"]),
                  referenceLineNumberCustomFormat: z.object({
                    currency: z.string().optional().default("EUR"),
                    customNegativePattern: z.string().optional(),
                    customPositivePattern: z.string().optional(),
                    displayUnit: z.enum(["none", "thousands", "millions", "auto"]).optional(),
                    fractionDigits: z.number().optional(),
                    preset: z.enum(["default", "number", "currency", "percent", "time", "custom"]),
                    separators: z.enum(["us", "de"]),
                  }),
                  referenceLineNumberDisplay: z.enum(["auto", "full", "compact", "custom"]),
                  totalLabelFontSize: z.enum(["auto", "xs", "s", "m", "l"]),
                  totalLabelPosition: z.enum(["auto", "top", "inside"]),
                  totalLabels: z.enum(["auto", "show", "hide", "required"]),
                  totalNumberCustomFormat: z.object({
                    currency: z.string().optional().default("EUR"),
                    customNegativePattern: z.string().optional(),
                    customPositivePattern: z.string().optional(),
                    displayUnit: z.enum(["none", "thousands", "millions", "auto"]).optional(),
                    fractionDigits: z.number().optional(),
                    preset: z.enum(["default", "number", "currency", "percent", "time", "custom"]),
                    separators: z.enum(["us", "de"]),
                  }),
                  totalNumberDisplay: z.enum(["auto", "full", "compact", "custom"]),
                  valueLabelFontSize: z.enum(["auto", "xs", "s", "m", "l"]),
                  valueLabels: z.enum(["auto", "show", "hide"]),
                  valueNumberCustomFormat: z.object({
                    currency: z.string().optional().default("EUR"),
                    customNegativePattern: z.string().optional(),
                    customPositivePattern: z.string().optional(),
                    displayUnit: z.enum(["none", "thousands", "millions", "auto"]).optional(),
                    fractionDigits: z.number().optional(),
                    preset: z.enum(["default", "number", "currency", "percent", "time", "custom"]),
                    separators: z.enum(["us", "de"]),
                  }),
                  valueNumberDisplay: z.enum(["auto", "full", "compact", "custom"]),
                  xAxisFontSize: z.enum(["auto", "xs", "s", "m", "l"]),
                  xAxisLabelCustomFormat: z.object({
                    customPattern: z.string().optional(),
                    preset: z.enum(["default", "isoDate", "dateDE", "dateUS", "dateTimeDE", "dateTimeUS", "custom"]),
                  }),
                  xAxisLabelFormat: z.enum([
                    "auto",
                    "source",
                    "yearQuarter",
                    "shortYearQuarter",
                    "quarter",
                    "monthShortYear",
                    "monthNumericYear",
                    "custom",
                  ]),
                  xAxisLabelFrequency: z.enum(["auto", "all", "every2", "every3", "firstLast"]),
                  xAxisLabelRotation: z.enum(["auto", "none", "diagonal", "vertical"]),
                  xAxisLabels: z.enum(["auto", "show", "hide", "rotate", "sparse"]),
                  yAxisFontSize: z.enum(["auto", "xs", "s", "m", "l"]),
                  yAxisLabels: z.enum(["auto", "show", "hide"]),
                  yAxisNumberCustomFormat: z.object({
                    currency: z.string().optional().default("EUR"),
                    customNegativePattern: z.string().optional(),
                    customPositivePattern: z.string().optional(),
                    displayUnit: z.enum(["none", "thousands", "millions", "auto"]).optional(),
                    fractionDigits: z.number().optional(),
                    preset: z.enum(["default", "number", "currency", "percent", "time", "custom"]),
                    separators: z.enum(["us", "de"]),
                  }),
                  yAxisNumberDisplay: z.enum(["auto", "full", "compact", "custom"]),
                })
                .partial(),
              large: z
                .object({
                  axisFontSize: z.enum(["auto", "xs", "s", "m", "l"]),
                  axisLabels: z.enum(["auto", "show", "hide"]),
                  axisNumberDisplay: z.enum(["auto", "full", "compact", "custom"]),
                  barGap: z.enum(["auto", "tight", "normal", "wide"]),
                  barSize: z.enum(["auto", "slim", "normal", "wide"]),
                  barSizing: z.enum(["auto", "relative", "fixed"]),
                  density: z.enum(["auto", "comfortable", "compact"]),
                  gridLines: z.enum(["auto", "show", "hide"]),
                  heatmapAxisDomain: z.enum(["auto", "data", "full"]),
                  heatmapCellBorders: z.enum(["auto", "show", "hide", "subtle", "separated"]),
                  heatmapColorScale: z.enum(["auto", "standard", "accent", "custom"]),
                  heatmapZeroValueDisplay: z.enum(["auto", "scale", "background"]),
                  labelPriority: z.array(z.enum(["totals", "series", "axis", "legend"])),
                  layout: z.enum(["auto", "tight", "balanced", "roomy"]),
                  legend: z.enum(["auto", "show", "hide"]),
                  legendFontSize: z.enum(["auto", "xs", "s", "m", "l"]),
                  numberDisplay: z.enum(["auto", "full", "compact", "custom"]),
                  referenceLineLabelFontSize: z.enum(["auto", "xs", "s", "m", "l"]),
                  referenceLineLabelMode: z.enum(["labelAndValue", "valueOnly", "labelOnly"]),
                  referenceLineLabels: z.enum(["auto", "show", "hide"]),
                  referenceLineLegend: z.enum(["auto", "show", "hide"]),
                  referenceLineNumberCustomFormat: z.object({
                    currency: z.string().optional().default("EUR"),
                    customNegativePattern: z.string().optional(),
                    customPositivePattern: z.string().optional(),
                    displayUnit: z.enum(["none", "thousands", "millions", "auto"]).optional(),
                    fractionDigits: z.number().optional(),
                    preset: z.enum(["default", "number", "currency", "percent", "time", "custom"]),
                    separators: z.enum(["us", "de"]),
                  }),
                  referenceLineNumberDisplay: z.enum(["auto", "full", "compact", "custom"]),
                  totalLabelFontSize: z.enum(["auto", "xs", "s", "m", "l"]),
                  totalLabelPosition: z.enum(["auto", "top", "inside"]),
                  totalLabels: z.enum(["auto", "show", "hide", "required"]),
                  totalNumberCustomFormat: z.object({
                    currency: z.string().optional().default("EUR"),
                    customNegativePattern: z.string().optional(),
                    customPositivePattern: z.string().optional(),
                    displayUnit: z.enum(["none", "thousands", "millions", "auto"]).optional(),
                    fractionDigits: z.number().optional(),
                    preset: z.enum(["default", "number", "currency", "percent", "time", "custom"]),
                    separators: z.enum(["us", "de"]),
                  }),
                  totalNumberDisplay: z.enum(["auto", "full", "compact", "custom"]),
                  valueLabelFontSize: z.enum(["auto", "xs", "s", "m", "l"]),
                  valueLabels: z.enum(["auto", "show", "hide"]),
                  valueNumberCustomFormat: z.object({
                    currency: z.string().optional().default("EUR"),
                    customNegativePattern: z.string().optional(),
                    customPositivePattern: z.string().optional(),
                    displayUnit: z.enum(["none", "thousands", "millions", "auto"]).optional(),
                    fractionDigits: z.number().optional(),
                    preset: z.enum(["default", "number", "currency", "percent", "time", "custom"]),
                    separators: z.enum(["us", "de"]),
                  }),
                  valueNumberDisplay: z.enum(["auto", "full", "compact", "custom"]),
                  xAxisFontSize: z.enum(["auto", "xs", "s", "m", "l"]),
                  xAxisLabelCustomFormat: z.object({
                    customPattern: z.string().optional(),
                    preset: z.enum(["default", "isoDate", "dateDE", "dateUS", "dateTimeDE", "dateTimeUS", "custom"]),
                  }),
                  xAxisLabelFormat: z.enum([
                    "auto",
                    "source",
                    "yearQuarter",
                    "shortYearQuarter",
                    "quarter",
                    "monthShortYear",
                    "monthNumericYear",
                    "custom",
                  ]),
                  xAxisLabelFrequency: z.enum(["auto", "all", "every2", "every3", "firstLast"]),
                  xAxisLabelRotation: z.enum(["auto", "none", "diagonal", "vertical"]),
                  xAxisLabels: z.enum(["auto", "show", "hide", "rotate", "sparse"]),
                  yAxisFontSize: z.enum(["auto", "xs", "s", "m", "l"]),
                  yAxisLabels: z.enum(["auto", "show", "hide"]),
                  yAxisNumberCustomFormat: z.object({
                    currency: z.string().optional().default("EUR"),
                    customNegativePattern: z.string().optional(),
                    customPositivePattern: z.string().optional(),
                    displayUnit: z.enum(["none", "thousands", "millions", "auto"]).optional(),
                    fractionDigits: z.number().optional(),
                    preset: z.enum(["default", "number", "currency", "percent", "time", "custom"]),
                    separators: z.enum(["us", "de"]),
                  }),
                  yAxisNumberDisplay: z.enum(["auto", "full", "compact", "custom"]),
                })
                .partial(),
              medium: z
                .object({
                  axisFontSize: z.enum(["auto", "xs", "s", "m", "l"]),
                  axisLabels: z.enum(["auto", "show", "hide"]),
                  axisNumberDisplay: z.enum(["auto", "full", "compact", "custom"]),
                  barGap: z.enum(["auto", "tight", "normal", "wide"]),
                  barSize: z.enum(["auto", "slim", "normal", "wide"]),
                  barSizing: z.enum(["auto", "relative", "fixed"]),
                  density: z.enum(["auto", "comfortable", "compact"]),
                  gridLines: z.enum(["auto", "show", "hide"]),
                  heatmapAxisDomain: z.enum(["auto", "data", "full"]),
                  heatmapCellBorders: z.enum(["auto", "show", "hide", "subtle", "separated"]),
                  heatmapColorScale: z.enum(["auto", "standard", "accent", "custom"]),
                  heatmapZeroValueDisplay: z.enum(["auto", "scale", "background"]),
                  labelPriority: z.array(z.enum(["totals", "series", "axis", "legend"])),
                  layout: z.enum(["auto", "tight", "balanced", "roomy"]),
                  legend: z.enum(["auto", "show", "hide"]),
                  legendFontSize: z.enum(["auto", "xs", "s", "m", "l"]),
                  numberDisplay: z.enum(["auto", "full", "compact", "custom"]),
                  referenceLineLabelFontSize: z.enum(["auto", "xs", "s", "m", "l"]),
                  referenceLineLabelMode: z.enum(["labelAndValue", "valueOnly", "labelOnly"]),
                  referenceLineLabels: z.enum(["auto", "show", "hide"]),
                  referenceLineLegend: z.enum(["auto", "show", "hide"]),
                  referenceLineNumberCustomFormat: z.object({
                    currency: z.string().optional().default("EUR"),
                    customNegativePattern: z.string().optional(),
                    customPositivePattern: z.string().optional(),
                    displayUnit: z.enum(["none", "thousands", "millions", "auto"]).optional(),
                    fractionDigits: z.number().optional(),
                    preset: z.enum(["default", "number", "currency", "percent", "time", "custom"]),
                    separators: z.enum(["us", "de"]),
                  }),
                  referenceLineNumberDisplay: z.enum(["auto", "full", "compact", "custom"]),
                  totalLabelFontSize: z.enum(["auto", "xs", "s", "m", "l"]),
                  totalLabelPosition: z.enum(["auto", "top", "inside"]),
                  totalLabels: z.enum(["auto", "show", "hide", "required"]),
                  totalNumberCustomFormat: z.object({
                    currency: z.string().optional().default("EUR"),
                    customNegativePattern: z.string().optional(),
                    customPositivePattern: z.string().optional(),
                    displayUnit: z.enum(["none", "thousands", "millions", "auto"]).optional(),
                    fractionDigits: z.number().optional(),
                    preset: z.enum(["default", "number", "currency", "percent", "time", "custom"]),
                    separators: z.enum(["us", "de"]),
                  }),
                  totalNumberDisplay: z.enum(["auto", "full", "compact", "custom"]),
                  valueLabelFontSize: z.enum(["auto", "xs", "s", "m", "l"]),
                  valueLabels: z.enum(["auto", "show", "hide"]),
                  valueNumberCustomFormat: z.object({
                    currency: z.string().optional().default("EUR"),
                    customNegativePattern: z.string().optional(),
                    customPositivePattern: z.string().optional(),
                    displayUnit: z.enum(["none", "thousands", "millions", "auto"]).optional(),
                    fractionDigits: z.number().optional(),
                    preset: z.enum(["default", "number", "currency", "percent", "time", "custom"]),
                    separators: z.enum(["us", "de"]),
                  }),
                  valueNumberDisplay: z.enum(["auto", "full", "compact", "custom"]),
                  xAxisFontSize: z.enum(["auto", "xs", "s", "m", "l"]),
                  xAxisLabelCustomFormat: z.object({
                    customPattern: z.string().optional(),
                    preset: z.enum(["default", "isoDate", "dateDE", "dateUS", "dateTimeDE", "dateTimeUS", "custom"]),
                  }),
                  xAxisLabelFormat: z.enum([
                    "auto",
                    "source",
                    "yearQuarter",
                    "shortYearQuarter",
                    "quarter",
                    "monthShortYear",
                    "monthNumericYear",
                    "custom",
                  ]),
                  xAxisLabelFrequency: z.enum(["auto", "all", "every2", "every3", "firstLast"]),
                  xAxisLabelRotation: z.enum(["auto", "none", "diagonal", "vertical"]),
                  xAxisLabels: z.enum(["auto", "show", "hide", "rotate", "sparse"]),
                  yAxisFontSize: z.enum(["auto", "xs", "s", "m", "l"]),
                  yAxisLabels: z.enum(["auto", "show", "hide"]),
                  yAxisNumberCustomFormat: z.object({
                    currency: z.string().optional().default("EUR"),
                    customNegativePattern: z.string().optional(),
                    customPositivePattern: z.string().optional(),
                    displayUnit: z.enum(["none", "thousands", "millions", "auto"]).optional(),
                    fractionDigits: z.number().optional(),
                    preset: z.enum(["default", "number", "currency", "percent", "time", "custom"]),
                    separators: z.enum(["us", "de"]),
                  }),
                  yAxisNumberDisplay: z.enum(["auto", "full", "compact", "custom"]),
                })
                .partial(),
              small: z
                .object({
                  axisFontSize: z.enum(["auto", "xs", "s", "m", "l"]),
                  axisLabels: z.enum(["auto", "show", "hide"]),
                  axisNumberDisplay: z.enum(["auto", "full", "compact", "custom"]),
                  barGap: z.enum(["auto", "tight", "normal", "wide"]),
                  barSize: z.enum(["auto", "slim", "normal", "wide"]),
                  barSizing: z.enum(["auto", "relative", "fixed"]),
                  density: z.enum(["auto", "comfortable", "compact"]),
                  gridLines: z.enum(["auto", "show", "hide"]),
                  heatmapAxisDomain: z.enum(["auto", "data", "full"]),
                  heatmapCellBorders: z.enum(["auto", "show", "hide", "subtle", "separated"]),
                  heatmapColorScale: z.enum(["auto", "standard", "accent", "custom"]),
                  heatmapZeroValueDisplay: z.enum(["auto", "scale", "background"]),
                  labelPriority: z.array(z.enum(["totals", "series", "axis", "legend"])),
                  layout: z.enum(["auto", "tight", "balanced", "roomy"]),
                  legend: z.enum(["auto", "show", "hide"]),
                  legendFontSize: z.enum(["auto", "xs", "s", "m", "l"]),
                  numberDisplay: z.enum(["auto", "full", "compact", "custom"]),
                  referenceLineLabelFontSize: z.enum(["auto", "xs", "s", "m", "l"]),
                  referenceLineLabelMode: z.enum(["labelAndValue", "valueOnly", "labelOnly"]),
                  referenceLineLabels: z.enum(["auto", "show", "hide"]),
                  referenceLineLegend: z.enum(["auto", "show", "hide"]),
                  referenceLineNumberCustomFormat: z.object({
                    currency: z.string().optional().default("EUR"),
                    customNegativePattern: z.string().optional(),
                    customPositivePattern: z.string().optional(),
                    displayUnit: z.enum(["none", "thousands", "millions", "auto"]).optional(),
                    fractionDigits: z.number().optional(),
                    preset: z.enum(["default", "number", "currency", "percent", "time", "custom"]),
                    separators: z.enum(["us", "de"]),
                  }),
                  referenceLineNumberDisplay: z.enum(["auto", "full", "compact", "custom"]),
                  totalLabelFontSize: z.enum(["auto", "xs", "s", "m", "l"]),
                  totalLabelPosition: z.enum(["auto", "top", "inside"]),
                  totalLabels: z.enum(["auto", "show", "hide", "required"]),
                  totalNumberCustomFormat: z.object({
                    currency: z.string().optional().default("EUR"),
                    customNegativePattern: z.string().optional(),
                    customPositivePattern: z.string().optional(),
                    displayUnit: z.enum(["none", "thousands", "millions", "auto"]).optional(),
                    fractionDigits: z.number().optional(),
                    preset: z.enum(["default", "number", "currency", "percent", "time", "custom"]),
                    separators: z.enum(["us", "de"]),
                  }),
                  totalNumberDisplay: z.enum(["auto", "full", "compact", "custom"]),
                  valueLabelFontSize: z.enum(["auto", "xs", "s", "m", "l"]),
                  valueLabels: z.enum(["auto", "show", "hide"]),
                  valueNumberCustomFormat: z.object({
                    currency: z.string().optional().default("EUR"),
                    customNegativePattern: z.string().optional(),
                    customPositivePattern: z.string().optional(),
                    displayUnit: z.enum(["none", "thousands", "millions", "auto"]).optional(),
                    fractionDigits: z.number().optional(),
                    preset: z.enum(["default", "number", "currency", "percent", "time", "custom"]),
                    separators: z.enum(["us", "de"]),
                  }),
                  valueNumberDisplay: z.enum(["auto", "full", "compact", "custom"]),
                  xAxisFontSize: z.enum(["auto", "xs", "s", "m", "l"]),
                  xAxisLabelCustomFormat: z.object({
                    customPattern: z.string().optional(),
                    preset: z.enum(["default", "isoDate", "dateDE", "dateUS", "dateTimeDE", "dateTimeUS", "custom"]),
                  }),
                  xAxisLabelFormat: z.enum([
                    "auto",
                    "source",
                    "yearQuarter",
                    "shortYearQuarter",
                    "quarter",
                    "monthShortYear",
                    "monthNumericYear",
                    "custom",
                  ]),
                  xAxisLabelFrequency: z.enum(["auto", "all", "every2", "every3", "firstLast"]),
                  xAxisLabelRotation: z.enum(["auto", "none", "diagonal", "vertical"]),
                  xAxisLabels: z.enum(["auto", "show", "hide", "rotate", "sparse"]),
                  yAxisFontSize: z.enum(["auto", "xs", "s", "m", "l"]),
                  yAxisLabels: z.enum(["auto", "show", "hide"]),
                  yAxisNumberCustomFormat: z.object({
                    currency: z.string().optional().default("EUR"),
                    customNegativePattern: z.string().optional(),
                    customPositivePattern: z.string().optional(),
                    displayUnit: z.enum(["none", "thousands", "millions", "auto"]).optional(),
                    fractionDigits: z.number().optional(),
                    preset: z.enum(["default", "number", "currency", "percent", "time", "custom"]),
                    separators: z.enum(["us", "de"]),
                  }),
                  yAxisNumberDisplay: z.enum(["auto", "full", "compact", "custom"]),
                })
                .partial(),
            })
            .partial()
            .optional(),
          dashboardGrid: z
            .object({ h: z.number().int().gte(1).lte(9007199254740991), w: z.number().int().gte(1).lte(12) })
            .optional(),
          dashboardGridBySize: z
            .object({
              full: z.object({ h: z.number().int().gte(1).lte(9007199254740991), w: z.number().int().gte(1).lte(12) }),
              large: z.object({ h: z.number().int().gte(1).lte(9007199254740991), w: z.number().int().gte(1).lte(12) }),
              medium: z.object({
                h: z.number().int().gte(1).lte(9007199254740991),
                w: z.number().int().gte(1).lte(12),
              }),
              small: z.object({ h: z.number().int().gte(1).lte(9007199254740991), w: z.number().int().gte(1).lte(12) }),
            })
            .partial()
            .optional(),
          id: z.string().min(1),
          kind: z.literal("chart"),
          name: z.string().min(1),
          preferredSize: z.enum(["small", "medium", "large", "full"]).optional(),
        }),
        z.object({
          dashboardDefaults: z
            .object({
              full: z
                .object({
                  comparison: z.enum(["auto", "show", "hide"]),
                  comparisonWindow: z.enum(["auto", "alignedToDate", "fullPeriod"]),
                  density: z.enum(["auto", "comfortable", "compact"]),
                  label: z.enum(["auto", "show", "hide"]),
                  periodHint: z.enum(["auto", "show", "hide"]),
                  style: z.enum(["standard", "sparkline", "hero", "multi"]),
                  subtitle: z.enum(["auto", "show", "hide"]),
                  valueNumberCustomFormat: z.object({
                    currency: z.string().optional().default("EUR"),
                    customNegativePattern: z.string().optional(),
                    customPositivePattern: z.string().optional(),
                    displayUnit: z.enum(["none", "thousands", "millions", "auto"]).optional(),
                    fractionDigits: z.number().optional(),
                    preset: z.enum(["default", "number", "currency", "percent", "time", "custom"]),
                    separators: z.enum(["us", "de"]),
                  }),
                  valueNumberDisplay: z.enum(["auto", "full", "compact", "custom"]),
                  valueWindow: z.enum(["currentPeriod", "selectedRange"]),
                })
                .partial(),
              large: z
                .object({
                  comparison: z.enum(["auto", "show", "hide"]),
                  comparisonWindow: z.enum(["auto", "alignedToDate", "fullPeriod"]),
                  density: z.enum(["auto", "comfortable", "compact"]),
                  label: z.enum(["auto", "show", "hide"]),
                  periodHint: z.enum(["auto", "show", "hide"]),
                  style: z.enum(["standard", "sparkline", "hero", "multi"]),
                  subtitle: z.enum(["auto", "show", "hide"]),
                  valueNumberCustomFormat: z.object({
                    currency: z.string().optional().default("EUR"),
                    customNegativePattern: z.string().optional(),
                    customPositivePattern: z.string().optional(),
                    displayUnit: z.enum(["none", "thousands", "millions", "auto"]).optional(),
                    fractionDigits: z.number().optional(),
                    preset: z.enum(["default", "number", "currency", "percent", "time", "custom"]),
                    separators: z.enum(["us", "de"]),
                  }),
                  valueNumberDisplay: z.enum(["auto", "full", "compact", "custom"]),
                  valueWindow: z.enum(["currentPeriod", "selectedRange"]),
                })
                .partial(),
              medium: z
                .object({
                  comparison: z.enum(["auto", "show", "hide"]),
                  comparisonWindow: z.enum(["auto", "alignedToDate", "fullPeriod"]),
                  density: z.enum(["auto", "comfortable", "compact"]),
                  label: z.enum(["auto", "show", "hide"]),
                  periodHint: z.enum(["auto", "show", "hide"]),
                  style: z.enum(["standard", "sparkline", "hero", "multi"]),
                  subtitle: z.enum(["auto", "show", "hide"]),
                  valueNumberCustomFormat: z.object({
                    currency: z.string().optional().default("EUR"),
                    customNegativePattern: z.string().optional(),
                    customPositivePattern: z.string().optional(),
                    displayUnit: z.enum(["none", "thousands", "millions", "auto"]).optional(),
                    fractionDigits: z.number().optional(),
                    preset: z.enum(["default", "number", "currency", "percent", "time", "custom"]),
                    separators: z.enum(["us", "de"]),
                  }),
                  valueNumberDisplay: z.enum(["auto", "full", "compact", "custom"]),
                  valueWindow: z.enum(["currentPeriod", "selectedRange"]),
                })
                .partial(),
              small: z
                .object({
                  comparison: z.enum(["auto", "show", "hide"]),
                  comparisonWindow: z.enum(["auto", "alignedToDate", "fullPeriod"]),
                  density: z.enum(["auto", "comfortable", "compact"]),
                  label: z.enum(["auto", "show", "hide"]),
                  periodHint: z.enum(["auto", "show", "hide"]),
                  style: z.enum(["standard", "sparkline", "hero", "multi"]),
                  subtitle: z.enum(["auto", "show", "hide"]),
                  valueNumberCustomFormat: z.object({
                    currency: z.string().optional().default("EUR"),
                    customNegativePattern: z.string().optional(),
                    customPositivePattern: z.string().optional(),
                    displayUnit: z.enum(["none", "thousands", "millions", "auto"]).optional(),
                    fractionDigits: z.number().optional(),
                    preset: z.enum(["default", "number", "currency", "percent", "time", "custom"]),
                    separators: z.enum(["us", "de"]),
                  }),
                  valueNumberDisplay: z.enum(["auto", "full", "compact", "custom"]),
                  valueWindow: z.enum(["currentPeriod", "selectedRange"]),
                })
                .partial(),
            })
            .partial()
            .optional(),
          dashboardGrid: z
            .object({ h: z.number().int().gte(1).lte(9007199254740991), w: z.number().int().gte(1).lte(12) })
            .optional(),
          dashboardGridBySize: z
            .object({
              full: z.object({ h: z.number().int().gte(1).lte(9007199254740991), w: z.number().int().gte(1).lte(12) }),
              large: z.object({ h: z.number().int().gte(1).lte(9007199254740991), w: z.number().int().gte(1).lte(12) }),
              medium: z.object({
                h: z.number().int().gte(1).lte(9007199254740991),
                w: z.number().int().gte(1).lte(12),
              }),
              small: z.object({ h: z.number().int().gte(1).lte(9007199254740991), w: z.number().int().gte(1).lte(12) }),
            })
            .partial()
            .optional(),
          id: z.string().min(1),
          kind: z.literal("kpi"),
          metricId: z.string().min(1).optional(),
          name: z.string().min(1),
          preferredSize: z.enum(["small", "medium", "large", "full"]).optional(),
          valueRef: z
            .object({
              aggregation: z.enum(["sum", "avg", "count", "countDistinct", "min", "max", "ratio", "custom"]),
              fieldId: z.string().optional(),
              label: z.string().optional(),
              metricId: z.string().min(1).optional(),
              periodRollupStrategy: z
                .enum(["metricDefault", "standard", "sum", "avg", "min", "max", "lastPeriod"])
                .optional(),
              sourceAlias: z.string().optional(),
            })
            .optional(),
        }),
        z.object({
          dashboardDefaults: z
            .object({
              full: z
                .object({
                  columnLabels: z.enum(["auto", "full", "compact"]),
                  density: z.enum(["auto", "comfortable", "compact"]),
                  header: z.enum(["auto", "show", "hide"]),
                  overflow: z.enum(["fit", "scroll"]),
                  pagination: z.enum(["auto", "show", "hide"]),
                  rowLimit: z.number(),
                  totals: z.enum(["auto", "show", "hide"]),
                })
                .partial(),
              large: z
                .object({
                  columnLabels: z.enum(["auto", "full", "compact"]),
                  density: z.enum(["auto", "comfortable", "compact"]),
                  header: z.enum(["auto", "show", "hide"]),
                  overflow: z.enum(["fit", "scroll"]),
                  pagination: z.enum(["auto", "show", "hide"]),
                  rowLimit: z.number(),
                  totals: z.enum(["auto", "show", "hide"]),
                })
                .partial(),
              medium: z
                .object({
                  columnLabels: z.enum(["auto", "full", "compact"]),
                  density: z.enum(["auto", "comfortable", "compact"]),
                  header: z.enum(["auto", "show", "hide"]),
                  overflow: z.enum(["fit", "scroll"]),
                  pagination: z.enum(["auto", "show", "hide"]),
                  rowLimit: z.number(),
                  totals: z.enum(["auto", "show", "hide"]),
                })
                .partial(),
              small: z
                .object({
                  columnLabels: z.enum(["auto", "full", "compact"]),
                  density: z.enum(["auto", "comfortable", "compact"]),
                  header: z.enum(["auto", "show", "hide"]),
                  overflow: z.enum(["fit", "scroll"]),
                  pagination: z.enum(["auto", "show", "hide"]),
                  rowLimit: z.number(),
                  totals: z.enum(["auto", "show", "hide"]),
                })
                .partial(),
            })
            .partial()
            .optional(),
          dashboardGrid: z
            .object({ h: z.number().int().gte(1).lte(9007199254740991), w: z.number().int().gte(1).lte(12) })
            .optional(),
          dashboardGridBySize: z
            .object({
              full: z.object({ h: z.number().int().gte(1).lte(9007199254740991), w: z.number().int().gte(1).lte(12) }),
              large: z.object({ h: z.number().int().gte(1).lte(9007199254740991), w: z.number().int().gte(1).lte(12) }),
              medium: z.object({
                h: z.number().int().gte(1).lte(9007199254740991),
                w: z.number().int().gte(1).lte(12),
              }),
              small: z.object({ h: z.number().int().gte(1).lte(9007199254740991), w: z.number().int().gte(1).lte(12) }),
            })
            .partial()
            .optional(),
          filterTree: JsonValue.nullish(),
          id: z.string().min(1),
          kind: z.literal("table"),
          name: z.string().min(1),
          preferredSize: z.enum(["small", "medium", "large", "full"]).optional(),
          sort: z
            .array(
              z.object({
                aggregation: z.string().min(1).optional(),
                direction: z.enum(["asc", "desc"]),
                fieldId: z.string().min(1),
              }),
            )
            .optional(),
          visibleFieldRefs: z.array(z.object({ fieldId: z.string(), sourceAlias: z.string() }).partial()).optional(),
        }),
      ]),
    )
    .optional(),
  defaultDashboardVariantId: z.string().min(1).optional(),
  description: z.string().optional(),
  globalFilters: JsonValue.nullish(),
  id: z.string().min(1),
  joins: z
    .array(
      z.object({
        joinKeys: z
          .array(z.object({ leftFieldId: z.string().min(1), rightFieldId: z.string().min(1) }))
          .optional()
          .default([]),
        joinType: z.enum(["inner", "left"]),
        leftSourceAlias: z.string().min(1),
        rightSourceAlias: z.string().min(1),
        timeKey: z.object({ leftFieldId: z.string().min(1), rightFieldId: z.string().min(1) }).optional(),
      }),
    )
    .optional()
    .default([]),
  metricIds: z.array(z.string()).optional().default([]),
  ownerDrivingSchoolIds: z.array(z.string()).optional(),
  pivotConfig: z
    .object({
      columnFields: z.array(z.object({ fieldId: z.string(), sourceAlias: z.string() }).partial()).default([]),
      rowFields: z.array(z.object({ fieldId: z.string(), sourceAlias: z.string() }).partial()).default([]),
      valueFields: z
        .array(
          z.object({
            aggregation: z.enum(["sum", "avg", "count", "countDistinct", "min", "max", "ratio", "custom"]),
            fieldId: z.string().optional(),
            label: z.string().optional(),
            metricId: z.string().min(1).optional(),
            periodRollupStrategy: z
              .enum(["metricDefault", "standard", "sum", "avg", "min", "max", "lastPeriod"])
              .optional(),
            sourceAlias: z.string().optional(),
          }),
        )
        .default([]),
    })
    .partial(),
  previewView: z.enum(["table", "chart"]).optional().default("table"),
  sources: z
    .array(
      z.object({
        alias: z.string().min(1),
        datasourceId: z.string().min(1),
        enabledFieldIds: z.array(z.string()).optional().default([]),
      }),
    )
    .optional()
    .default([]),
  state: z.string().optional().default("Draft"),
  tableConfig: z
    .object({
      columnDateFormats: z
        .record(
          z.object({
            customPattern: z.string().optional(),
            preset: z.enum(["default", "isoDate", "dateDE", "dateUS", "dateTimeDE", "dateTimeUS", "custom"]),
          }),
        )
        .default({}),
      columnNumberFormats: z
        .record(
          z.object({
            currency: z.string().optional().default("EUR"),
            customNegativePattern: z.string().optional(),
            customPositivePattern: z.string().optional(),
            displayUnit: z.enum(["none", "thousands", "millions", "auto"]).optional(),
            fractionDigits: z.number().optional(),
            preset: z.enum(["default", "number", "currency", "percent", "time", "custom"]),
            separators: z.enum(["us", "de"]),
          }),
        )
        .default({}),
      filters: JsonValue.nullable(),
      rowsPerPage: z.number().default(25),
      sort: z
        .array(
          z.object({
            aggregation: z.string().min(1).optional(),
            direction: z.enum(["asc", "desc"]),
            fieldId: z.string().min(1),
          }),
        )
        .default([]),
      visibleFieldRefs: z.array(z.object({ fieldId: z.string(), sourceAlias: z.string() }).partial()).default([]),
    })
    .partial(),
  timeConfig: z.object({
    comparison: z.enum(["off", "previousPeriod", "previousYear"]).optional().default("off"),
    fromDate: z.string().optional(),
    granularity: z.enum(["day", "week", "month", "quarter", "year"]),
    preset: z
      .enum([
        "today",
        "yesterday",
        "thisWeek",
        "thisMonth",
        "thisQuarter",
        "thisYear",
        "last7Days",
        "last30Days",
        "last90Days",
        "weekToDate",
        "monthToDate",
        "quarterToDate",
        "yearToDate",
        "previousWeek",
        "previousMonth",
        "previousQuarter",
        "previousYear",
      ])
      .optional(),
    relativePeriods: z.number().optional().default(12),
    timeDimension: z
      .object({ fieldId: z.string(), sourceAlias: z.string() })
      .partial()
      .optional()
      .default({ fieldId: "", sourceAlias: "" }),
    toDate: z.string().optional(),
  }),
  title: z.string().min(1),
});
const PulseAnalysisListDto = z.object({ items: z.array(PulseAnalysisDefinitionDto) }).passthrough();
const PulseRegistryDeleteResultDto = z.object({ ok: z.boolean() }).passthrough();
const PulseDashboardDefinitionDto = z.object({
  access: z
    .object({
      consume: z
        .object({
          requiredGrants: z
            .array(
              z.enum([
                "PulseAudience:autovioAdmin",
                "PulseAudience:autovioStaff",
                "PulseAudience:school",
                "PulseDashboard:viewDraft",
                "PulseAnalysis:viewDraft",
                "PulseDatasource:viewDraft",
                "PulseMetric:viewDraft",
                "PulseScope:ownSchools",
                "PulseScope:global",
                "PulseDashboard:consume",
                "PulseDashboard:manage",
                "PulseDashboard:embed",
                "PulseAnalysis:consume",
                "PulseAnalysis:manage",
                "PulseDatasource:consume",
                "PulseDatasource:manage",
                "PulseMetric:consume",
                "PulseMetric:manage",
                "PulseAi:consume",
                "PulseAi:manage",
                "PulseApiClient:manage",
                "PulseTheme:manage",
                "PulsePermissions:manage",
              ]),
            )
            .default([]),
        })
        .partial(),
      manage: z
        .object({
          requiredGrants: z
            .array(
              z.enum([
                "PulseAudience:autovioAdmin",
                "PulseAudience:autovioStaff",
                "PulseAudience:school",
                "PulseDashboard:viewDraft",
                "PulseAnalysis:viewDraft",
                "PulseDatasource:viewDraft",
                "PulseMetric:viewDraft",
                "PulseScope:ownSchools",
                "PulseScope:global",
                "PulseDashboard:consume",
                "PulseDashboard:manage",
                "PulseDashboard:embed",
                "PulseAnalysis:consume",
                "PulseAnalysis:manage",
                "PulseDatasource:consume",
                "PulseDatasource:manage",
                "PulseMetric:consume",
                "PulseMetric:manage",
                "PulseAi:consume",
                "PulseAi:manage",
                "PulseApiClient:manage",
                "PulseTheme:manage",
                "PulsePermissions:manage",
              ]),
            )
            .default([]),
        })
        .partial(),
    })
    .partial()
    .optional(),
  copiedAt: z.string().min(1).optional(),
  copiedBy: z.string().min(1).optional(),
  copiedFromVersion: z.number().optional(),
  description: z.string().optional(),
  globalFilters: z
    .array(
      z.object({
        bindings: z
          .array(
            z.object({
              analysisKey: z.string(),
              fieldId: z.string(),
              sourceAlias: z.string(),
              tileId: z.string().optional(),
            }),
          )
          .optional()
          .default([]),
        filterTree: JsonValue.nullish(),
        id: z.string(),
        label: z.string(),
        values: z.array(z.string()).optional().default([]),
      }),
    )
    .optional(),
  id: z
    .string()
    .min(1)
    .regex(/^[a-z0-9][a-z0-9_-]*$/),
  ownerDrivingSchoolIds: z.array(z.string()).optional(),
  sourceTemplateId: z.string().min(1).optional(),
  state: z.string().min(1).optional().default("Draft"),
  tiles: z
    .array(
      z.object({
        analysisKey: z.string().optional(),
        h: z.number(),
        id: z.string(),
        kind: z.enum(["kpi", "chart", "table", "section", "spacer"]),
        renderOverrides: z
          .object({
            chart: z
              .object({
                axisFontSize: z.enum(["auto", "xs", "s", "m", "l"]),
                axisLabels: z.enum(["auto", "show", "hide"]),
                axisNumberDisplay: z.enum(["auto", "full", "compact", "custom"]),
                barGap: z.enum(["auto", "tight", "normal", "wide"]),
                barSize: z.enum(["auto", "slim", "normal", "wide"]),
                barSizing: z.enum(["auto", "relative", "fixed"]),
                density: z.enum(["auto", "comfortable", "compact"]),
                gridLines: z.enum(["auto", "show", "hide"]),
                heatmapAxisDomain: z.enum(["auto", "data", "full"]),
                heatmapCellBorders: z.enum(["auto", "show", "hide", "subtle", "separated"]),
                heatmapColorScale: z.enum(["auto", "standard", "accent", "custom"]),
                heatmapZeroValueDisplay: z.enum(["auto", "scale", "background"]),
                labelPriority: z.array(z.enum(["totals", "series", "axis", "legend"])),
                layout: z.enum(["auto", "tight", "balanced", "roomy"]),
                legend: z.enum(["auto", "show", "hide"]),
                legendFontSize: z.enum(["auto", "xs", "s", "m", "l"]),
                numberDisplay: z.enum(["auto", "full", "compact", "custom"]),
                referenceLineLabelFontSize: z.enum(["auto", "xs", "s", "m", "l"]),
                referenceLineLabelMode: z.enum(["labelAndValue", "valueOnly", "labelOnly"]),
                referenceLineLabels: z.enum(["auto", "show", "hide"]),
                referenceLineLegend: z.enum(["auto", "show", "hide"]),
                referenceLineNumberCustomFormat: z.object({
                  currency: z.string().optional().default("EUR"),
                  customNegativePattern: z.string().optional(),
                  customPositivePattern: z.string().optional(),
                  displayUnit: z.enum(["none", "thousands", "millions", "auto"]).optional(),
                  fractionDigits: z.number().optional(),
                  preset: z.enum(["default", "number", "currency", "percent", "time", "custom"]),
                  separators: z.enum(["us", "de"]),
                }),
                referenceLineNumberDisplay: z.enum(["auto", "full", "compact", "custom"]),
                totalLabelFontSize: z.enum(["auto", "xs", "s", "m", "l"]),
                totalLabelPosition: z.enum(["auto", "top", "inside"]),
                totalLabels: z.enum(["auto", "show", "hide", "required"]),
                totalNumberCustomFormat: z.object({
                  currency: z.string().optional().default("EUR"),
                  customNegativePattern: z.string().optional(),
                  customPositivePattern: z.string().optional(),
                  displayUnit: z.enum(["none", "thousands", "millions", "auto"]).optional(),
                  fractionDigits: z.number().optional(),
                  preset: z.enum(["default", "number", "currency", "percent", "time", "custom"]),
                  separators: z.enum(["us", "de"]),
                }),
                totalNumberDisplay: z.enum(["auto", "full", "compact", "custom"]),
                valueLabelFontSize: z.enum(["auto", "xs", "s", "m", "l"]),
                valueLabels: z.enum(["auto", "show", "hide"]),
                valueNumberCustomFormat: z.object({
                  currency: z.string().optional().default("EUR"),
                  customNegativePattern: z.string().optional(),
                  customPositivePattern: z.string().optional(),
                  displayUnit: z.enum(["none", "thousands", "millions", "auto"]).optional(),
                  fractionDigits: z.number().optional(),
                  preset: z.enum(["default", "number", "currency", "percent", "time", "custom"]),
                  separators: z.enum(["us", "de"]),
                }),
                valueNumberDisplay: z.enum(["auto", "full", "compact", "custom"]),
                xAxisFontSize: z.enum(["auto", "xs", "s", "m", "l"]),
                xAxisLabelCustomFormat: z.object({
                  customPattern: z.string().optional(),
                  preset: z.enum(["default", "isoDate", "dateDE", "dateUS", "dateTimeDE", "dateTimeUS", "custom"]),
                }),
                xAxisLabelFormat: z.enum([
                  "auto",
                  "source",
                  "yearQuarter",
                  "shortYearQuarter",
                  "quarter",
                  "monthShortYear",
                  "monthNumericYear",
                  "custom",
                ]),
                xAxisLabelFrequency: z.enum(["auto", "all", "every2", "every3", "firstLast"]),
                xAxisLabelRotation: z.enum(["auto", "none", "diagonal", "vertical"]),
                xAxisLabels: z.enum(["auto", "show", "hide", "rotate", "sparse"]),
                yAxisFontSize: z.enum(["auto", "xs", "s", "m", "l"]),
                yAxisLabels: z.enum(["auto", "show", "hide"]),
                yAxisNumberCustomFormat: z.object({
                  currency: z.string().optional().default("EUR"),
                  customNegativePattern: z.string().optional(),
                  customPositivePattern: z.string().optional(),
                  displayUnit: z.enum(["none", "thousands", "millions", "auto"]).optional(),
                  fractionDigits: z.number().optional(),
                  preset: z.enum(["default", "number", "currency", "percent", "time", "custom"]),
                  separators: z.enum(["us", "de"]),
                }),
                yAxisNumberDisplay: z.enum(["auto", "full", "compact", "custom"]),
              })
              .partial(),
            kpi: z
              .object({
                comparison: z.enum(["auto", "show", "hide"]),
                comparisonWindow: z.enum(["auto", "alignedToDate", "fullPeriod"]),
                density: z.enum(["auto", "comfortable", "compact"]),
                label: z.enum(["auto", "show", "hide"]),
                periodHint: z.enum(["auto", "show", "hide"]),
                style: z.enum(["standard", "sparkline", "hero", "multi"]),
                subtitle: z.enum(["auto", "show", "hide"]),
                valueNumberCustomFormat: z.object({
                  currency: z.string().optional().default("EUR"),
                  customNegativePattern: z.string().optional(),
                  customPositivePattern: z.string().optional(),
                  displayUnit: z.enum(["none", "thousands", "millions", "auto"]).optional(),
                  fractionDigits: z.number().optional(),
                  preset: z.enum(["default", "number", "currency", "percent", "time", "custom"]),
                  separators: z.enum(["us", "de"]),
                }),
                valueNumberDisplay: z.enum(["auto", "full", "compact", "custom"]),
                valueWindow: z.enum(["currentPeriod", "selectedRange"]),
              })
              .partial(),
            section: z
              .object({
                showUnderline: z.boolean(),
                textAlign: z.enum(["left", "center", "right"]),
                verticalAlign: z.enum(["top", "center", "bottom"]),
              })
              .partial(),
            table: z
              .object({
                columnLabels: z.enum(["auto", "full", "compact"]),
                density: z.enum(["auto", "comfortable", "compact"]),
                header: z.enum(["auto", "show", "hide"]),
                overflow: z.enum(["fit", "scroll"]),
                pagination: z.enum(["auto", "show", "hide"]),
                rowLimit: z.number(),
                totals: z.enum(["auto", "show", "hide"]),
              })
              .partial(),
          })
          .partial()
          .optional(),
        timeOverride: z
          .object({
            comparison: z.enum(["off", "previousPeriod", "previousYear"]),
            fromDate: z.string(),
            granularity: z.enum(["day", "week", "month", "quarter", "year"]),
            preset: z.enum([
              "today",
              "yesterday",
              "thisWeek",
              "thisMonth",
              "thisQuarter",
              "thisYear",
              "last7Days",
              "last30Days",
              "last90Days",
              "weekToDate",
              "monthToDate",
              "quarterToDate",
              "yearToDate",
              "previousWeek",
              "previousMonth",
              "previousQuarter",
              "previousYear",
            ]),
            relativePeriods: z.number(),
            toDate: z.string(),
          })
          .partial()
          .optional(),
        title: z.string().optional(),
        valueRef: z
          .object({
            aggregation: z.enum(["sum", "avg", "count", "countDistinct", "min", "max", "ratio", "custom"]),
            fieldId: z.string().optional(),
            label: z.string().optional(),
            metricId: z.string().min(1).optional(),
            periodRollupStrategy: z
              .enum(["metricDefault", "standard", "sum", "avg", "min", "max", "lastPeriod"])
              .optional(),
            sourceAlias: z.string().optional(),
          })
          .optional(),
        variantId: z.string().optional(),
        variantSize: z.enum(["small", "medium", "large", "full"]).optional(),
        w: z.number(),
        x: z.number(),
        y: z.number(),
      }),
    )
    .optional()
    .default([]),
  title: z.string().min(1),
});
const PulseDashboardListDto = z.object({ items: z.array(PulseDashboardDefinitionDto) }).passthrough();
const PulseDatasourceDatasetsResponseDto = z.object({ datasets: z.array(z.string()) }).passthrough();
const PulseDatasourceFieldUniquesResponseDto = z
  .object({ totalRows: z.number().optional(), values: z.array(z.string()) })
  .passthrough();
const PulseDatasourceDefinitionDto = z.object({
  access: z
    .object({
      consume: z
        .object({
          requiredGrants: z
            .array(
              z.enum([
                "PulseAudience:autovioAdmin",
                "PulseAudience:autovioStaff",
                "PulseAudience:school",
                "PulseDashboard:viewDraft",
                "PulseAnalysis:viewDraft",
                "PulseDatasource:viewDraft",
                "PulseMetric:viewDraft",
                "PulseScope:ownSchools",
                "PulseScope:global",
                "PulseDashboard:consume",
                "PulseDashboard:manage",
                "PulseDashboard:embed",
                "PulseAnalysis:consume",
                "PulseAnalysis:manage",
                "PulseDatasource:consume",
                "PulseDatasource:manage",
                "PulseMetric:consume",
                "PulseMetric:manage",
                "PulseAi:consume",
                "PulseAi:manage",
                "PulseApiClient:manage",
                "PulseTheme:manage",
                "PulsePermissions:manage",
              ]),
            )
            .default([]),
        })
        .partial(),
      manage: z
        .object({
          requiredGrants: z
            .array(
              z.enum([
                "PulseAudience:autovioAdmin",
                "PulseAudience:autovioStaff",
                "PulseAudience:school",
                "PulseDashboard:viewDraft",
                "PulseAnalysis:viewDraft",
                "PulseDatasource:viewDraft",
                "PulseMetric:viewDraft",
                "PulseScope:ownSchools",
                "PulseScope:global",
                "PulseDashboard:consume",
                "PulseDashboard:manage",
                "PulseDashboard:embed",
                "PulseAnalysis:consume",
                "PulseAnalysis:manage",
                "PulseDatasource:consume",
                "PulseDatasource:manage",
                "PulseMetric:consume",
                "PulseMetric:manage",
                "PulseAi:consume",
                "PulseAi:manage",
                "PulseApiClient:manage",
                "PulseTheme:manage",
                "PulsePermissions:manage",
              ]),
            )
            .default([]),
        })
        .partial(),
    })
    .partial()
    .optional(),
  ai: z.object({
    generationNotes: z.array(z.string()),
    lookup: z
      .object({
        aliases: z.array(z.string()),
        businessTerms: z.array(z.string()),
        caveats: z.array(z.string()),
        defaultInterpretationRules: z.array(z.string()),
        fieldAliases: z.record(z.array(z.string())),
        goodFor: z.array(z.string()),
        notGoodFor: z.array(z.string()),
      })
      .partial()
      .optional(),
    prompt: z.string().min(1),
    promptVersion: z.string().min(1),
    reviewState: z.enum(["draft", "reviewed"]),
  }),
  consumptionPolicy: z.enum(["internalOnly", "schoolScopeable", "publicReference"]).optional(),
  description: z.string().min(1),
  id: z.string().min(1),
  name: z.string().min(1),
  outputContract: z.object({
    columnOrder: z.array(z.string()).optional(),
    contractVersion: z.literal(1),
    defaultQueryState: z
      .object({
        filterEnabled: z.boolean(),
        filters: z.object({}).partial().passthrough().nullable(),
        groupByFieldIds: z.array(z.string()).default([]),
        measures: z
          .array(
            z.object({
              aggregation: z.enum(["sum", "avg", "count", "countDistinct", "min", "max", "ratio", "custom"]),
              fieldId: z.string().min(1),
            }),
          )
          .default([]),
        sort: z.array(z.object({ direction: z.enum(["asc", "desc"]), fieldId: z.string().min(1) })).default([]),
      })
      .partial()
      .optional(),
    dimensions: z
      .array(
        z.object({
          capabilities: z
            .array(z.enum(["filterable", "segmentable", "groupable", "aggregatable"]))
            .optional()
            .default([]),
          defaultVisible: z.boolean().optional(),
          definition: z.string().min(1).optional(),
          description: z.string().min(1).optional(),
          fieldId: z.string().min(1),
          format: z.enum(["currency", "percentage", "count", "ratio", "text", "enum", "date", "datetime"]),
          isSegment: z.boolean().optional(),
          label: z.string().min(1),
          numberFormat: z
            .object({
              currency: z.string(),
              customNegativePattern: z.string(),
              customPositivePattern: z.string(),
              displayUnit: z.enum(["none", "thousands", "millions", "auto"]).optional(),
              fractionDigits: z.number(),
              preset: z.enum(["default", "number", "currency", "percent", "time", "custom"]),
              separators: z.enum(["us", "de"]),
            })
            .optional(),
          role: z.enum(["dimension", "measure", "identifier", "time"]),
          type: z.enum(["string", "number", "boolean", "date", "datetime", "timestamp", "json"]),
          valueLabels: z.record(z.string()).optional(),
        }),
      )
      .optional()
      .default([]),
    filters: z
      .array(
        z.object({
          allowedOperators: z
            .array(
              z.enum([
                "equals",
                "notEquals",
                "between",
                "notBetween",
                "known",
                "unknown",
                "moreThan",
                "lessThan",
                "contains",
                "notContains",
                "startsWith",
                "notStartsWith",
                "endsWith",
                "notEndsWith",
                "gt",
                "gte",
                "lt",
                "lte",
                "before",
                "after",
                "thisWeek",
                "lastWeek",
                "nextWeek",
                "thisWeekSoFar",
                "thisMonth",
                "lastMonth",
                "nextMonth",
                "thisMonthSoFar",
                "thisYear",
                "lastYear",
                "nextYear",
                "thisYearSoFar",
                "thisQuarter",
                "lastQuarter",
                "nextQuarter",
                "thisQuarterSoFar",
                "today",
                "yesterday",
                "tomorrow",
                "last7Days",
                "last14Days",
                "last30Days",
                "last60Days",
                "last90Days",
                "last180Days",
                "last365Days",
              ]),
            )
            .optional()
            .default([]),
          defaultOperator: z.enum([
            "equals",
            "notEquals",
            "between",
            "notBetween",
            "known",
            "unknown",
            "moreThan",
            "lessThan",
            "contains",
            "notContains",
            "startsWith",
            "notStartsWith",
            "endsWith",
            "notEndsWith",
            "gt",
            "gte",
            "lt",
            "lte",
            "before",
            "after",
            "thisWeek",
            "lastWeek",
            "nextWeek",
            "thisWeekSoFar",
            "thisMonth",
            "lastMonth",
            "nextMonth",
            "thisMonthSoFar",
            "thisYear",
            "lastYear",
            "nextYear",
            "thisYearSoFar",
            "thisQuarter",
            "lastQuarter",
            "nextQuarter",
            "thisQuarterSoFar",
            "today",
            "yesterday",
            "tomorrow",
            "last7Days",
            "last14Days",
            "last30Days",
            "last60Days",
            "last90Days",
            "last180Days",
            "last365Days",
          ]),
          fieldId: z.string().min(1),
          label: z.string().min(1),
          suggestedValues: z.array(z.string()).optional(),
        }),
      )
      .optional()
      .default([]),
    joinHints: z.array(z.string()).optional().default([]),
    measures: z
      .array(
        z.object({
          allowedAggregations: z
            .array(z.enum(["sum", "avg", "count", "countDistinct", "min", "max", "ratio", "custom"]))
            .optional()
            .default([]),
          defaultAggregation: z.enum(["sum", "avg", "count", "countDistinct", "min", "max", "ratio", "custom"]),
          defaultVisible: z.boolean().optional(),
          definition: z.string().min(1).optional(),
          description: z.string().min(1).optional(),
          distinctFieldId: z.string().min(1).optional(),
          fieldId: z.string().min(1),
          format: z.enum(["currency", "percentage", "count", "ratio", "text", "enum", "date", "datetime"]),
          isMetric: z.boolean().optional(),
          label: z.string().min(1),
          numberFormat: z
            .object({
              currency: z.string(),
              customNegativePattern: z.string(),
              customPositivePattern: z.string(),
              displayUnit: z.enum(["none", "thousands", "millions", "auto"]).optional(),
              fractionDigits: z.number(),
              preset: z.enum(["default", "number", "currency", "percent", "time", "custom"]),
              separators: z.enum(["us", "de"]),
            })
            .optional(),
          rollup: z.enum(["additive", "non_additive", "snapshot", "ratio", "weighted", "custom"]),
          type: z.enum(["string", "number", "boolean", "date", "datetime", "timestamp", "json"]),
        }),
      )
      .optional()
      .default([]),
    primaryDateField: z.string().min(1),
    primaryEntity: z.string().min(1),
    reporting: z
      .object({ recommendedMetricIds: z.array(z.string()).default([]), reportHints: z.array(z.string()).default([]) })
      .partial()
      .optional()
      .default({ recommendedMetricIds: [], reportHints: [] }),
    timeFields: z.array(
      z.object({
        dateFormat: z
          .object({
            customPattern: z.string(),
            preset: z.enum(["default", "isoDate", "dateDE", "dateUS", "dateTimeDE", "dateTimeUS", "custom"]),
          })
          .optional(),
        defaultVisible: z.boolean().optional().default(true),
        definition: z.string().min(1).optional(),
        fieldId: z.string().min(1),
        granularities: z
          .array(z.enum(["day", "week", "month", "quarter", "year"]))
          .optional()
          .default([]),
        isPrimary: z.boolean().optional(),
        label: z.string().min(1),
        type: z.enum(["date", "datetime", "timestamp"]),
      }),
    ),
  }),
  schema: z
    .array(
      z.object({
        description: z.string().min(1).optional(),
        id: z.string().min(1),
        nullable: z.boolean(),
        sourceColumn: z.string().min(1),
        type: z.enum(["string", "number", "boolean", "date", "datetime", "timestamp", "json"]),
      }),
    )
    .min(1),
  source: z.object({
    dataset: z.string().min(1),
    kind: z.enum(["bigquery_view", "bigquery_table"]),
    object: z.string().min(1),
    owner: z.string().min(1),
    project: z.string().optional(),
    refreshCadence: z.enum(["daily", "hourly", "weekly", "manual"]),
    sqlPath: z.string().min(1),
  }),
  status: z.enum(["draft", "reviewed"]),
});
const PulseDatasourceFieldUniquesBodyDto = z.object({
  datasource: PulseDatasourceDefinitionDto,
  fieldId: z.string().min(1),
  limit: z.number().int().gt(0).lte(9007199254740991).optional(),
  search: z.string().optional(),
});
const PulseDatasourceIntrospectSchemaResponseDto = z
  .object({
    fields: z.array(
      z.object({
        description: z.string().optional(),
        id: z.string(),
        nullable: z.boolean(),
        sourceColumn: z.string(),
        type: z.enum(["string", "number", "boolean", "date", "datetime", "timestamp", "json"]),
      }),
    ),
    source: z.object({
      dataset: z.string(),
      kind: z.enum(["bigquery_table", "bigquery_view"]),
      object: z.string(),
      project: z.string(),
    }),
  })
  .passthrough();
const PulseDatasourcePreviewResponseDto = z
  .object({
    columnLabels: z.record(z.string()),
    columns: z.array(z.string()),
    datasourceId: z.string(),
    debugSql: z.object({ countQuery: z.string(), params: z.record(z.string()), previewQuery: z.string() }).optional(),
    limit: z.number(),
    mode: z.enum(["raw", "aggregated"]),
    page: z.number(),
    rows: z.array(z.record(JsonValue)),
    totalRows: z.number(),
  })
  .passthrough();
const PulseDatasourcePreviewBodyDto = z.object({
  cacheMode: z.enum(["cache", "reload"]).optional(),
  columns: z.array(z.string()).optional(),
  datasource: PulseDatasourceDefinitionDto,
  dimensions: z.array(z.string()).optional(),
  filters: JsonValue.nullish(),
  limit: z.number().int().gt(0).lte(9007199254740991).optional(),
  measures: z
    .array(
      z.object({
        aggregation: z.string().min(1),
        fieldId: z.string().min(1),
        metricId: z.string().min(1).optional(),
        periodRollupStrategy: z.string().min(1).optional(),
      }),
    )
    .optional(),
  page: z.number().int().gt(0).lte(9007199254740991).optional(),
  sort: z
    .array(
      z.object({
        aggregation: z.string().min(1).optional(),
        direction: z.enum(["asc", "desc"]),
        fieldId: z.string().min(1),
      }),
    )
    .optional(),
});
const PulseDatasourceProjectsResponseDto = z.object({ projects: z.array(z.string()) }).passthrough();
const PulseDatasourceSourceObjectsResponseDto = z
  .object({ objects: z.array(z.object({ kind: z.enum(["bigquery_table", "bigquery_view"]), name: z.string() })) })
  .passthrough();
const PulseDatasourceListDto = z.object({ items: z.array(PulseDatasourceDefinitionDto) }).passthrough();
const PulseGrantConfigDeleteDto = z.object({
  id: z.string().min(1),
  section: z.enum(["group", "roleMapping", "schoolOverride", "userOverride"]),
});
const PulseGrantDefinitionDto = z.object({
  category: z.enum(["audience", "scope", "dashboard", "analysis", "datasource", "metric", "ai", "admin", "other"]),
  description: z.string(),
  key: z.enum([
    "PulseAudience:autovioAdmin",
    "PulseAudience:autovioStaff",
    "PulseAudience:school",
    "PulseDashboard:viewDraft",
    "PulseAnalysis:viewDraft",
    "PulseDatasource:viewDraft",
    "PulseMetric:viewDraft",
    "PulseScope:ownSchools",
    "PulseScope:global",
    "PulseDashboard:consume",
    "PulseDashboard:manage",
    "PulseDashboard:embed",
    "PulseAnalysis:consume",
    "PulseAnalysis:manage",
    "PulseDatasource:consume",
    "PulseDatasource:manage",
    "PulseMetric:consume",
    "PulseMetric:manage",
    "PulseAi:consume",
    "PulseAi:manage",
    "PulseApiClient:manage",
    "PulseTheme:manage",
    "PulsePermissions:manage",
  ]),
  label: z.string(),
});
const PulseGrantGroupDefinitionDto = z.object({
  description: z.string(),
  grants: z.array(
    z.enum([
      "PulseAudience:autovioAdmin",
      "PulseAudience:autovioStaff",
      "PulseAudience:school",
      "PulseDashboard:viewDraft",
      "PulseAnalysis:viewDraft",
      "PulseDatasource:viewDraft",
      "PulseMetric:viewDraft",
      "PulseScope:ownSchools",
      "PulseScope:global",
      "PulseDashboard:consume",
      "PulseDashboard:manage",
      "PulseDashboard:embed",
      "PulseAnalysis:consume",
      "PulseAnalysis:manage",
      "PulseDatasource:consume",
      "PulseDatasource:manage",
      "PulseMetric:consume",
      "PulseMetric:manage",
      "PulseAi:consume",
      "PulseAi:manage",
      "PulseApiClient:manage",
      "PulseTheme:manage",
      "PulsePermissions:manage",
    ]),
  ),
  key: z.string().min(1),
  label: z.string(),
});
const PulseGrantRoleMappingDefinitionDto = z.object({
  claim: z.enum(["role", "grant", "schoolScope"]),
  groupKeys: z.array(z.string()),
  id: z.string().min(1),
  value: z.string().min(1),
});
const PulseGrantOverrideDefinitionDto = z.object({
  addDrivingSchoolIds: z.array(z.string()),
  addGrants: z.array(
    z.enum([
      "PulseAudience:autovioAdmin",
      "PulseAudience:autovioStaff",
      "PulseAudience:school",
      "PulseDashboard:viewDraft",
      "PulseAnalysis:viewDraft",
      "PulseDatasource:viewDraft",
      "PulseMetric:viewDraft",
      "PulseScope:ownSchools",
      "PulseScope:global",
      "PulseDashboard:consume",
      "PulseDashboard:manage",
      "PulseDashboard:embed",
      "PulseAnalysis:consume",
      "PulseAnalysis:manage",
      "PulseDatasource:consume",
      "PulseDatasource:manage",
      "PulseMetric:consume",
      "PulseMetric:manage",
      "PulseAi:consume",
      "PulseAi:manage",
      "PulseApiClient:manage",
      "PulseTheme:manage",
      "PulsePermissions:manage",
    ]),
  ),
  autovioUserId: z.string().optional(),
  grantGlobalScope: z.boolean(),
  label: z.string().optional(),
  removeGrants: z.array(
    z.enum([
      "PulseAudience:autovioAdmin",
      "PulseAudience:autovioStaff",
      "PulseAudience:school",
      "PulseDashboard:viewDraft",
      "PulseAnalysis:viewDraft",
      "PulseDatasource:viewDraft",
      "PulseMetric:viewDraft",
      "PulseScope:ownSchools",
      "PulseScope:global",
      "PulseDashboard:consume",
      "PulseDashboard:manage",
      "PulseDashboard:embed",
      "PulseAnalysis:consume",
      "PulseAnalysis:manage",
      "PulseDatasource:consume",
      "PulseDatasource:manage",
      "PulseMetric:consume",
      "PulseMetric:manage",
      "PulseAi:consume",
      "PulseAi:manage",
      "PulseApiClient:manage",
      "PulseTheme:manage",
      "PulsePermissions:manage",
    ]),
  ),
  subjectId: z.string().min(1),
});
const PulseGrantConfigurationDto = z.object({
  grants: z.array(PulseGrantDefinitionDto),
  groups: z.array(PulseGrantGroupDefinitionDto),
  roleMappings: z.array(PulseGrantRoleMappingDefinitionDto),
  schoolOverrides: z.array(PulseGrantOverrideDefinitionDto),
  userOverrides: z.array(PulseGrantOverrideDefinitionDto),
});
const PulseGrantGroupConfigPutDto = z.object({ item: PulseGrantGroupDefinitionDto, section: z.literal("group") });
const PulseGrantRoleMappingConfigPutDto = z.object({
  item: PulseGrantRoleMappingDefinitionDto,
  section: z.literal("roleMapping"),
});
const PulseGrantSchoolOverrideConfigPutDto = z.object({
  item: PulseGrantOverrideDefinitionDto,
  section: z.literal("schoolOverride"),
});
const PulseGrantUserOverrideConfigPutDto = z.object({
  item: PulseGrantOverrideDefinitionDto,
  section: z.literal("userOverride"),
});
const upsertPulseGrantConfigurationItem_Body = z.union([
  PulseGrantGroupConfigPutDto,
  PulseGrantRoleMappingConfigPutDto,
  PulseGrantSchoolOverrideConfigPutDto,
  PulseGrantUserOverrideConfigPutDto,
]);
const PulseMetricDefinitionDto = z.object({
  constraints: z.object({
    compatibleDimensions: z.array(z.string()),
    nullHandling: z.enum(["null", "zero"]),
    requiredDimensions: z.array(z.string()),
    zeroDivisionHandling: z.enum(["null", "zero"]),
  }),
  description: z.string().min(1),
  entityKey: z.string().min(1).optional(),
  filters: z.array(z.object({ description: z.string().min(1).optional(), label: z.string().min(1) })).optional(),
  format: z.object({
    decimals: z.number(),
    kind: z.enum(["currency", "percentage", "count", "ratio", "decimal"]),
    unit: z.string().min(1).optional(),
  }),
  formula: z.string().min(1).optional(),
  formulaText: z.string().min(1).optional(),
  id: z.string().min(1),
  kind: z.enum(["base", "calculated"]),
  materialization: z.object({
    readiness: z.enum(["definition_only", "ready_for_bigquery_scheduling"]),
    refreshCadence: z.string().min(1),
    targetDataset: z.string().min(1),
    targetTable: z.string().min(1),
  }),
  name: z.string().min(1),
  note: z.string().min(1).optional(),
  originNote: z.string().min(1).optional(),
  periodRollupStrategy: z.enum(["standard", "sum", "avg", "min", "max", "lastPeriod"]).optional(),
  rollupReviewNote: z.string().min(1).optional(),
  segmentRollup: z.string().min(1).optional(),
  source: z.object({ datasourceId: z.string().min(1), fieldId: z.string().min(1) }).optional(),
  status: z.enum(["draft", "conflict", "published"]),
  time: z.object({
    dateField: z.string().min(1),
    grain: z.enum(["daily", "weekly", "monthly"]),
    supportedRollups: z.array(z.enum(["daily", "weekly", "monthly"])),
  }),
  timeRollup: z.string().min(1).optional(),
});
const PulseMetricListDto = z.object({ items: z.array(PulseMetricDefinitionDto) }).passthrough();
const PulseThemeDefinitionDto = z.object({
  colors: z
    .object({
      accent: z.string().min(1).default("#74a089"),
      accentOn: z.string().min(1).default("#ffffff"),
      accentSoft: z.string().min(1).default("#e3ece7"),
      accentStrong: z.string().min(1).default("#315340"),
      active: z.string().min(1).default("rgba(17,19,14,0.055)"),
      app: z.string().min(1).default("#f8f8f8"),
      borderDefault: z.string().min(1).default("rgba(17,19,14,0.06)"),
      borderStrong: z.string().min(1).default("rgba(17,19,14,0.12)"),
      borderSubtle: z.string().min(1).default("transparent"),
      chartPalette: z
        .array(z.string())
        .min(1)
        .default([
          "#B23F3F",
          "#D55B5B",
          "#E87B7B",
          "#F5DCDC",
          "#FAE5E5",
          "#F39557",
          "#FDEFE6",
          "#F6CA00",
          "#2E6A4D",
          "#315340",
          "#4F8F6E",
          "#5D806E",
          "#74A089",
          "#B9CFC4",
          "#DCEAE1",
          "#E3ECE7",
          "#ECF1ED",
          "#007AFF",
          "#D9EBFF",
          "#D483B7",
          "#11130E",
          "#4A4D45",
          "#888888",
          "#8A8E83",
          "#D8D8D8",
          "#E6E8E1",
          "#F6F7F3",
          "#FFFFFF",
        ]),
      dashboardBackground: z.string().min(1).default("#f8f8f8"),
      gridLine: z.string().min(1).default("rgba(17,19,14,0.05)"),
      hover: z.string().min(1).default("rgba(17,19,14,0.035)"),
      pillNegativeBg: z.string().min(1).default("#f5dcdc"),
      pillNegativeFg: z.string().min(1).default("#b23f3f"),
      pillNeutralBg: z.string().min(1).default("#e6e8e1"),
      pillNeutralFg: z.string().min(1).default("#4a4d45"),
      pillPositiveBg: z.string().min(1).default("#dceae1"),
      pillPositiveFg: z.string().min(1).default("#2e6a4d"),
      sectionBackground: z.string().min(1).default("#f8f8f8"),
      sectionBorder: z.string().min(1).default("transparent"),
      sectionRule: z.string().min(1).default("rgba(17,19,14,0.12)"),
      sectionText: z.string().min(1).default("#11130e"),
      statusInfo: z.string().min(1).default("#007aff"),
      statusNegative: z.string().min(1).default("#d55b5b"),
      statusPositive: z.string().min(1).default("#4f8f6e"),
      statusWarning: z.string().min(1).default("#f39557"),
      surface: z.string().min(1).default("#ffffff"),
      surface2: z.string().min(1).default("#f6f7f3"),
      textInverse: z.string().min(1).default("#ffffff"),
      textPrimary: z.string().min(1).default("#11130e"),
      textSecondary: z.string().min(1).default("#4a4d45"),
      textTertiary: z.string().min(1).default("#8a8e83"),
      tileHeaderBackground: z.string().min(1).default("#ffffff"),
      tileHeaderBorder: z.string().min(1).default("rgba(17,19,14,0.06)"),
      tileHeaderText: z.string().min(1).default("#11130e"),
      tinted: z.string().min(1).default("#ecf1ed"),
      tooltipBg: z.string().min(1).default("#11130e"),
      tooltipFg: z.string().min(1).default("#ffffff"),
    })
    .partial()
    .optional()
    .default({
      accent: "#74a089",
      accentOn: "#ffffff",
      accentSoft: "#e3ece7",
      accentStrong: "#315340",
      active: "rgba(17,19,14,0.055)",
      app: "#f8f8f8",
      borderDefault: "rgba(17,19,14,0.06)",
      borderStrong: "rgba(17,19,14,0.12)",
      borderSubtle: "transparent",
      chartPalette: [
        "#B23F3F",
        "#D55B5B",
        "#E87B7B",
        "#F5DCDC",
        "#FAE5E5",
        "#F39557",
        "#FDEFE6",
        "#F6CA00",
        "#2E6A4D",
        "#315340",
        "#4F8F6E",
        "#5D806E",
        "#74A089",
        "#B9CFC4",
        "#DCEAE1",
        "#E3ECE7",
        "#ECF1ED",
        "#007AFF",
        "#D9EBFF",
        "#D483B7",
        "#11130E",
        "#4A4D45",
        "#888888",
        "#8A8E83",
        "#D8D8D8",
        "#E6E8E1",
        "#F6F7F3",
        "#FFFFFF",
      ],
      dashboardBackground: "#f8f8f8",
      gridLine: "rgba(17,19,14,0.05)",
      hover: "rgba(17,19,14,0.035)",
      pillNegativeBg: "#f5dcdc",
      pillNegativeFg: "#b23f3f",
      pillNeutralBg: "#e6e8e1",
      pillNeutralFg: "#4a4d45",
      pillPositiveBg: "#dceae1",
      pillPositiveFg: "#2e6a4d",
      sectionBackground: "#f8f8f8",
      sectionBorder: "transparent",
      sectionRule: "rgba(17,19,14,0.12)",
      sectionText: "#11130e",
      statusInfo: "#007aff",
      statusNegative: "#d55b5b",
      statusPositive: "#4f8f6e",
      statusWarning: "#f39557",
      surface: "#ffffff",
      surface2: "#f6f7f3",
      textInverse: "#ffffff",
      textPrimary: "#11130e",
      textSecondary: "#4a4d45",
      textTertiary: "#8a8e83",
      tileHeaderBackground: "#ffffff",
      tileHeaderBorder: "rgba(17,19,14,0.06)",
      tileHeaderText: "#11130e",
      tinted: "#ecf1ed",
      tooltipBg: "#11130e",
      tooltipFg: "#ffffff",
    }),
  description: z.string().min(1).optional(),
  id: z
    .string()
    .min(1)
    .regex(/^[a-z0-9][a-z0-9_-]*$/),
  name: z.string().min(1).optional().default("AUTOVIO Pulse Light"),
  scope: z
    .union([
      z.object({ kind: z.literal("default") }),
      z.object({ kind: z.literal("tenant"), tenantId: z.string().min(1) }),
    ])
    .optional()
    .default({ kind: "default" }),
  typography: z
    .object({
      axisLabelSize: z.number().default(11),
      bodyLineHeight: z.number().default(1.5),
      bodySize: z.number().default(14),
      dashboardTitleSize: z.number().default(24),
      dashboardTitleWeight: z.number().default(700),
      fontFamily: z
        .string()
        .min(1)
        .default('"Poppins", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif'),
      heroKpiLabelSize: z.number().default(12),
      heroKpiLabelWeight: z.number().default(500),
      heroKpiValueSize: z.number().default(44),
      heroKpiValueWeight: z.number().default(700),
      kpiLabelSize: z.number().default(12),
      kpiLabelWeight: z.number().default(500),
      kpiValueSize: z.number().default(36),
      kpiValueWeight: z.number().default(700),
      legendSize: z.number().default(11),
      monoFontFamily: z.string().min(1).default('"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace'),
      oneLineKpiLabelLineHeight: z.number().default(1.3),
      oneLineKpiLabelSize: z.number().default(12),
      oneLineKpiLabelWeight: z.number().default(500),
      oneLineKpiValueLineHeight: z.number().default(1.18),
      oneLineKpiValueSize: z.number().default(32),
      oneLineKpiValueWeight: z.number().default(700),
      sectionTitleSize: z.number().default(20),
      sectionTitleWeight: z.number().default(800),
      tileTitleSize: z.number().default(13),
      tileTitleWeight: z.number().default(600),
      tooltipSize: z.number().default(12),
    })
    .partial()
    .optional()
    .default({
      axisLabelSize: 11,
      bodyLineHeight: 1.5,
      bodySize: 14,
      dashboardTitleSize: 24,
      dashboardTitleWeight: 700,
      fontFamily: '"Poppins", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
      heroKpiLabelSize: 12,
      heroKpiLabelWeight: 500,
      heroKpiValueSize: 44,
      heroKpiValueWeight: 700,
      kpiLabelSize: 12,
      kpiLabelWeight: 500,
      kpiValueSize: 36,
      kpiValueWeight: 700,
      legendSize: 11,
      monoFontFamily: '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
      oneLineKpiLabelLineHeight: 1.3,
      oneLineKpiLabelSize: 12,
      oneLineKpiLabelWeight: 500,
      oneLineKpiValueLineHeight: 1.18,
      oneLineKpiValueSize: 32,
      oneLineKpiValueWeight: 700,
      sectionTitleSize: 20,
      sectionTitleWeight: 800,
      tileTitleSize: 13,
      tileTitleWeight: 600,
      tooltipSize: 12,
    }),
});
const PulseThemeListDto = z.object({ items: z.array(PulseThemeDefinitionDto) }).passthrough();
const DrivingLessonTypeEnum = z.enum([
  "normal",
  "ueberlandfahrt",
  "autobahnfahrt",
  "nachtfahrt",
  "simulator",
  "praktischePruefung",
  "schaltkompetenz",
  "theoretischePruefung",
  "fahrprobe",
]);
const PriceDto = z
  .object({
    authorityFees: z
      .object({ gross: z.number().int(), net: z.number().int(), vat: z.number().int() })
      .passthrough()
      .optional(),
    instructorPrice: z
      .object({ fee: z.number().int(), gross: z.number().int(), net: z.number().int(), vat: z.number().int() })
      .passthrough(),
    quantity: z.number().gte(0).optional(),
    studentPrice: z.object({ gross: z.number().int(), net: z.number().int(), vat: z.number().int() }).passthrough(),
    studentUnitPrice: z
      .object({ gross: z.number().int(), net: z.number().int(), vat: z.number().int() })
      .passthrough()
      .optional(),
  })
  .passthrough();
const RecommendationDto = z
  .object({
    bookedTrainingId: z.string().min(1),
    drivingLessonType: DrivingLessonTypeEnum,
    end: z.string().datetime({ offset: true }),
    instructorUid: z.string().min(1).optional(),
    price: PriceDto,
    resourceUids: z.array(z.string()),
    start: z.string().datetime({ offset: true }),
    studentUid: z.string().min(1),
  })
  .passthrough();
const ReferralCodeApplicationDto = z
  .object({
    appliedAt: z.string().datetime({ offset: true }),
    appliedBy: z.object({ id: z.string(), name: z.string() }).passthrough(),
  })
  .passthrough();
const ReferralCodeUsageDto = z
  .object({
    appliedAt: z.string().datetime({ offset: true }).nullish(),
    appliedBy: z.object({ firstName: z.string(), lastName: z.string(), studentId: z.string() }).passthrough(),
    createdAt: z.string().datetime({ offset: true }),
    id: z.string().uuid(),
    isNew: z.boolean(),
    referralCode: z.string(),
    rewardAmount: z.unknown().nullish(),
    rewardCurrency: z.string().nullish(),
    rewardDescription: z.string().nullish(),
    state: z.enum(["RESERVED", "PENDING", "APPLIED", "CANCELED"]),
    updatedAt: z.string().datetime({ offset: true }),
    userId: z.string().uuid().nullish(),
  })
  .passthrough();
const ClaimRewardRequestBody = z
  .object({ referralCodeUsageId: z.string().min(1), utid: z.string().min(1).optional(), value: MoneyDto })
  .passthrough();
const ClaimRewardResponseBody = z
  .object({ amount: z.string(), currency: z.literal("EUR"), description: z.string() })
  .passthrough();
const CreateGiftVoucherRequestBody = z
  .object({
    beneficiary: z.string().min(1),
    description: z.string().min(1),
    id: z.string().min(1),
    note: z.string().optional(),
    value: MoneyDto,
  })
  .passthrough();
const GiftVoucherDto = z
  .object({
    amount: z.string(),
    beneficiaryId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
    createdById: z.string().uuid(),
    currency: z.literal("EUR"),
    description: z.string(),
    id: z.string().uuid(),
  })
  .passthrough();
const DrivingSchoolSearchResultDto = z
  .object({
    endDate: z.string().datetime({ offset: true }).nullish(),
    id: z.string(),
    name: z.string(),
    relatedSearchFieldUiString: z.string().optional(),
    relatedSearchResultUiString: z.string().optional(),
    uiString: z.string(),
  })
  .passthrough();
const GuardianSearchResultDto = z
  .object({
    email: z.string(),
    firstName: z.string(),
    id: z.string().uuid(),
    lastName: z.string(),
    phone: z.string(),
    relatedSearchFieldUiString: z.string().optional(),
    relatedSearchResultUiString: z.string().optional(),
    studentDrivingSchoolId: z.string(),
    studentId: z.string(),
    uiString: z.string(),
  })
  .passthrough();
const InstructorSearchResultDto = z
  .object({
    drivingSchoolId: z.string(),
    endDate: z.string().datetime({ offset: true }),
    id: z.string(),
    relatedSearchFieldUiString: z.string().optional(),
    relatedSearchResultUiString: z.string().optional(),
    uiString: z.string(),
    user: z
      .object({ firstName: z.string(), id: z.string().uuid(), lastName: z.string(), phoneNumber: z.string().nullish() })
      .passthrough(),
  })
  .passthrough();
const StudentSearchResultDto = z
  .object({
    drivingSchoolId: z.string(),
    id: z.string(),
    referralCode: z.string().nullish(),
    relatedSearchFieldUiString: z.string().optional(),
    relatedSearchResultUiString: z.string().optional(),
    uiString: z.string(),
    user: z
      .object({ firstName: z.string(), id: z.string().uuid(), lastName: z.string(), phoneNumber: z.string().nullish() })
      .passthrough(),
  })
  .passthrough();
const SearchResponseDto = z
  .object({
    drivingSchools: z.array(DrivingSchoolSearchResultDto),
    guardians: z.array(GuardianSearchResultDto),
    instructors: z.array(InstructorSearchResultDto),
    students: z.array(StudentSearchResultDto),
  })
  .partial()
  .passthrough();
const ListServiceEntryDto = z.array(
  z
    .object({
      bookedTrainingId: z.string().uuid().nullish(),
      branchId: z.string().uuid().nullish(),
      calendarEventId: z.string().uuid().nullish(),
      costBearerId: z.string().uuid().nullish(),
      createdAt: z.string().datetime({ offset: true }),
      createdById: z.string().uuid(),
      currency: z.string(),
      description: z.string(),
      drivingLicenseClass: z
        .enum([
          "A",
          "A1",
          "A2",
          "A80",
          "AM",
          "B",
          "B196",
          "B197",
          "B78",
          "B96",
          "BE",
          "C",
          "C1",
          "C1E",
          "CE",
          "D",
          "D1",
          "D1E",
          "DE",
          "L",
          "M",
          "T",
        ])
        .nullish(),
      drivingSchoolId: z.string(),
      id: z.string().uuid(),
      instructorId: z.string().nullish(),
      issuerType: z.enum(["DRIVING_SCHOOL", "AUTOVIO", "STUDENT", "COST_BEARER"]),
      metadata: z.object({}).partial().passthrough().nullish(),
      quantity: z.union([z.number(), z.string()]).optional(),
      recipientType: z.enum(["DRIVING_SCHOOL", "AUTOVIO", "STUDENT", "COST_BEARER"]),
      serviceDate: z.string().datetime({ offset: true }),
      serviceTypeId: z.string().uuid().nullish(),
      status: z.enum(["PENDING", "INVOICED", "CANCELED"]),
      studentId: z.string().nullish(),
      totalGross: z.union([z.number(), z.string()]),
      totalNet: z.union([z.number(), z.string()]),
      unitPriceGross: z.union([z.number(), z.string()]),
      unitPriceNet: z.union([z.number(), z.string()]),
      updatedAt: z.string().datetime({ offset: true }),
      updatedById: z.string().uuid(),
      vatAmount: z.union([z.number(), z.string()]),
      vatRate: z.union([z.number(), z.string()]),
    })
    .passthrough(),
);
const CreateServiceEntryDto = z
  .object({
    bookedTrainingId: z.string().uuid().nullish(),
    branchId: z.string().uuid().nullish(),
    calendarEventId: z.string().uuid().nullish(),
    costBearerId: z.string().uuid().nullish(),
    currency: z.string(),
    description: z.string(),
    drivingLicenseClass: DrivingLicenseClassEnum.nullish(),
    drivingSchoolId: z.string(),
    instructorId: z.string().nullish(),
    issuerType: z.enum(["DRIVING_SCHOOL", "AUTOVIO", "STUDENT", "COST_BEARER"]),
    metadata: z.object({}).partial().passthrough().nullish(),
    quantity: z.union([z.number(), z.string()]).optional(),
    recipientType: z.enum(["DRIVING_SCHOOL", "AUTOVIO", "STUDENT", "COST_BEARER"]),
    serviceDate: z.string().datetime({ offset: true }),
    serviceTypeId: z.string().uuid().nullish(),
    studentId: z.string().nullish(),
    unitPriceGross: z.union([z.number(), z.string()]),
    unitPriceNet: z.union([z.number(), z.string()]),
    vatRate: z.union([z.number(), z.string()]),
  })
  .passthrough();
const ServiceEntryDto = z
  .object({
    bookedTrainingId: z.string().uuid().nullish(),
    branchId: z.string().uuid().nullish(),
    calendarEventId: z.string().uuid().nullish(),
    costBearerId: z.string().uuid().nullish(),
    createdAt: z.string().datetime({ offset: true }),
    createdById: z.string().uuid(),
    currency: z.string(),
    description: z.string(),
    drivingLicenseClass: DrivingLicenseClassEnum.nullish(),
    drivingSchoolId: z.string(),
    id: z.string().uuid(),
    instructorId: z.string().nullish(),
    issuerType: z.enum(["DRIVING_SCHOOL", "AUTOVIO", "STUDENT", "COST_BEARER"]),
    metadata: z.object({}).partial().passthrough().nullish(),
    quantity: z.union([z.number(), z.string()]).optional(),
    recipientType: z.enum(["DRIVING_SCHOOL", "AUTOVIO", "STUDENT", "COST_BEARER"]),
    serviceDate: z.string().datetime({ offset: true }),
    serviceTypeId: z.string().uuid().nullish(),
    status: z.enum(["PENDING", "INVOICED", "CANCELED"]),
    studentId: z.string().nullish(),
    totalGross: z.union([z.number(), z.string()]),
    totalNet: z.union([z.number(), z.string()]),
    unitPriceGross: z.union([z.number(), z.string()]),
    unitPriceNet: z.union([z.number(), z.string()]),
    updatedAt: z.string().datetime({ offset: true }),
    updatedById: z.string().uuid(),
    vatAmount: z.union([z.number(), z.string()]),
    vatRate: z.union([z.number(), z.string()]),
  })
  .passthrough();
const UpdateServiceEntryDto = z
  .object({
    bookedTrainingId: z.string().uuid().nullable(),
    branchId: z.string().uuid().nullable(),
    costBearerId: z.string().uuid().nullable(),
    description: z.string(),
    instructorId: z.string().nullable(),
    metadata: z.object({}).partial().passthrough().nullable(),
    quantity: z.union([z.number(), z.string()]),
    serviceDate: z.string().datetime({ offset: true }),
    serviceTypeId: z.string().uuid().nullable(),
    unitPriceGross: z.union([z.number(), z.string()]),
    unitPriceNet: z.union([z.number(), z.string()]),
    vatRate: z.union([z.number(), z.string()]),
  })
  .partial();
const CreateServiceTypeDto = z
  .object({
    category: z
      .enum(["DRIVING_LESSON", "THEORY_LESSON", "AUTHORITY_FEE", "BASE_FEE", "TEACHING_MATERIAL", "EXAM_FEE", "OTHER"])
      .nullish(),
    drivingSchoolId: z.string(),
    price: z.union([z.number(), z.string()]).nullish(),
    title: z.string(),
    vatRate: z.union([z.number(), z.string()]),
  })
  .passthrough();
const UpdateServiceTypeDto = z
  .object({
    category: z
      .enum(["DRIVING_LESSON", "THEORY_LESSON", "AUTHORITY_FEE", "BASE_FEE", "TEACHING_MATERIAL", "EXAM_FEE", "OTHER"])
      .nullable(),
    price: z.union([z.number(), z.string()]).nullable(),
    title: z.string(),
    vatRate: z.union([z.number(), z.string()]),
  })
  .partial();
const SignUpPaymentSelectionEnum = z.enum([
  "financing",
  "pay_as_you_drive",
  "invoice_payment",
  "prepaid",
  "prepaid_bank_transfer",
  "prepaid_card",
  "prepaid_sepa_debit",
  "datapart_sepa_debit",
  "external_bank_transfer",
]);
const SignUpDataUpdateDto = z
  .object({
    formData: z.record(z.object({}).partial().passthrough()).optional().default({}),
    id: z.string(),
    paymentConfiguration: z
      .object({
        allowedPaymentSelections: z.array(SignUpPaymentSelectionEnum),
        selectedNumberLessons: z.record(z.number()),
        selectedPaymentSelection: SignUpPaymentSelectionEnum,
      })
      .partial()
      .passthrough()
      .optional(),
  })
  .passthrough();
const SignUpData = z
  .object({
    createdAt: z.unknown(),
    createdBy: z.string().uuid().optional(),
    formData: z.record(z.object({}).partial().passthrough()).optional().default({}),
    id: z.string(),
    paymentConfiguration: z
      .object({
        allowedPaymentSelections: z.array(SignUpPaymentSelectionEnum),
        selectedNumberLessons: z.record(z.number()),
        selectedPaymentSelection: SignUpPaymentSelectionEnum,
      })
      .partial()
      .passthrough()
      .optional(),
    updatedAt: z.unknown(),
    updatedBy: z.string().uuid().optional(),
    writeId: z.string().uuid(),
  })
  .passthrough();
const SalutationEnum = z.enum(["Herr", "Frau", "Divers", "MoechteIchNichtSagen"]);
const StudentDto = z
  .object({
    archived: z.boolean(),
    billingProvider: z.enum(["datapart", "autovio", "vogel_export"]),
    createdAt: z.string().datetime({ offset: true }),
    dateOfRegistration: z.string().datetime({ offset: true }).nullish(),
    degenerId: z.string().nullish(),
    drivingSchoolId: z.string(),
    dunningPostponedUntil: z.string().datetime({ offset: true }).nullish(),
    fahrschulcockpitId: z.number().int().gte(-9007199254740991).lte(9007199254740991).nullish(),
    fahrschuleLiveId: z.number().int().gte(-9007199254740991).lte(9007199254740991).nullish(),
    guardian: z
      .object({
        createdAt: z.string().datetime({ offset: true }),
        email: z.string(),
        firstName: z.string(),
        id: z.string().uuid(),
        lastName: z.string(),
        phone: z.string(),
        updatedAt: z.string().datetime({ offset: true }),
      })
      .passthrough()
      .nullish(),
    guardianId: z.string().uuid().nullish(),
    hubspotContactId: z.string().nullish(),
    id: z.string(),
    nationality: z.string(),
    needsGlasses: z.boolean().nullish(),
    previousDrivingLicenseClasses: z.array(DrivingLicenseClassEnum),
    referralCode: z.string().nullish(),
    salutation: SalutationEnum.nullish(),
    subscriptionPackageId: z.string().uuid().nullish(),
    subscriptionPackageName: z.string().nullish(),
    subscriptionPackagePrice: MoneyDto.nullish(),
    theoryExamLanguage: z.string(),
    updatedAt: z.string().datetime({ offset: true }),
    user: z
      .object({
        birthName: z.string().nullish(),
        createdAt: z.string().datetime({ offset: true }),
        email: z.string().nullish(),
        firstName: z.string(),
        gender: GenderEnum.nullish(),
        id: z.string().uuid(),
        lastName: z.string(),
        phoneNumber: z.string().nullish(),
        updatedAt: z.string().datetime({ offset: true }),
      })
      .passthrough(),
    userId: z.string().uuid(),
    vogelId: z.string().uuid().nullish(),
  })
  .passthrough();
const CreateStudentDto = z
  .object({
    assistedDrivingBF17: z.boolean().optional(),
    branchId: z.string().optional(),
    bundleId: z.string(),
    contactData: z
      .object({
        city: z.string(),
        company: z.string().optional(),
        email: z.string().email(),
        firstName: z.string(),
        lastName: z.string(),
        phone: z.string(),
        postalCode: z.string(),
        street: z.string(),
      })
      .passthrough(),
    courseId: z.string().optional(),
    dateOfBirth: z.string().regex(/^(19|20)\d{2}-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])$/),
    dateOfRegistration: z.string().datetime({ offset: true }).optional(),
    discountCode: z.string().optional(),
    drivingSchoolId: z.string(),
    endDate: z.string().optional(),
    guardian: z
      .object({ email: z.string().email(), phone: z.string() })
      .passthrough()
      .and(z.object({ firstName: z.string(), lastName: z.string() }).passthrough())
      .optional(),
    invoiceAddress: z
      .object({
        city: z.string(),
        company: z.string().optional(),
        email: z.string().email().optional(),
        firstName: z.string(),
        lastName: z.string(),
        phone: z.string().optional(),
        postalCode: z.string(),
        street: z.string(),
      })
      .passthrough()
      .optional(),
    legal: z
      .object({
        gdprPrivacyPolicy: z
          .object({ checkboxText: z.string(), consent: z.boolean(), subscriptionId: z.string().optional() })
          .passthrough(),
        marketing: z
          .object({ checkboxText: z.string(), consent: z.boolean(), subscriptionId: z.string().optional() })
          .passthrough()
          .optional(),
      })
      .passthrough()
      .optional()
      .default({ gdprPrivacyPolicy: { checkboxText: "", consent: false } }),
    loginEmail: z.string().email(),
    nationality: z.string().min(2).max(2),
    needsGlasses: z.boolean().optional(),
    package: z.string().min(1).optional(),
    packagePrice: MoneyDto.optional(),
    password: z.string().optional(),
    paymentSelection: z.enum(["pay_as_you_drive", "prepaid", "invoice_payment", "external_bank_transfer"]),
    placeOfBirth: z.string(),
    previousDrivingLicenseClasses: z.array(DrivingLicenseClassEnum).optional().default([]),
    promoCodeUsageId: z.string().optional(),
    reference: z.string().optional(),
    salutation: SalutationEnum.optional(),
    startDate: z.string().optional(),
    subscriptionPackageId: z.string().uuid().optional(),
    theoryExamLanguage: z.string().min(2).max(2),
  })
  .passthrough();
const UpdateStudentDto = z
  .object({
    archived: z.boolean(),
    assignedLabels: z.array(z.string()),
    dunningPostponedUntil: z.string().datetime({ offset: true }).nullable(),
    guardian: z.object({ email: z.string(), firstName: z.string(), lastName: z.string(), phone: z.string() }).partial(),
    hubspotContactId: z.string().nullable(),
    nationality: z.string().min(2).max(2),
    needsGlasses: z.boolean(),
    previousDrivingLicenseClasses: z.array(DrivingLicenseClassEnum),
    salutation: SalutationEnum.nullable(),
    subscriptionPackageId: z.string().uuid(),
    theoryBranches: z.array(z.string()),
    theoryExamLanguage: z.string().min(2).max(2),
    user: z
      .object({
        birthName: z.string().nullable(),
        email: z.string().nullable(),
        firstName: z.string(),
        gender: GenderEnum.nullable(),
        lastName: z.string(),
        phoneNumber: z.string().nullable(),
      })
      .partial(),
  })
  .partial();
const StudentActivityDto = z
  .object({
    calendarEvent: z.union([
      z.object({ type: z.literal("TheoryLesson"), uid: z.string().min(1) }).passthrough(),
      z
        .object({
          drivingLessonType: DrivingLessonTypeEnum,
          duration: z.object({ minutes: z.number().int().gte(-9007199254740991).lte(9007199254740991) }).passthrough(),
          type: z.literal("DrivingLesson"),
          uid: z.string().min(1),
        })
        .passthrough(),
    ]),
    dateTime: z.string().datetime({ offset: true }),
    label: z.string().min(1),
  })
  .passthrough();
const AddAttachmentDto = z.object({ createdAt: z.string().optional(), fileName: z.string(), storageRef: z.string() });
const UnblockStudentDto = z.object({ reason: z.string().min(1) });
const CancelBookedTrainingDto = z.object({ cancelReason: z.string().min(1).nullable() }).partial();
const StudentCourseDto = z
  .object({
    bookedQuoteId: z.string().uuid(),
    courseIds: z.array(z.string()),
    endDate: z.string().nullable(),
    modules: z.array(
      z
        .object({
          attendedLessonUnits: z.number().gte(0),
          minutesTooLate: z.number().int().gte(0).lte(9007199254740991),
          missedLessonUnits: z.number().gte(0),
          moduleColor: z.string(),
          moduleLessonUnits: z.number().int().gt(0).lte(9007199254740991),
          moduleName: z.string(),
        })
        .passthrough(),
    ),
    name: z.string(),
    startDate: z.string().nullable(),
  })
  .passthrough();
const ExamResultEnum = z.enum(["passed", "failed"]);
const ExternalTrainingProgressDto = z
  .object({
    autobahnfahrt_UE: z.number().int().gte(-9007199254740991).lte(9007199254740991).nullish(),
    drivingLicenseClass: DrivingLicenseClassEnum,
    nachtfahrt_UE: z.number().int().gte(-9007199254740991).lte(9007199254740991).nullish(),
    practical_exam_date: z.string().nullish(),
    practical_exam_result: ExamResultEnum.nullish(),
    simulator_UE: z.number().int().gte(-9007199254740991).lte(9007199254740991).nullish(),
    studentId: z.string(),
    testfahrt_schaltkompetenz_date: z.string().nullish(),
    theoriestunden_Grundstoff: z.number().int().gte(-9007199254740991).lte(9007199254740991).nullish(),
    theoriestunden_Zusatzstoff: z.number().int().gte(-9007199254740991).lte(9007199254740991).nullish(),
    theory_exam_date: z.string().nullish(),
    theory_exam_result: ExamResultEnum.nullish(),
    ueberlandfahrt_UE: z.number().int().gte(-9007199254740991).lte(9007199254740991).nullish(),
    uebungsfahrt_UE: z.number().int().gte(-9007199254740991).lte(9007199254740991).nullish(),
    uebungsfahrt_mit_Schaltwagen_UE: z.number().int().gte(-9007199254740991).lte(9007199254740991).nullish(),
  })
  .passthrough();
const UpdateExternalTrainingProgressDto = z
  .object({
    autobahnfahrt_UE: z.number().int().gte(-9007199254740991).lte(9007199254740991).nullable(),
    nachtfahrt_UE: z.number().int().gte(-9007199254740991).lte(9007199254740991).nullable(),
    practical_exam_date: z.string().nullable(),
    practical_exam_result: ExamResultEnum.nullable(),
    simulator_UE: z.number().int().gte(-9007199254740991).lte(9007199254740991).nullable(),
    testfahrt_schaltkompetenz_date: z.string().nullable(),
    theoriestunden_Grundstoff: z.number().int().gte(-9007199254740991).lte(9007199254740991).nullable(),
    theoriestunden_Zusatzstoff: z.number().int().gte(-9007199254740991).lte(9007199254740991).nullable(),
    theory_exam_date: z.string().nullable(),
    theory_exam_result: ExamResultEnum.nullable(),
    ueberlandfahrt_UE: z.number().int().gte(-9007199254740991).lte(9007199254740991).nullable(),
    uebungsfahrt_UE: z.number().int().gte(-9007199254740991).lte(9007199254740991).nullable(),
    uebungsfahrt_mit_Schaltwagen_UE: z.number().int().gte(-9007199254740991).lte(9007199254740991).nullable(),
  })
  .partial();
const CreateExternalTrainingProgressDto = z
  .object({
    autobahnfahrt_UE: z.number().int().gte(-9007199254740991).lte(9007199254740991).nullable(),
    nachtfahrt_UE: z.number().int().gte(-9007199254740991).lte(9007199254740991).nullable(),
    practical_exam_date: z.string().nullable(),
    practical_exam_result: ExamResultEnum.nullable(),
    simulator_UE: z.number().int().gte(-9007199254740991).lte(9007199254740991).nullable(),
    testfahrt_schaltkompetenz_date: z.string().nullable(),
    theoriestunden_Grundstoff: z.number().int().gte(-9007199254740991).lte(9007199254740991).nullable(),
    theoriestunden_Zusatzstoff: z.number().int().gte(-9007199254740991).lte(9007199254740991).nullable(),
    theory_exam_date: z.string().nullable(),
    theory_exam_result: ExamResultEnum.nullable(),
    ueberlandfahrt_UE: z.number().int().gte(-9007199254740991).lte(9007199254740991).nullable(),
    uebungsfahrt_UE: z.number().int().gte(-9007199254740991).lte(9007199254740991).nullable(),
    uebungsfahrt_mit_Schaltwagen_UE: z.number().int().gte(-9007199254740991).lte(9007199254740991).nullable(),
  })
  .partial();
const ChangePricesDto = z.object({
  bookedQuoteId: z.string(),
  newPrices: z.record(MoneyDto),
  reason: z.string().min(1),
});
const UpdateStudentSubscriptionDto = z
  .object({
    disabled: z.boolean(),
    pausedUntil: z.string().nullable(),
    renewalDay: z.number().int().gte(1).lte(28),
    subscriptionPackageId: z.string().uuid(),
  })
  .partial();
const SubscriptionPackageDto = z
  .object({
    createdAt: z.string().datetime({ offset: true }),
    createdById: z.string().uuid(),
    drivingSchoolId: z.string(),
    features: z.array(z.string()),
    id: z.string().uuid(),
    name: z.string(),
    price: MoneyDto,
    recommended: z.boolean(),
    updatedAt: z.string().datetime({ offset: true }),
    updatedById: z.string().uuid(),
  })
  .passthrough();
const CreateSubscriptionPackageDto = z
  .object({
    drivingSchoolId: z.string(),
    features: z.array(z.string()).optional().default([]),
    name: z.string(),
    price: MoneyDto,
    recommended: z.boolean(),
  })
  .passthrough();
const PublicSubscriptionPackageDto = z
  .object({
    drivingSchoolId: z.string(),
    features: z.array(z.string()),
    id: z.string().uuid(),
    name: z.string(),
    price: MoneyDto,
    recommended: z.boolean(),
  })
  .passthrough();
const UpdateSubscriptionPackageDto = z
  .object({ features: z.array(z.string()), name: z.string(), price: MoneyDto, recommended: z.boolean() })
  .partial()
  .passthrough();
const TableauJwtRequest = z.object({ drivingSchoolUid: z.string() }).partial().passthrough();
const TableauJwtResponse = z.object({ jwt: z.string() }).passthrough();
const TimetableDto = z
  .object({
    createdAt: z.string().datetime({ offset: true }),
    createdById: z.string().uuid(),
    deleted: z.boolean(),
    deletedAt: z.string().datetime({ offset: true }).nullish(),
    deletedById: z.string().uuid().nullish(),
    drivingSchoolId: z.string(),
    id: z.string().uuid(),
    lessonsSchedule: z.array(z.array(z.string())),
    name: z.string(),
    updatedAt: z.string().datetime({ offset: true }),
    updatedById: z.string().uuid(),
  })
  .passthrough();
const CreateTimetableDto = z.object({
  drivingSchoolId: z.string(),
  idempotencyKey: z.string(),
  lessonsSchedule: z.array(z.array(z.string())),
  name: z.string(),
});
const UpdateTimetableDto = z.object({ lessonsSchedule: z.array(z.array(z.string())), name: z.string() }).partial();
const SendResetPasswordEmailRequest = z.object({ email: z.string().email() }).passthrough();
const UserDto_deprecated = z.object({
  avatarUrl: z.string().optional(),
  createdAt: z.string().datetime({ offset: true }),
  firstName: z.string(),
  id: z.string(),
  lastName: z.string(),
  phoneNumber: z.string().nullish(),
  updatedAt: z.string().datetime({ offset: true }),
});
const PatchUserDto = z
  .object({ firstName: z.string(), lastName: z.string(), phoneNumber: z.string().nullable() })
  .partial();
const UpdateUserGrantsDto = z
  .object({
    delta: z.array(
      z.enum([
        "DrivingLesson:bookOnBehalfOfInstructor",
        "manageDrivingSchoolAutoForm",
        "manageTheoryLessons",
        "manageBkfCourses",
        "manageFleet",
        "manageUsers",
        "CalendarEvent:historical:delete",
        "CalendarEvent:triggerPayment",
        "DrivingLesson:bookWithoutInvite",
        "DrivingLesson:historical:create",
        "Exam:bookWithoutInvite",
        "Exam:historical:create",
        "Instructor:editAvailabilities",
        "Invoice:refund",
        "PrepaidCredits:refund",
        "Scheduler:view",
        "SignatureRequest:create",
        "SignatureRequest:deleteSignedAusbildungsnachweis",
        "Student:getFilledOutForm",
        "TheoryLesson:historical:create",
        "calendarViewEventsOfOtherInstructors",
        "changePrice",
        "changeStatusOfStudentWithOutstandingPayments",
        "createCardReaderPayment",
        "createCashPayment",
        "createExternalBankTransfer",
        "createManualInvoice",
        "drivingSchoolOffboarding",
        "examCreate",
        "giftCredits:add",
        "retryPayments",
        "viewCalendarEventEditHistory",
        "viewDrivingSchoolBusinessIntelligenceData",
        "viewPerformanceOverview",
        "viewDATEV",
        "editDATEVConfig",
        "!DrivingLesson:bookOnBehalfOfInstructor",
        "!manageDrivingSchoolAutoForm",
        "!manageTheoryLessons",
        "!manageBkfCourses",
        "!manageFleet",
        "!manageUsers",
        "!CalendarEvent:historical:delete",
        "!CalendarEvent:triggerPayment",
        "!DrivingLesson:bookWithoutInvite",
        "!DrivingLesson:historical:create",
        "!Exam:bookWithoutInvite",
        "!Exam:historical:create",
        "!Instructor:editAvailabilities",
        "!Invoice:refund",
        "!PrepaidCredits:refund",
        "!Scheduler:view",
        "!SignatureRequest:create",
        "!SignatureRequest:deleteSignedAusbildungsnachweis",
        "!Student:getFilledOutForm",
        "!TheoryLesson:historical:create",
        "!calendarViewEventsOfOtherInstructors",
        "!changePrice",
        "!changeStatusOfStudentWithOutstandingPayments",
        "!createCardReaderPayment",
        "!createCashPayment",
        "!createExternalBankTransfer",
        "!createManualInvoice",
        "!drivingSchoolOffboarding",
        "!examCreate",
        "!giftCredits:add",
        "!retryPayments",
        "!viewCalendarEventEditHistory",
        "!viewDrivingSchoolBusinessIntelligenceData",
        "!viewPerformanceOverview",
        "!viewDATEV",
        "!editDATEVConfig",
      ]),
    ),
  })
  .passthrough();
const UpdateAuthEmailRequest = z.object({ email: z.string().email() }).passthrough();
const GeoCoordinatesDto = z.object({ lat: z.number(), lng: z.number() }).passthrough();
const GearTypeEnum = z.enum(["automatic", "electric", "manual"]);
const VehicleDto = z
  .object({
    adasIds: z.array(z.string()),
    availabilities: z.array(z.array(z.string())),
    branchId: z.string().uuid().nullish(),
    createdAt: z.string().datetime({ offset: true }),
    createdById: z.string().uuid(),
    deleted: z.boolean(),
    deletedAt: z.string().datetime({ offset: true }).nullish(),
    deletedById: z.string().uuid().nullish(),
    drivingLicenseClasses: z.array(DrivingLicenseClassEnum),
    drivingSchoolId: z.string(),
    entitledInstructorIds: z.array(z.string()),
    fahrschulcockpitId: z.number().int().gte(-9007199254740991).lte(9007199254740991).nullish(),
    fahrschuleLiveId: z.number().int().gte(-9007199254740991).lte(9007199254740991).nullish(),
    gearType: GearTypeEnum.nullish(),
    id: z.string(),
    maxDaysInAdvance: z.number().int().gte(-9007199254740991).lte(9007199254740991).nullish(),
    minHoursInAdvance: z.number().int().gte(-9007199254740991).lte(9007199254740991).nullish(),
    name: z.string(),
    notes: z.string(),
    plateNumber: z.string(),
    possibleSimulatorDrivingLessonDurations: z.array(z.number()),
    type: z.enum(["bus", "car", "motorcycle", "simulator", "tractor", "trailer", "truck"]),
    updatedAt: z.string().datetime({ offset: true }),
    updatedById: z.string().uuid(),
    vogelId: z.string().uuid().nullish(),
  })
  .passthrough();
const CreateBusDto = z.object({
  adasIds: z.array(z.string()),
  branchId: z.string().uuid().nullish(),
  drivingLicenseClasses: z.array(DrivingLicenseClassEnum),
  drivingSchoolId: z.string(),
  entitledInstructorIds: z.array(z.string()),
  idempotencyKey: z.string(),
  name: z.string(),
  notes: z.string(),
  plateNumber: z.string(),
  type: z.literal("bus"),
});
const CreateCarDto = z.object({
  adasIds: z.array(z.string()),
  branchId: z.string().uuid().nullish(),
  drivingLicenseClasses: z.array(DrivingLicenseClassEnum),
  drivingSchoolId: z.string(),
  entitledInstructorIds: z.array(z.string()),
  gearType: GearTypeEnum,
  idempotencyKey: z.string(),
  name: z.string(),
  notes: z.string(),
  plateNumber: z.string(),
  type: z.literal("car"),
});
const CreateMotorcycleDto = z.object({
  adasIds: z.array(z.string()),
  branchId: z.string().uuid().nullish(),
  drivingLicenseClasses: z.array(DrivingLicenseClassEnum),
  drivingSchoolId: z.string(),
  entitledInstructorIds: z.array(z.string()),
  idempotencyKey: z.string(),
  name: z.string(),
  notes: z.string(),
  plateNumber: z.string(),
  type: z.literal("motorcycle"),
});
const CreateSimulatorDto = z.object({
  availabilities: z.array(z.array(z.string())),
  branchId: z.string().uuid().nullish(),
  drivingLicenseClasses: z.array(DrivingLicenseClassEnum),
  drivingSchoolId: z.string(),
  idempotencyKey: z.string(),
  maxDaysInAdvance: z.number().int().gte(1).lte(9007199254740991),
  minHoursInAdvance: z.number().int().gte(0).lte(9007199254740991),
  name: z.string(),
  notes: z.string(),
  possibleSimulatorDrivingLessonDurations: z.array(z.number()),
  type: z.literal("simulator"),
});
const CreateTractorDto = z.object({
  adasIds: z.array(z.string()),
  branchId: z.string().uuid().nullish(),
  drivingLicenseClasses: z.array(DrivingLicenseClassEnum),
  drivingSchoolId: z.string(),
  entitledInstructorIds: z.array(z.string()),
  idempotencyKey: z.string(),
  name: z.string(),
  notes: z.string(),
  plateNumber: z.string(),
  type: z.literal("tractor"),
});
const CreateTrailerDto = z.object({
  branchId: z.string().uuid().nullish(),
  drivingLicenseClasses: z.array(DrivingLicenseClassEnum),
  drivingSchoolId: z.string(),
  entitledInstructorIds: z.array(z.string()),
  idempotencyKey: z.string(),
  name: z.string(),
  notes: z.string(),
  plateNumber: z.string(),
  type: z.literal("trailer"),
});
const CreateTruckDto = z.object({
  adasIds: z.array(z.string()),
  branchId: z.string().uuid().nullish(),
  drivingLicenseClasses: z.array(DrivingLicenseClassEnum),
  drivingSchoolId: z.string(),
  entitledInstructorIds: z.array(z.string()),
  idempotencyKey: z.string(),
  name: z.string(),
  notes: z.string(),
  plateNumber: z.string(),
  type: z.literal("truck"),
});
const CreateVehicleDto = z.union([
  CreateBusDto,
  CreateCarDto,
  CreateMotorcycleDto,
  CreateSimulatorDto,
  CreateTractorDto,
  CreateTrailerDto,
  CreateTruckDto,
]);
const UpdateVehicleDto = z
  .object({
    adasIds: z.array(z.string()),
    availabilities: z.array(z.array(z.string())),
    branchId: z.string().uuid().nullable(),
    drivingLicenseClasses: z.array(DrivingLicenseClassEnum),
    entitledInstructorIds: z.array(z.string()),
    gearType: GearTypeEnum,
    maxDaysInAdvance: z.number().int().gte(-9007199254740991).lte(9007199254740991).nullable(),
    minHoursInAdvance: z.number().int().gte(-9007199254740991).lte(9007199254740991).nullable(),
    name: z.string(),
    notes: z.string(),
    plateNumber: z.string(),
    possibleSimulatorDrivingLessonDurations: z.array(z.number()),
  })
  .partial()
  .passthrough();
const VogelFahrschulmanagerContext = z
  .object({
    apiBaseUrl: z.string().url(),
    apiKey: z.string(),
    jwt: z.string(),
    referer: z.string().url(),
    userAgent: z.string(),
  })
  .passthrough();
const GetVogelFahrschuelerDto = z
  .object({
    autovioDrivingSchoolId: z.string(),
    onlyActive: z.boolean().optional(),
    vogelFahrschulmanagerContext: VogelFahrschulmanagerContext,
  })
  .passthrough();
const VogelFahrschuelerDto = z
  .object({
    aktiv: z.boolean(),
    id: z.string(),
    importiert: z.boolean(),
    karteiNr: z.string(),
    klassen: z.string(),
    nachname: z.string(),
    vorname: z.string(),
  })
  .passthrough();
const VogelLoginDto = z.object({ password: z.string(), username: z.string() }).passthrough();
const BookQuoteDto = z
  .object({
    assistedDrivingBF17: z.boolean(),
    branchId: z.string().optional(),
    contactData: z
      .object({
        city: z.string(),
        company: z.string().optional(),
        email: z.string().email(),
        firstName: z.string(),
        lastName: z.string(),
        phone: z.string(),
        postalCode: z.string(),
        street: z.string(),
      })
      .passthrough(),
    courseId: z.string().optional(),
    dateOfBirth: z.string().regex(/^(19|20)\d{2}-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])$/),
    discountCode: z.string().optional(),
    endDate: z.string().optional(),
    guardian: z
      .object({ email: z.string().email(), phone: z.string() })
      .passthrough()
      .and(z.object({ firstName: z.string(), lastName: z.string() }).passthrough())
      .optional(),
    invoiceAddress: z
      .object({
        city: z.string(),
        company: z.string().optional(),
        email: z.string().email().optional(),
        firstName: z.string(),
        lastName: z.string(),
        phone: z.string().optional(),
        postalCode: z.string(),
        street: z.string(),
      })
      .passthrough()
      .optional(),
    legal: z
      .object({
        gdprPrivacyPolicy: z
          .object({ checkboxText: z.string(), consent: z.boolean(), subscriptionId: z.string().optional() })
          .passthrough(),
        marketing: z
          .object({ checkboxText: z.string(), consent: z.boolean(), subscriptionId: z.string().optional() })
          .passthrough()
          .optional(),
      })
      .passthrough(),
    nationality: z.string().min(2).max(2).optional(),
    needsGlasses: z.boolean().optional(),
    package: z.string().min(1).optional(),
    packagePrice: MoneyDto.optional(),
    placeOfBirth: z.string(),
    previousDrivingLicenseClasses: z.array(DrivingLicenseClassEnum).optional(),
    promoCodeUsageId: z.string().optional(),
    quote: z.object({ quoteId: z.string(), quoteName: z.string() }).passthrough(),
    reference: z.string(),
    salutation: SalutationEnum.optional(),
    startDate: z.string().optional(),
    subscriptionPackageId: z.string().uuid().optional(),
    theoryExamLanguage: z.string().min(2).max(2).optional(),
  })
  .passthrough();
const SignUpOfferDto = z
  .object({
    benefits: z.array(z.string()),
    branch: z
      .object({
        name: z.string(),
        postalAddress: z
          .object({
            city: z.string().nullable(),
            country: z.string().nullable(),
            geoCoordinates: z.object({ lat: z.number(), lng: z.number() }).passthrough(),
            postalCode: z.string().nullable(),
            state: z.string().nullable(),
            street: z.string().nullable(),
            type: z.literal("PostalAddress"),
          })
          .partial()
          .passthrough(),
        uid: z.string(),
      })
      .passthrough()
      .optional(),
    branchId: z.string().optional(),
    contactPerson: z
      .object({
        avatarUrl: z.string().optional(),
        bookingUrl: z.string(),
        jobDescription: z.string(),
        mainCta: z.object({ label: z.string(), url: z.string() }).passthrough().optional(),
        name: z.string(),
        phone: z.string(),
        secondaryCta: z.object({ label: z.string(), url: z.string() }).passthrough().optional(),
        title: z.string(),
      })
      .passthrough()
      .optional(),
    drivingSchoolProfile: z
      .object({
        branches: z.array(
          z
            .object({
              name: z.string(),
              postalAddress: z
                .object({
                  city: z.string().nullable(),
                  country: z.string().nullable(),
                  geoCoordinates: z.object({ lat: z.number(), lng: z.number() }).passthrough(),
                  postalCode: z.string().nullable(),
                  state: z.string().nullable(),
                  street: z.string().nullable(),
                  type: z.literal("PostalAddress"),
                })
                .partial()
                .passthrough(),
              uid: z.string(),
            })
            .passthrough(),
        ),
        logoUrl: z.string().optional(),
        minimumSimulatorUE: z.number().int().gte(-9007199254740991).lte(9007199254740991).nullish(),
        name: z.string(),
        postalAddress: z
          .object({
            city: z.string().nullable(),
            country: z.string().nullable(),
            geoCoordinates: z.object({ lat: z.number(), lng: z.number() }).passthrough(),
            postalCode: z.string().nullable(),
            state: z.string().nullable(),
            street: z.string().nullable(),
            type: z.literal("PostalAddress"),
          })
          .partial()
          .passthrough()
          .optional(),
        profileUrl: z.string().optional(),
      })
      .passthrough(),
    geolocation: z
      .object({
        coordinates: z.object({ lat: z.number(), lng: z.number() }).passthrough(),
        imageUrl: z.string().optional(),
        imageUrlSmall: z.string().optional(),
      })
      .passthrough()
      .optional(),
    ignoreSubscriptionPackages: z.boolean(),
    imageUrls: z.array(z.string()),
    offerIntroVideo: z
      .object({ previewImageUrl: z.string(), url: z.string(), urlSmall: z.string() })
      .passthrough()
      .optional(),
    promoCode: z.string().optional(),
    quotes: z.array(
      z
        .object({
          allowedPaymentSelections: z.array(SignUpPaymentSelectionEnum),
          asfCourseId: z.string().optional(),
          bundleSnapshot: z
            .object({
              areas: z.array(
                z
                  .object({
                    circles: z.array(z.object({ lat: z.number(), lng: z.number(), r: z.number() }).passthrough()),
                    displayLocation: z
                      .object({
                        city: z.string(),
                        geoCoordinates: z.object({ lat: z.number(), lng: z.number() }).passthrough(),
                        postalCode: z.string(),
                        street: z.string(),
                        type: z.literal("PostalAddress"),
                      })
                      .partial()
                      .passthrough()
                      .optional(),
                    drivingSchoolId: z.string().optional(),
                    id: z.string(),
                    instructorIds: z.array(z.string()).optional(),
                    name: z.string(),
                  })
                  .passthrough(),
              ),
              authorityFees: z.record(z.number().int().gte(0).lte(9007199254740991)),
              baseFee: z.number(),
              description: z.string().optional(),
              disabled: z.boolean().optional(),
              drivingSchoolId: z.string().min(1),
              featureList: z.array(z.object({ name: z.string(), value: z.string() }).passthrough()),
              guaranteedDrivingLessonsPerWeek: z.number().int().gte(0).lte(9007199254740991).optional(),
              icon: z.enum(["savings", "speed", "bolt"]).optional(),
              id: z.string(),
              isDrivingLicenseClassAdvancement: z.boolean().optional(),
              isDrivingSchoolChange: z.boolean().optional(),
              isHighlight: z.boolean().optional(),
              name: z.string(),
              productPrices: z.record(z.number().int().gte(0).lte(9007199254740991)),
              products: z.array(
                z
                  .object({
                    description: z.string(),
                    id: z.string(),
                    name: z.string(),
                    requires: z.array(
                      z.enum([
                        "instructor",
                        "motorcycle",
                        "car",
                        "manual_car",
                        "trailer",
                        "location",
                        "zoom_seat",
                        "simulator",
                      ]),
                    ),
                    type: z.enum([
                      "Übungsstunde",
                      "Überlandfahrt",
                      "Autobahnfahrt",
                      "Nachtfahrt",
                      "Simulator-Fahrstunde",
                      "Testfahrt_Schaltkompetenz",
                      "Theorieunterricht_Grundstoff",
                      "Theorieunterricht_B96",
                      "Theorieunterricht_Zusatzstoff_A",
                      "Theorieunterricht_Zusatzstoff_B",
                      "Theorieunterricht_Zusatzstoff_C",
                      "Theorieunterricht_Zusatzstoff_CE",
                      "Theorieunterricht_Zusatzstoff_D",
                      "Theorieunterricht_Zusatzstoff_L",
                      "Theorieunterricht_Zusatzstoff_T",
                      "Theorieprüfung",
                      "Fahrprüfung",
                      "Zoom-Meeting",
                      "sonstiges",
                    ]),
                  })
                  .passthrough(),
              ),
              salesCategory: z
                .enum([
                  "initial-signup",
                  "transcription",
                  "extension",
                  "coaching-amaxophobia",
                  "coaching-mpu",
                  "asf-course",
                ])
                .optional(),
              subtitle: z.string().optional(),
              tags: z.array(z.string()).min(1),
              teachingMaterialFee: z.number().optional(),
              title: z.string().optional(),
              trainings: z.array(
                z
                  .object({
                    compulsoryLessons: z.record(z.number()),
                    drivingLicenseClass: DrivingLicenseClassEnum,
                    id: z.string(),
                    name: z.string(),
                    products: z.array(
                      z
                        .object({
                          description: z.string(),
                          id: z.string(),
                          name: z.string(),
                          requires: z.array(
                            z.enum([
                              "instructor",
                              "motorcycle",
                              "car",
                              "manual_car",
                              "trailer",
                              "location",
                              "zoom_seat",
                              "simulator",
                            ]),
                          ),
                          type: z.enum([
                            "Übungsstunde",
                            "Überlandfahrt",
                            "Autobahnfahrt",
                            "Nachtfahrt",
                            "Simulator-Fahrstunde",
                            "Testfahrt_Schaltkompetenz",
                            "Theorieunterricht_Grundstoff",
                            "Theorieunterricht_B96",
                            "Theorieunterricht_Zusatzstoff_A",
                            "Theorieunterricht_Zusatzstoff_B",
                            "Theorieunterricht_Zusatzstoff_C",
                            "Theorieunterricht_Zusatzstoff_CE",
                            "Theorieunterricht_Zusatzstoff_D",
                            "Theorieunterricht_Zusatzstoff_L",
                            "Theorieunterricht_Zusatzstoff_T",
                            "Theorieprüfung",
                            "Fahrprüfung",
                            "Zoom-Meeting",
                            "sonstiges",
                          ]),
                        })
                        .passthrough(),
                    ),
                  })
                  .passthrough(),
              ),
            })
            .passthrough(),
          createdAt: z.string(),
          customerName: z.string().optional(),
          id: z.string(),
          instructorId: z.string().optional(),
          validUntil: z.string(),
        })
        .passthrough(),
    ),
    slug: z.string(),
    studentEmail: z.string().optional(),
  })
  .passthrough();
const SignUpQuoteDto = z
  .object({
    drivingSchoolProfile: z
      .object({
        branches: z.array(
          z
            .object({
              name: z.string(),
              postalAddress: z
                .object({
                  city: z.string().nullable(),
                  country: z.string().nullable(),
                  geoCoordinates: z.object({ lat: z.number(), lng: z.number() }).passthrough(),
                  postalCode: z.string().nullable(),
                  state: z.string().nullable(),
                  street: z.string().nullable(),
                  type: z.literal("PostalAddress"),
                })
                .partial()
                .passthrough(),
              uid: z.string(),
            })
            .passthrough(),
        ),
        logoUrl: z.string().optional(),
        minimumSimulatorUE: z.number().int().gte(-9007199254740991).lte(9007199254740991).nullish(),
        name: z.string(),
        postalAddress: z
          .object({
            city: z.string().nullable(),
            country: z.string().nullable(),
            geoCoordinates: z.object({ lat: z.number(), lng: z.number() }).passthrough(),
            postalCode: z.string().nullable(),
            state: z.string().nullable(),
            street: z.string().nullable(),
            type: z.literal("PostalAddress"),
          })
          .partial()
          .passthrough()
          .optional(),
        profileUrl: z.string().optional(),
      })
      .passthrough(),
    quote: z
      .object({
        allowedPaymentSelections: z.array(SignUpPaymentSelectionEnum),
        asfCourseId: z.string().optional(),
        bundleSnapshot: z
          .object({
            areas: z.array(
              z
                .object({
                  circles: z.array(z.object({ lat: z.number(), lng: z.number(), r: z.number() }).passthrough()),
                  displayLocation: z
                    .object({
                      city: z.string(),
                      geoCoordinates: z.object({ lat: z.number(), lng: z.number() }).passthrough(),
                      postalCode: z.string(),
                      street: z.string(),
                      type: z.literal("PostalAddress"),
                    })
                    .partial()
                    .passthrough()
                    .optional(),
                  drivingSchoolId: z.string().optional(),
                  id: z.string(),
                  instructorIds: z.array(z.string()).optional(),
                  name: z.string(),
                })
                .passthrough(),
            ),
            authorityFees: z.record(z.number().int().gte(0).lte(9007199254740991)),
            baseFee: z.number(),
            description: z.string().optional(),
            disabled: z.boolean().optional(),
            drivingSchoolId: z.string().min(1),
            featureList: z.array(z.object({ name: z.string(), value: z.string() }).passthrough()),
            guaranteedDrivingLessonsPerWeek: z.number().int().gte(0).lte(9007199254740991).optional(),
            icon: z.enum(["savings", "speed", "bolt"]).optional(),
            id: z.string(),
            isDrivingLicenseClassAdvancement: z.boolean().optional(),
            isDrivingSchoolChange: z.boolean().optional(),
            isHighlight: z.boolean().optional(),
            name: z.string(),
            productPrices: z.record(z.number().int().gte(0).lte(9007199254740991)),
            products: z.array(
              z
                .object({
                  description: z.string(),
                  id: z.string(),
                  name: z.string(),
                  requires: z.array(
                    z.enum([
                      "instructor",
                      "motorcycle",
                      "car",
                      "manual_car",
                      "trailer",
                      "location",
                      "zoom_seat",
                      "simulator",
                    ]),
                  ),
                  type: z.enum([
                    "Übungsstunde",
                    "Überlandfahrt",
                    "Autobahnfahrt",
                    "Nachtfahrt",
                    "Simulator-Fahrstunde",
                    "Testfahrt_Schaltkompetenz",
                    "Theorieunterricht_Grundstoff",
                    "Theorieunterricht_B96",
                    "Theorieunterricht_Zusatzstoff_A",
                    "Theorieunterricht_Zusatzstoff_B",
                    "Theorieunterricht_Zusatzstoff_C",
                    "Theorieunterricht_Zusatzstoff_CE",
                    "Theorieunterricht_Zusatzstoff_D",
                    "Theorieunterricht_Zusatzstoff_L",
                    "Theorieunterricht_Zusatzstoff_T",
                    "Theorieprüfung",
                    "Fahrprüfung",
                    "Zoom-Meeting",
                    "sonstiges",
                  ]),
                })
                .passthrough(),
            ),
            salesCategory: z
              .enum([
                "initial-signup",
                "transcription",
                "extension",
                "coaching-amaxophobia",
                "coaching-mpu",
                "asf-course",
              ])
              .optional(),
            subtitle: z.string().optional(),
            tags: z.array(z.string()).min(1),
            teachingMaterialFee: z.number().optional(),
            title: z.string().optional(),
            trainings: z.array(
              z
                .object({
                  compulsoryLessons: z.record(z.number()),
                  drivingLicenseClass: DrivingLicenseClassEnum,
                  id: z.string(),
                  name: z.string(),
                  products: z.array(
                    z
                      .object({
                        description: z.string(),
                        id: z.string(),
                        name: z.string(),
                        requires: z.array(
                          z.enum([
                            "instructor",
                            "motorcycle",
                            "car",
                            "manual_car",
                            "trailer",
                            "location",
                            "zoom_seat",
                            "simulator",
                          ]),
                        ),
                        type: z.enum([
                          "Übungsstunde",
                          "Überlandfahrt",
                          "Autobahnfahrt",
                          "Nachtfahrt",
                          "Simulator-Fahrstunde",
                          "Testfahrt_Schaltkompetenz",
                          "Theorieunterricht_Grundstoff",
                          "Theorieunterricht_B96",
                          "Theorieunterricht_Zusatzstoff_A",
                          "Theorieunterricht_Zusatzstoff_B",
                          "Theorieunterricht_Zusatzstoff_C",
                          "Theorieunterricht_Zusatzstoff_CE",
                          "Theorieunterricht_Zusatzstoff_D",
                          "Theorieunterricht_Zusatzstoff_L",
                          "Theorieunterricht_Zusatzstoff_T",
                          "Theorieprüfung",
                          "Fahrprüfung",
                          "Zoom-Meeting",
                          "sonstiges",
                        ]),
                      })
                      .passthrough(),
                  ),
                })
                .passthrough(),
            ),
          })
          .passthrough(),
        createdAt: z.string(),
        customerName: z.string().optional(),
        id: z.string(),
        instructorId: z.string().optional(),
        validUntil: z.string(),
      })
      .passthrough(),
  })
  .passthrough();
const StudentOnboardingStateDto = z
  .object({
    notificationsSeen: z.record(z.string().datetime({ offset: true })),
    numPrepaidDrivingLessonsPerLicenseClass: z.record(z.number().int().gte(0).lte(9007199254740991)).nullable(),
    paymentSelection: SignUpPaymentSelectionEnum.nullable(),
    prepaidCheckoutAmount: z
      .string()
      .regex(/^(0|[1-9]\d{0,5})\.\d{2}$/)
      .nullable(),
    promoCodeUsageId: z.string().uuid().nullable(),
    userId: z.string().uuid(),
  })
  .passthrough();
const StudentOnboardingStateUpdateDto = z
  .object({
    notificationsSeen: z.record(z.string().datetime({ offset: true })),
    numPrepaidDrivingLessonsPerLicenseClass: z.record(z.number().int().gte(0).lte(9007199254740991)).nullable(),
    paymentSelection: SignUpPaymentSelectionEnum.nullable(),
    prepaidCheckoutAmount: z
      .string()
      .regex(/^(0|[1-9]\d{0,5})\.\d{2}$/)
      .nullable(),
    promoCodeUsageId: z.string().uuid().nullable(),
  })
  .partial();
const StudentOnboardingStateImportDto = z
  .object({
    firebaseAuthUserUid: z.string().min(1),
    notificationsSeen: z.record(z.string().datetime({ offset: true })),
    numPrepaidDrivingLessonsPerLicenseClass: z.record(z.number().int().gte(0).lte(9007199254740991)).nullable(),
    paymentSelection: SignUpPaymentSelectionEnum.nullable(),
    prepaidCheckoutAmount: z
      .string()
      .regex(/^(0|[1-9]\d{0,5})\.\d{2}$/)
      .nullable(),
    promoCodeUsageId: z.string().uuid().nullable(),
  })
  .partial();
const BlockStudentDto = UnblockStudentDto;
const GetCalendarEventsFilterDto = z
  .object({
    courseId: z.string().min(1).optional(),
    drivingSchoolId: z.string().min(1).optional(),
    end: z.string().datetime({ offset: true }),
    instructorId: z.string().min(1).optional(),
    start: z.string().datetime({ offset: true }),
    type: z.array(z.enum(["VehicleEvent", "CourseLesson"])).min(1),
    vehicleId: z.string().min(1).optional(),
  })
  .passthrough();
const NoteFilterDto = z
  .object({
    createdById: z.string().uuid(),
    drivingSchoolId: z.string().nullish(),
    id: z.string().uuid(),
    studentId: z.string().nullish(),
    updatedById: z.string().uuid(),
  })
  .passthrough();
const PostFilterDto = z
  .object({
    createdById: z.string().uuid(),
    id: z.string().uuid(),
    threadId: z.string().uuid(),
    updatedById: z.string().uuid(),
  })
  .passthrough();
const RoleEnum = z.enum([
  "admin",
  "appTester",
  "dispatcher",
  "drivingSchoolManager",
  "instructor",
  "partnerSuccess",
  "student",
  "studentSales",
  "drivingSchoolAnalyst",
  "drivingSchoolCustomerService",
]);
const StudentFilterDto = z
  .object({
    "!name": z.string(),
    drivingSchoolId: z.string(),
    id: z.string(),
    "user.firstName": z.string(),
    "user.lastName": z.string(),
    "user.phoneNumber": z.string().nullable(),
  })
  .partial()
  .passthrough();
const ThreadDto = z
  .object({
    createdAt: z.string().datetime({ offset: true }),
    id: z.string().uuid(),
    posts: z.record(PostDto).optional(),
    updatedAt: z.string().datetime({ offset: true }),
  })
  .passthrough();

export const schemas = {
  AdasDto,
  CreateAdasDto,
  UpdateAdasDto,
  executeAdminAction_Body,
  createSepaDebitMandate_Body,
  createVogelAppointment_Body,
  createVogelExportAuth_Body,
  updateVogelExportAuth_Body,
  AppliedReferralCodeDto,
  AttachmentDto,
  CreateDrivingSchoolAutoFormDto,
  JsonValue,
  DrivingSchoolAutoFormDto,
  UpdateDrivingSchoolAutoFormDto,
  UserDto,
  BranchDto,
  CreateBranchDto,
  UpdateBranchDto,
  MoneyDto,
  CancellationPolicyFixedFeeDto,
  CancellationPolicyPercentageFeeDto,
  CancellationPolicyFeeDto,
  CancellationPolicyPreconditionDto,
  CancellationPolicyRuleDto,
  CancellationPolicyDto,
  AddStudentToCourseDto,
  PostalAddressDto,
  ASFCourseSessionTypeEnum,
  ASFCourseSessionDto,
  ASFCourseDto,
  BookTheoryLessonsDto,
  AcceptInviteDto,
  TheoryLessonAttendanceEnum,
  UpdateAttendanceDto,
  CancelBookedAppointmentDto,
  UpdateExamResultDto,
  UpdateTheoryLessonStudentBookedTrainingsDto,
  SingleFilter,
  SingleOrder,
  VehicleEventTypeEnum,
  VehicleEventDto,
  BufferTimesDto,
  CourseLessonDto,
  CalendarEventDto,
  CreateASFCourseDto,
  TheoryLectureCategoryEnum,
  TheoryLectureDto,
  CreateTheoryLessonDto,
  CreateTheoryExamDto,
  AdditionalContentEnum,
  DrivingLessonTypeEnum2,
  CreateDrivingLessonDto,
  CreateRemoteLessonDto,
  CreateCourseLessonDto,
  CreateVehicleEventDto,
  OtherEventDurationEnum,
  CreateOtherCalendarEventDto,
  CreateCalendarEventDto,
  PostalAddress,
  OnlineMeeting,
  CreateTheoryCourseDto,
  RemoveStudentFromCourseDto,
  EditDrivingLessonDto3,
  EditTheoryLessonDto,
  EditOtherEventDto,
  EditRemoteLessonDto,
  EditCourseLessonDto,
  EditVehicleEventDto,
  EditASFCourseSessionDto,
  EditCalendarEventDto3,
  TheoryCourseLecture,
  DrivingSchoolTheoryCourseDto,
  DrivingSchoolTheoryCoursesDto,
  CreateContractForBookedTrainingDto,
  GenerateContractPdfDto,
  ListCostBearerDto,
  CreateCostBearerDto,
  CostBearerDto,
  UpdateCostBearerDto,
  CourseLessonParticipantCheckInEnum,
  CourseLessonAttendanceStatusEnum,
  CourseLessonAttendancesDto,
  SetCourseLessonAttendancesDto,
  CourseLessonCheckInDto,
  DrivingLicenseClassEnum,
  CourseTemplateDto,
  CreateCourseTemplateDto,
  UpdateCourseTemplateDto,
  CourseDto,
  CreateCourseDto,
  UpdateCourseDto,
  CourseBundleDto,
  CloneCourseDto,
  CourseParticipantDto,
  EnrollStudentInCourseDto,
  CustomCardDto,
  CreateCustomCardDto,
  createCustomCard_Body,
  UpdateCustomCardDto,
  updateCustomCard_Body,
  SchaltnachweisDataDto,
  SignDocumentsDto,
  SignDocumentsResultDto,
  SignByStudentDto,
  CreateSignatureRequestDto,
  CreateSignatureRequestResultDto,
  DrivingLicenseAuthorityFormTypeEnum,
  DrivingLicenseAuthorityDto,
  CreateDrivingLicenseAuthorityDto,
  DrivingLicenseAuthorityFormDto,
  CreateDrivingLicenseAuthorityFormDto,
  UpdateDrivingLicenseAuthorityFormDto,
  UpdateDrivingLicenseAuthorityInstructionDto,
  UpdateDrivingLicenseAuthorityDto,
  CreateDrivingLicenseAuthorityInstructionDto,
  CreateDrivingSchoolDto,
  ExamLocationWeekdayEnum,
  ExamTypeEnum,
  ExamLocationDto,
  CreateExamLocationDto,
  UpdateExamLocationDto,
  ListDrivingSchoolLabelDto,
  DrivingSchoolLabelColorEnum,
  CreateDrivingSchoolLabelDto,
  DrivingSchoolLabelDto,
  UpdateDrivingSchoolLabelDto,
  ListOtherEventCategoryDto,
  OtherEventCategoryDurationOptionEnum,
  UtilizationCategoryEnum,
  CreateOtherEventCategoryDto,
  OtherCalendarEventCategoryTypeEnum,
  OtherEventCategoryDto,
  UpdateOtherEventCategoryDto,
  RoomDto,
  CreateRoomDto,
  UpdateRoomDto,
  StatsOverviewDto,
  StudentSignupStatsDto,
  DrivingSchoolSchemaDto,
  UpdateDrivingSchoolDto,
  EmployeeDto,
  ServiceTypeDto,
  SignupDrivingSchoolAutoFormDto,
  BlockStudentAndSendPaymentReminderRequest,
  SendDunningNoticeRequest,
  SendSammelquittungEmailDto,
  GenderEnum,
  CreateEmployeeDto,
  UpdateEmployeeDto,
  ExamAuthorityFeeDto,
  CreateExamAuthorityFeeDto,
  UpdateExamAuthorityFeeDto,
  CreateInstructorDto,
  InstructorDto,
  WorktimeStatsDto,
  WorktimeStatsDto2,
  UpdateInstructorDto,
  RecommendationSettingsDto,
  UpdateSignatureSettingsDto,
  GeofencedTimeRangeDto,
  WorkTimesDto,
  filter,
  UpdateNoteDto,
  NoteDto,
  CreateNoteDto,
  CreateNotificationDto,
  _PayBaseFeeRequestDto,
  PayBaseFeeResponseDto,
  _PayBaseFeeResponseDto,
  CreatePostDto,
  PostDto,
  UpdatePostDto,
  PromoCodeDto,
  CreatePromoCodeDto,
  ReservePromoCodeRequest,
  ReservePromoCodeResponse,
  PromoCodeExpiredErrorDto,
  TooManyUsagesErrorDto,
  UpdatePromoCodeDto,
  PublicInstructorDto,
  AiPromptBriefingsDto,
  AiPromptBriefingsItemDto,
  AiPromptBriefingsUpdateDto,
  AiAdminProviderSettingsDto,
  AiAdminProviderSettingsItemDto,
  AiAdminProviderSettingsUpdateDto,
  AiSessionLogEntryDto,
  AiSessionLogsResponseDto,
  AiUsageControlsDto,
  AiUsageControlsItemDto,
  AiUsageControlsUpdateDto,
  AiUsageBudgetStateDto,
  AiUsageEventDto,
  AiUsageEventsResponseDto,
  AiWarehouseSchemaCatalogFileDto,
  AiWarehouseSchemaCatalogResponseDto,
  AiWarehouseSchemaCatalogUploadDto,
  PulseAnalysisDefinitionDto,
  PulseAnalysisListDto,
  PulseRegistryDeleteResultDto,
  PulseDashboardDefinitionDto,
  PulseDashboardListDto,
  PulseDatasourceDatasetsResponseDto,
  PulseDatasourceFieldUniquesResponseDto,
  PulseDatasourceDefinitionDto,
  PulseDatasourceFieldUniquesBodyDto,
  PulseDatasourceIntrospectSchemaResponseDto,
  PulseDatasourcePreviewResponseDto,
  PulseDatasourcePreviewBodyDto,
  PulseDatasourceProjectsResponseDto,
  PulseDatasourceSourceObjectsResponseDto,
  PulseDatasourceListDto,
  PulseGrantConfigDeleteDto,
  PulseGrantDefinitionDto,
  PulseGrantGroupDefinitionDto,
  PulseGrantRoleMappingDefinitionDto,
  PulseGrantOverrideDefinitionDto,
  PulseGrantConfigurationDto,
  PulseGrantGroupConfigPutDto,
  PulseGrantRoleMappingConfigPutDto,
  PulseGrantSchoolOverrideConfigPutDto,
  PulseGrantUserOverrideConfigPutDto,
  upsertPulseGrantConfigurationItem_Body,
  PulseMetricDefinitionDto,
  PulseMetricListDto,
  PulseThemeDefinitionDto,
  PulseThemeListDto,
  DrivingLessonTypeEnum,
  PriceDto,
  RecommendationDto,
  ReferralCodeApplicationDto,
  ReferralCodeUsageDto,
  ClaimRewardRequestBody,
  ClaimRewardResponseBody,
  CreateGiftVoucherRequestBody,
  GiftVoucherDto,
  DrivingSchoolSearchResultDto,
  GuardianSearchResultDto,
  InstructorSearchResultDto,
  StudentSearchResultDto,
  SearchResponseDto,
  ListServiceEntryDto,
  CreateServiceEntryDto,
  ServiceEntryDto,
  UpdateServiceEntryDto,
  CreateServiceTypeDto,
  UpdateServiceTypeDto,
  SignUpPaymentSelectionEnum,
  SignUpDataUpdateDto,
  SignUpData,
  SalutationEnum,
  StudentDto,
  CreateStudentDto,
  UpdateStudentDto,
  StudentActivityDto,
  AddAttachmentDto,
  UnblockStudentDto,
  CancelBookedTrainingDto,
  StudentCourseDto,
  ExamResultEnum,
  ExternalTrainingProgressDto,
  UpdateExternalTrainingProgressDto,
  CreateExternalTrainingProgressDto,
  ChangePricesDto,
  UpdateStudentSubscriptionDto,
  SubscriptionPackageDto,
  CreateSubscriptionPackageDto,
  PublicSubscriptionPackageDto,
  UpdateSubscriptionPackageDto,
  TableauJwtRequest,
  TableauJwtResponse,
  TimetableDto,
  CreateTimetableDto,
  UpdateTimetableDto,
  SendResetPasswordEmailRequest,
  UserDto_deprecated,
  PatchUserDto,
  UpdateUserGrantsDto,
  UpdateAuthEmailRequest,
  GeoCoordinatesDto,
  GearTypeEnum,
  VehicleDto,
  CreateBusDto,
  CreateCarDto,
  CreateMotorcycleDto,
  CreateSimulatorDto,
  CreateTractorDto,
  CreateTrailerDto,
  CreateTruckDto,
  CreateVehicleDto,
  UpdateVehicleDto,
  VogelFahrschulmanagerContext,
  GetVogelFahrschuelerDto,
  VogelFahrschuelerDto,
  VogelLoginDto,
  BookQuoteDto,
  SignUpOfferDto,
  SignUpQuoteDto,
  StudentOnboardingStateDto,
  StudentOnboardingStateUpdateDto,
  StudentOnboardingStateImportDto,
  BlockStudentDto,
  GetCalendarEventsFilterDto,
  NoteFilterDto,
  PostFilterDto,
  RoleEnum,
  StudentFilterDto,
  ThreadDto,
};

const endpoints = makeApi([
  {
    method: "get",
    path: "/adas",
    alias: "listAdas",
    requestFormat: "json",
    response: z.array(AdasDto),
  },
  {
    method: "post",
    path: "/adas",
    alias: "createAdas",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateAdasDto,
      },
    ],
    response: AdasDto,
  },
  {
    method: "get",
    path: "/adas/:id",
    alias: "getAdas",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: AdasDto,
  },
  {
    method: "patch",
    path: "/adas/:id",
    alias: "updateAdas",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateAdasDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: AdasDto,
  },
  {
    method: "get",
    path: "/api/v1/datapart/abgleich",
    alias: "getBWAOfCustomerToDate",
    description: `Get business evaluation report up to the specified cut-off date`,
    requestFormat: "json",
    parameters: [
      {
        name: "stichtag",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/v1/datapart/admin",
    alias: "getAdminView",
    description: `Renders an HTML page for triggering admin actions`,
    requestFormat: "json",
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/v1/datapart/admin/action",
    alias: "executeAdminAction",
    description: `Execute an admin action with the provided payload`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Action and payload for admin operation`,
        type: "Body",
        schema: executeAdminAction_Body,
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/v1/datapart/debug/sync-logs",
    alias: "getSyncLogsDebugView",
    description: `Renders an HTML page showing DatapartSyncLog entries`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "objectId",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/v1/datapart/entities",
    alias: "getDatapartEntities",
    description: `Returns DatapartEntity entries as JSON`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "type",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/v1/datapart/fahrschueler/changed",
    alias: "notifyStudentChanges",
    description: `Receive notification about changed students from Datapart`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Array of changed student UUIDs`,
        type: "Body",
        schema: z.array(z.string()),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/v1/datapart/fahrschueler/opstatus/:uuid",
    alias: "studentOPStatusGet",
    description: `Op Status eines Fahrschuelers abfragen`,
    requestFormat: "json",
    parameters: [
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/v1/datapart/fahrschueler/opstatus/changed",
    alias: "notifyOPStatusChanges",
    description: `Receive notification about changed OP (Offene Posten) statuses from Datapart`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Array of student IDs with changed OP status`,
        type: "Body",
        schema: z.array(z.string()),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/v1/datapart/fahrschueler/query",
    alias: "retrieveStudentPatches",
    description: `Retrieve multiple students by their UUIDs`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Array of student UUIDs to retrieve`,
        type: "Body",
        schema: z.array(z.string()),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 404,
        description: `No students were found`,
        schema: z.void(),
      },
      {
        status: 416,
        description: `Requested range not satisfiable, returns partial list`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/v1/datapart/gutschriften/changed",
    alias: "notifyCreditChanges",
    description: `Receive notification about changed credits from Datapart`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Array of changed credit UUIDs`,
        type: "Body",
        schema: z.array(z.string()),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/v1/datapart/rechnungssaetze",
    alias: "findInvoicesForDate",
    description: `Find invoice IDs within a specified date range`,
    requestFormat: "json",
    parameters: [
      {
        name: "from",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "until",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 404,
        description: `No invoices were found in time period`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/v1/datapart/rechnungssaetze/query",
    alias: "findInvoicesFor",
    description: `Retrieve multiple invoices by their IDs`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Array of invoice IDs to retrieve`,
        type: "Body",
        schema: z.array(z.string()),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 404,
        description: `No invoices were found`,
        schema: z.void(),
      },
      {
        status: 416,
        description: `Requested range not satisfiable, returns partial list`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/v1/datapart/rechnungssaetze/status/changed",
    alias: "notifyInvoiceChanges",
    description: `Receive notification about changed invoice statuses from Datapart`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Array of changed invoice UUIDs`,
        type: "Body",
        schema: z.array(z.string()),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/v1/datapart/sepa-mandate",
    alias: "createSepaDebitMandate",
    description: `Record a SEPA direct debit mandate for DATAPART Factoring`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `SEPA mandate data`,
        type: "Body",
        schema: createSepaDebitMandate_Body,
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 400,
        description: `Invalid payload`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/v1/datapart/sync-logs",
    alias: "getSyncLogs",
    description: `Returns DatapartSyncLog entries as JSON`,
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "context",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "objectId",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/v1/datapart/zahlungssaetze",
    alias: "findPaymentsForDate",
    description: `Find payment IDs within a specified date range`,
    requestFormat: "json",
    parameters: [
      {
        name: "from",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "until",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 404,
        description: `No payments were found in the time period`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/api/v1/datapart/zahlungssaetze/query",
    alias: "findPaymentsFor",
    description: `Retrieve multiple payments by their IDs`,
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        description: `Array of payment IDs to retrieve`,
        type: "Body",
        schema: z.array(z.string()),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 404,
        description: `No payments were found`,
        schema: z.void(),
      },
      {
        status: 416,
        description: `Requested range not satisfiable, returns partial list`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/api/v1/vogel-export/admin",
    alias: "getVogelExportAdminView",
    description: `Renders an HTML page for triggering Vogel export actions and managing related entities.`,
    requestFormat: "json",
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/v1/vogel-export/appointments",
    alias: "createVogelAppointment",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: createVogelAppointment_Body,
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/v1/vogel-export/calendar-event-vogel-sync",
    alias: "listCalendarEventVogelSync",
    requestFormat: "json",
    response: z.void(),
  },
  {
    method: "delete",
    path: "/api/v1/vogel-export/calendar-event-vogel-sync/:id",
    alias: "deleteCalendarEventVogelSync",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/v1/vogel-export/calendar-event-vogel-sync/:id",
    alias: "getCalendarEventVogelSync",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/v1/vogel-export/execute",
    alias: "executeVogelSync",
    requestFormat: "json",
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/v1/vogel-export/plan",
    alias: "planVogelSync",
    requestFormat: "json",
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/v1/vogel-export/students",
    alias: "createVogelStudent",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ autovioUserId: z.string() }).passthrough(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/v1/vogel-export/vogel-export-auth",
    alias: "listVogelExportAuth",
    requestFormat: "json",
    response: z.void(),
  },
  {
    method: "post",
    path: "/api/v1/vogel-export/vogel-export-auth",
    alias: "createVogelExportAuth",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: createVogelExportAuth_Body,
      },
    ],
    response: z.void(),
  },
  {
    method: "delete",
    path: "/api/v1/vogel-export/vogel-export-auth/:id",
    alias: "deleteVogelExportAuth",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/api/v1/vogel-export/vogel-export-auth/:id",
    alias: "getVogelExportAuth",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "patch",
    path: "/api/v1/vogel-export/vogel-export-auth/:id",
    alias: "updateVogelExportAuth",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: updateVogelExportAuth_Body,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/applied-referral-codes",
    alias: "listAppliedReferralCodes",
    requestFormat: "json",
    parameters: [
      {
        name: "range",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "sort",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "filter",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: z.array(AppliedReferralCodeDto),
  },
  {
    method: "get",
    path: "/attachment/:attachmentId",
    alias: "getAttachment",
    requestFormat: "json",
    parameters: [
      {
        name: "attachmentId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: AttachmentDto,
  },
  {
    method: "get",
    path: "/attachment/:attachmentId/download",
    alias: "downloadAttachment",
    requestFormat: "json",
    parameters: [
      {
        name: "attachmentId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/auto-forms",
    alias: "createAutoForm",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateDrivingSchoolAutoFormDto,
      },
    ],
    response: z.string(),
  },
  {
    method: "delete",
    path: "/auto-forms/:id",
    alias: "deleteAutoForm",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/auto-forms/:id",
    alias: "getAutoForm",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: DrivingSchoolAutoFormDto,
  },
  {
    method: "patch",
    path: "/auto-forms/:id",
    alias: "updateAutoForm",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateDrivingSchoolAutoFormDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "put",
    path: "/auto-forms/:id/pdfTemplateBaseFile",
    alias: "attachPdfTemplateBaseFileToAutoForm",
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ pdf: z.instanceof(File) }).passthrough(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/autovio-staff",
    alias: "getAutovioStaff",
    requestFormat: "json",
    response: z.array(UserDto),
  },
  {
    method: "get",
    path: "/branch",
    alias: "listBranches",
    requestFormat: "json",
    parameters: [
      {
        name: "drivingSchoolId",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: z.array(BranchDto),
  },
  {
    method: "post",
    path: "/branch",
    alias: "createBranch",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateBranchDto,
      },
    ],
    response: BranchDto,
  },
  {
    method: "get",
    path: "/branch/:id",
    alias: "getBranch",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: BranchDto,
  },
  {
    method: "patch",
    path: "/branch/:id",
    alias: "updateBranch",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateBranchDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: BranchDto,
  },
  {
    method: "get",
    path: "/calendar/:drivingSchoolId/theory-courses",
    alias: "getDrivingSchoolTheoryCourses",
    requestFormat: "json",
    parameters: [
      {
        name: "drivingSchoolId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.array(DrivingSchoolTheoryCourseDto),
  },
  {
    method: "post",
    path: "/calendar/add-student-to-course",
    alias: "addStudentToCourse",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: AddStudentToCourseDto,
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/calendar/asf-course/:asfCourseUid",
    alias: "getASFCourse",
    requestFormat: "json",
    parameters: [
      {
        name: "asfCourseUid",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ASFCourseDto,
  },
  {
    method: "put",
    path: "/calendar/book_theory_lessons",
    alias: "bookTheoryLessons",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: BookTheoryLessonsDto,
      },
    ],
    response: z.void(),
  },
  {
    method: "delete",
    path: "/calendar/event/:eventId",
    alias: "deleteCalendarEvent",
    requestFormat: "json",
    parameters: [
      {
        name: "eventId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "reason",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.void(),
  },
  {
    method: "patch",
    path: "/calendar/event/:eventId/:studentId/bookedTrainings",
    alias: "updateTheoryLessonStudentBookedTrainings",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateTheoryLessonStudentBookedTrainingsDto,
      },
      {
        name: "eventId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "studentId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "put",
    path: "/calendar/event/:eventId/accept_invite",
    alias: "acceptInvite",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: AcceptInviteDto,
      },
      {
        name: "eventId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "patch",
    path: "/calendar/event/:eventId/attendance",
    alias: "updateAttendance",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateAttendanceDto,
      },
      {
        name: "eventId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "put",
    path: "/calendar/event/:eventId/cancel",
    alias: "cancelAppointment",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ cancelReason: z.string() }).partial(),
      },
      {
        name: "eventId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "put",
    path: "/calendar/event/:eventId/decline_invite",
    alias: "declineInvite",
    requestFormat: "json",
    parameters: [
      {
        name: "eventId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "patch",
    path: "/calendar/event/:eventId/exam-result",
    alias: "updateExamResult",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateExamResultDto,
      },
      {
        name: "eventId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "put",
    path: "/calendar/event/:id/trigger_payment",
    alias: "triggerPayment",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/calendar/events",
    alias: "getCalendarEvents",
    requestFormat: "json",
    parameters: [
      {
        name: "filter",
        type: "Query",
        schema: z.array(SingleFilter).optional(),
      },
      {
        name: "order",
        type: "Query",
        schema: z.array(SingleOrder).optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().optional(),
      },
    ],
    response: z.array(CalendarEventDto),
  },
  {
    method: "post",
    path: "/calendar/new-ASF-course",
    alias: "createASFCourse",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateASFCourseDto,
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/calendar/new-event",
    alias: "createCalendarEvent",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateCalendarEventDto,
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/calendar/new-events",
    alias: "createCalendarEvents",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.array(CreateCalendarEventDto),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/calendar/new-theory-course",
    alias: "createTheoryCourse",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateTheoryCourseDto,
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/calendar/remove-student-from-course",
    alias: "removeStudentFromCourse",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: RemoveStudentFromCourseDto,
      },
    ],
    response: z.void(),
  },
  {
    method: "patch",
    path: "/calendar/v3/event/:eventId",
    alias: "editEvent_v3",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: EditCalendarEventDto3,
      },
      {
        name: "eventId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/contract/create-contract",
    alias: "createContractForBookedTraining",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateContractForBookedTrainingDto,
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/contract/generate-pdf",
    alias: "generateContractPdf",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: GenerateContractPdfDto,
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/contract/preview/:id",
    alias: "previewPdf",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/contract/update-contracts/:id",
    alias: "updateContractsOfStudent",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/cost-bearers",
    alias: "listCostBearers",
    requestFormat: "json",
    parameters: [
      {
        name: "drivingSchoolId",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: ListCostBearerDto,
  },
  {
    method: "post",
    path: "/cost-bearers",
    alias: "createCostBearer",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateCostBearerDto,
      },
    ],
    response: CostBearerDto,
  },
  {
    method: "delete",
    path: "/cost-bearers/:id",
    alias: "softDeleteCostBearer",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/cost-bearers/:id",
    alias: "getCostBearer",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: CostBearerDto,
  },
  {
    method: "patch",
    path: "/cost-bearers/:id",
    alias: "updateCostBearer",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateCostBearerDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: CostBearerDto,
  },
  {
    method: "get",
    path: "/course-lessons/:id/attendance",
    alias: "getCourseLessonAttendances",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: CourseLessonAttendancesDto,
  },
  {
    method: "put",
    path: "/course-lessons/:id/attendance",
    alias: "setCourseLessonAttendances",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.record(CourseLessonAttendanceStatusEnum),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "put",
    path: "/course-lessons/:id/check-in",
    alias: "setCourseLessonAttendanceCheckIn",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CourseLessonCheckInDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/course-lessons/:id/start-check-in",
    alias: "startCourseLessonCheckIn",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/course-templates",
    alias: "findCourseTemplates",
    requestFormat: "json",
    parameters: [
      {
        name: "drivingSchoolId",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: z.array(CourseTemplateDto),
  },
  {
    method: "post",
    path: "/course-templates",
    alias: "createCourseTemplate",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateCourseTemplateDto,
      },
    ],
    response: CourseTemplateDto,
  },
  {
    method: "delete",
    path: "/course-templates/:id",
    alias: "deleteCourseTemplate",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/course-templates/:id",
    alias: "getCourseTemplate",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: CourseTemplateDto,
  },
  {
    method: "patch",
    path: "/course-templates/:id",
    alias: "updateCourseTemplate",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateCourseTemplateDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: CourseTemplateDto,
  },
  {
    method: "get",
    path: "/courses",
    alias: "findCourses",
    requestFormat: "json",
    parameters: [
      {
        name: "drivingSchoolId",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: z.array(CourseDto),
  },
  {
    method: "post",
    path: "/courses",
    alias: "createCourse",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateCourseDto,
      },
    ],
    response: CourseDto,
  },
  {
    method: "delete",
    path: "/courses/:id",
    alias: "deleteCourse",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/courses/:id",
    alias: "getCourse",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: CourseDto,
  },
  {
    method: "patch",
    path: "/courses/:id",
    alias: "updateCourse",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateCourseDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: CourseDto,
  },
  {
    method: "get",
    path: "/courses/:id/bundle",
    alias: "getOrCreateCourseBundle",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ id: z.string() }).passthrough(),
  },
  {
    method: "post",
    path: "/courses/:id/clone",
    alias: "cloneCourse",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CloneCourseDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: CourseDto,
  },
  {
    method: "get",
    path: "/courses/:id/participants",
    alias: "getCourseParticipants",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.array(CourseParticipantDto),
  },
  {
    method: "put",
    path: "/courses/:id/participants",
    alias: "enrollStudentInCourse",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: EnrollStudentInCourseDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/custom-cards",
    alias: "listCustomCards",
    requestFormat: "json",
    parameters: [
      {
        name: "drivingSchoolId",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: z.array(CustomCardDto),
  },
  {
    method: "post",
    path: "/custom-cards",
    alias: "createCustomCard",
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: createCustomCard_Body,
      },
    ],
    response: CustomCardDto,
  },
  {
    method: "delete",
    path: "/custom-cards/:id",
    alias: "deleteCustomCard",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/custom-cards/:id",
    alias: "getCustomCard",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: CustomCardDto,
  },
  {
    method: "patch",
    path: "/custom-cards/:id",
    alias: "updateCustomCard",
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: updateCustomCard_Body,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: CustomCardDto,
  },
  {
    method: "get",
    path: "/datapart/dtp/api/fahrschueler/:uuid",
    alias: "studentGet",
    description: `Schuelerstammdaten abfragen`,
    requestFormat: "json",
    parameters: [
      {
        name: "uuid",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 404,
        description: `Fahrschueler nicht gefunden`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/documents/ASF_Teilnahmebescheinigung/:studentId/:asfCourseId",
    alias: "createASFTeilnahmebescheinigung",
    requestFormat: "json",
    parameters: [
      {
        name: "studentId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "asfCourseId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "dataSnapshot",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "studentSignature",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "drivingSchoolManagerSignatureDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "drivingSchoolManagerSignatureLocation",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "drivingSchoolManagerSignature",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "instructorSignature",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/documents/Ausbildungsnachweis/:studentId/:drivingLicenseClass",
    alias: "createAusbildungsnachweis",
    requestFormat: "json",
    parameters: [
      {
        name: "studentId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "drivingLicenseClass",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "dataSnapshot",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "studentSignature",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "drivingSchoolManagerSignatureDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "drivingSchoolManagerSignatureLocation",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "drivingSchoolManagerSignature",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "instructorSignature",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "dateOfCreation",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "includePlannedAppointments",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/documents/B196_Teilnahmebescheinigung/:studentId",
    alias: "createB196Teilnahmebescheinigung",
    requestFormat: "json",
    parameters: [
      {
        name: "studentId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "dataSnapshot",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "studentSignature",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "drivingSchoolManagerSignatureDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "drivingSchoolManagerSignatureLocation",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "drivingSchoolManagerSignature",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "includePlannedAppointments",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/documents/B96_Teilnahmebescheinigung/:studentId",
    alias: "createB96Teilnahmebescheinigung",
    requestFormat: "json",
    parameters: [
      {
        name: "studentId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "dataSnapshot",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "studentSignature",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "drivingSchoolManagerSignatureDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "drivingSchoolManagerSignatureLocation",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "drivingSchoolManagerSignature",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "includePlannedAppointments",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/documents/Schaltnachweis/:studentId",
    alias: "createSchaltnachweis",
    requestFormat: "json",
    parameters: [
      {
        name: "studentId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "data",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "dataSnapshot",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "studentSignature",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "drivingSchoolManagerSignatureDate",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "drivingSchoolManagerSignatureLocation",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "drivingSchoolManagerSignature",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "instructorSignature",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "includePlannedAppointments",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/documents/Schaltnachweis/:studentId/data",
    alias: "getSchaltnachweisData",
    requestFormat: "json",
    parameters: [
      {
        name: "studentId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "includePlannedAppointments",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: SchaltnachweisDataDto,
  },
  {
    method: "get",
    path: "/documents/SGB_III_Bescheinigung_Fehlzeiten/:studentId/:year/:month",
    alias: "createSGBIIIBescheinigungFehlzeiten",
    requestFormat: "json",
    parameters: [
      {
        name: "studentId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "year",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "month",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "put",
    path: "/documents/sign",
    alias: "signDocuments",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: SignDocumentsDto,
      },
    ],
    response: SignDocumentsResultDto,
  },
  {
    method: "delete",
    path: "/documents/signature_request/:requestId",
    alias: "deleteSignatureRequest",
    requestFormat: "json",
    parameters: [
      {
        name: "requestId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/documents/signatureRequests",
    alias: "createSignatureRequest",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateSignatureRequestDto,
      },
    ],
    response: z.object({ id: z.string().min(1) }).passthrough(),
  },
  {
    method: "put",
    path: "/documents/signByStudent",
    alias: "signDocumentByStudent",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: SignByStudentDto,
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/driving-license-authority",
    alias: "listDrivingLicenseAuthorities",
    requestFormat: "json",
    parameters: [
      {
        name: "q",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filter",
        type: "Query",
        schema: z.array(SingleFilter).optional(),
      },
      {
        name: "order",
        type: "Query",
        schema: z.array(SingleOrder).optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().optional(),
      },
    ],
    response: z.array(DrivingLicenseAuthorityDto),
  },
  {
    method: "post",
    path: "/driving-license-authority",
    alias: "createDrivingLicenseAuthority",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateDrivingLicenseAuthorityDto,
      },
    ],
    response: DrivingLicenseAuthorityDto,
  },
  {
    method: "delete",
    path: "/driving-license-authority/:authorityId/assignedBranches/:branchId",
    alias: "unassignBranchFromDrivingLicenseAuthority",
    requestFormat: "json",
    parameters: [
      {
        name: "authorityId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "branchId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "put",
    path: "/driving-license-authority/:authorityId/assignedBranches/:branchId",
    alias: "assignBranchToDrivingLicenseAuthority",
    requestFormat: "json",
    parameters: [
      {
        name: "authorityId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "branchId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/driving-license-authority/:authorityId/forms",
    alias: "getDrivingLicenseAuthorityForms",
    requestFormat: "json",
    parameters: [
      {
        name: "authorityId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: DrivingLicenseAuthorityFormDto,
  },
  {
    method: "post",
    path: "/driving-license-authority/:authorityId/forms",
    alias: "createDrivingLicenseAuthorityForm",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateDrivingLicenseAuthorityFormDto,
      },
      {
        name: "authorityId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: DrivingLicenseAuthorityFormDto,
  },
  {
    method: "delete",
    path: "/driving-license-authority/:authorityId/forms/:formId",
    alias: "deleteDrivingLicenseAuthorityForm",
    requestFormat: "json",
    parameters: [
      {
        name: "authorityId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "formId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "patch",
    path: "/driving-license-authority/:authorityId/forms/:formId",
    alias: "updateDrivingLicenseAuthorityForm",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateDrivingLicenseAuthorityFormDto,
      },
      {
        name: "authorityId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "formId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: DrivingLicenseAuthorityFormDto,
  },
  {
    method: "post",
    path: "/driving-license-authority/:authorityId/forms/:formId/attach-pdf",
    alias: "attachPdfToForm",
    requestFormat: "form-data",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z
          .object({ pdf: z.instanceof(File) })
          .partial()
          .passthrough(),
      },
      {
        name: "authorityId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "formId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: DrivingLicenseAuthorityFormDto,
  },
  {
    method: "get",
    path: "/driving-license-authority/:authorityId/forms/:id",
    alias: "getDrivingLicenseAuthorityForm",
    requestFormat: "json",
    parameters: [
      {
        name: "authorityId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: DrivingLicenseAuthorityFormDto,
  },
  {
    method: "patch",
    path: "/driving-license-authority/:authorityId/instructions/:instructionId",
    alias: "updateDrivingLicenseAuthorityInstruction",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateDrivingLicenseAuthorityInstructionDto,
      },
      {
        name: "authorityId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "instructionId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/driving-license-authority/:id",
    alias: "getDrivingLicenseAuthority",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: DrivingLicenseAuthorityDto,
  },
  {
    method: "patch",
    path: "/driving-license-authority/:id",
    alias: "updateDrivingLicenseAuthority",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateDrivingLicenseAuthorityDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: DrivingLicenseAuthorityDto,
  },
  {
    method: "post",
    path: "/driving-license-authority/:id/instructions",
    alias: "createDrivingLicenseAuthorityInstruction",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateDrivingLicenseAuthorityInstructionDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/driving-school",
    alias: "createDrivingSchool",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateDrivingSchoolDto,
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/driving-school/:drivingSchoolId/exam-location",
    alias: "listExamLocations",
    requestFormat: "json",
    parameters: [
      {
        name: "drivingSchoolId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.array(ExamLocationDto),
  },
  {
    method: "post",
    path: "/driving-school/:drivingSchoolId/exam-location",
    alias: "createExamLocation",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateExamLocationDto,
      },
      {
        name: "drivingSchoolId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ExamLocationDto,
  },
  {
    method: "delete",
    path: "/driving-school/:drivingSchoolId/exam-location/:id",
    alias: "deleteExamLocation",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "drivingSchoolId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ExamLocationDto,
  },
  {
    method: "get",
    path: "/driving-school/:drivingSchoolId/exam-location/:id",
    alias: "getExamLocation",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "drivingSchoolId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ExamLocationDto,
  },
  {
    method: "patch",
    path: "/driving-school/:drivingSchoolId/exam-location/:id",
    alias: "updateExamLocation",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateExamLocationDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "drivingSchoolId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ExamLocationDto,
  },
  {
    method: "get",
    path: "/driving-school/:drivingSchoolId/label",
    alias: "listDrivingSchoolLabels",
    requestFormat: "json",
    parameters: [
      {
        name: "drivingSchoolId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ListDrivingSchoolLabelDto,
  },
  {
    method: "post",
    path: "/driving-school/:drivingSchoolId/label",
    alias: "createDrivingSchoolLabel",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateDrivingSchoolLabelDto,
      },
      {
        name: "drivingSchoolId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: DrivingSchoolLabelDto,
  },
  {
    method: "delete",
    path: "/driving-school/:drivingSchoolId/label/:id",
    alias: "deleteDrivingSchoolLabel",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "drivingSchoolId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: DrivingSchoolLabelDto,
  },
  {
    method: "get",
    path: "/driving-school/:drivingSchoolId/label/:id",
    alias: "getDrivingSchoolLabel",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "drivingSchoolId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: DrivingSchoolLabelDto,
  },
  {
    method: "patch",
    path: "/driving-school/:drivingSchoolId/label/:id",
    alias: "updateDrivingSchoolLabel",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateDrivingSchoolLabelDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "drivingSchoolId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: DrivingSchoolLabelDto,
  },
  {
    method: "get",
    path: "/driving-school/:drivingSchoolId/other-event-category",
    alias: "listOtherEventCategories",
    requestFormat: "json",
    parameters: [
      {
        name: "drivingSchoolId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ListOtherEventCategoryDto,
  },
  {
    method: "post",
    path: "/driving-school/:drivingSchoolId/other-event-category",
    alias: "createOtherEventCategory",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateOtherEventCategoryDto,
      },
      {
        name: "drivingSchoolId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: OtherEventCategoryDto,
  },
  {
    method: "delete",
    path: "/driving-school/:drivingSchoolId/other-event-category/:id",
    alias: "deleteOtherEventCategory",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "drivingSchoolId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/driving-school/:drivingSchoolId/other-event-category/:id",
    alias: "getOtherEventCategory",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "drivingSchoolId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: OtherEventCategoryDto,
  },
  {
    method: "patch",
    path: "/driving-school/:drivingSchoolId/other-event-category/:id",
    alias: "updateOtherEventCategory",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateOtherEventCategoryDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "drivingSchoolId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: OtherEventCategoryDto,
  },
  {
    method: "get",
    path: "/driving-school/:drivingSchoolId/room",
    alias: "getRooms",
    requestFormat: "json",
    parameters: [
      {
        name: "drivingSchoolId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "branchId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "withDeleted",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.array(RoomDto),
  },
  {
    method: "post",
    path: "/driving-school/:drivingSchoolId/room",
    alias: "createRoom",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateRoomDto,
      },
      {
        name: "drivingSchoolId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: RoomDto,
  },
  {
    method: "delete",
    path: "/driving-school/:drivingSchoolId/room/:id",
    alias: "deleteRoom",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "drivingSchoolId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/driving-school/:drivingSchoolId/room/:id",
    alias: "getRoom",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "drivingSchoolId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: RoomDto,
  },
  {
    method: "patch",
    path: "/driving-school/:drivingSchoolId/room/:id",
    alias: "updateRoom",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateRoomDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "drivingSchoolId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: RoomDto,
  },
  {
    method: "get",
    path: "/driving-school/:drivingSchoolId/stats/overview",
    alias: "getOverviewStats",
    requestFormat: "json",
    parameters: [
      {
        name: "drivingSchoolId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: StatsOverviewDto,
  },
  {
    method: "get",
    path: "/driving-school/:drivingSchoolId/stats/students-signups",
    alias: "getStudentsSignupsStats",
    requestFormat: "json",
    parameters: [
      {
        name: "drivingSchoolId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "branchId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "groupBy",
        type: "Query",
        schema: z.enum(["day", "month", "drivingLicenseClass"]).optional(),
      },
      {
        name: "to",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "from",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "month",
        type: "Query",
        schema: z
          .string()
          .regex(/^\d{4}-\d{2}$/)
          .optional(),
      },
    ],
    response: StudentSignupStatsDto,
  },
  {
    method: "get",
    path: "/driving-school/:id",
    alias: "getDrivingSchool",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: DrivingSchoolSchemaDto,
  },
  {
    method: "patch",
    path: "/driving-school/:id",
    alias: "updateDrivingSchool",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateDrivingSchoolDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: DrivingSchoolSchemaDto,
  },
  {
    method: "get",
    path: "/driving-school/:id/auto-forms",
    alias: "getDrivingSchoolAutoForms",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "includeDeleted",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.array(DrivingSchoolAutoFormDto),
  },
  {
    method: "get",
    path: "/driving-school/:id/employees",
    alias: "getDrivingSchoolEmployees",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.array(EmployeeDto),
  },
  {
    method: "get",
    path: "/driving-school/:id/service-types",
    alias: "getDrivingSchoolServiceTypes",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.array(ServiceTypeDto),
  },
  {
    method: "get",
    path: "/driving-school/:id/signup-auto-forms",
    alias: "getSignupDrivingSchoolAutoForms",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.array(SignupDrivingSchoolAutoFormDto),
  },
  {
    method: "post",
    path: "/dunning/block-student-and-send-payment-reminder",
    alias: "blockStudentAndSendPaymentReminder",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: BlockStudentAndSendPaymentReminderRequest,
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/dunning/first-dunning-notice",
    alias: "sendFirstDunningNotice",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: SendDunningNoticeRequest,
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/dunning/handover-to-PAIR-Finance",
    alias: "handoverToPairFinance",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: SendDunningNoticeRequest,
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/dunning/sammelquittung",
    alias: "sendSammelquittungEmail",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: SendSammelquittungEmailDto,
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/dunning/second-dunning-notice",
    alias: "sendSecondDunningNotice",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: SendDunningNoticeRequest,
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/dunning/warning",
    alias: "sendWarning",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: SendDunningNoticeRequest,
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/employee",
    alias: "createEmployee",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateEmployeeDto,
      },
    ],
    response: EmployeeDto,
  },
  {
    method: "get",
    path: "/employee/:id",
    alias: "getEmployee",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: EmployeeDto,
  },
  {
    method: "patch",
    path: "/employee/:id",
    alias: "updateEmployee",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateEmployeeDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: EmployeeDto,
  },
  {
    method: "get",
    path: "/exam-authority-fees",
    alias: "getAllExamAuthorityFees",
    requestFormat: "json",
    response: z.array(ExamAuthorityFeeDto),
  },
  {
    method: "post",
    path: "/exam-authority-fees",
    alias: "createExamAuthorityFee",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateExamAuthorityFeeDto,
      },
    ],
    response: ExamAuthorityFeeDto,
  },
  {
    method: "get",
    path: "/exam-authority-fees/:id",
    alias: "getExamAuthorityFee",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ExamAuthorityFeeDto,
  },
  {
    method: "patch",
    path: "/exam-authority-fees/:id",
    alias: "updateExamAuthorityFee",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateExamAuthorityFeeDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ExamAuthorityFeeDto,
  },
  {
    method: "post",
    path: "/hubspot-webhook",
    alias: "handleHubspotWebhook[1]",
    requestFormat: "json",
    response: z.void(),
  },
  {
    method: "get",
    path: "/hubspot/oauth2/redirect",
    alias: "handleOAuthRedirect[0]",
    requestFormat: "json",
    parameters: [
      {
        name: "code",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/hubspot/webhook",
    alias: "handleHubspotWebhook[0]",
    requestFormat: "json",
    response: z.void(),
  },
  {
    method: "post",
    path: "/instructor",
    alias: "createInstructor",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateInstructorDto,
      },
    ],
    response: InstructorDto,
  },
  {
    method: "patch",
    path: "/instructor/:id",
    alias: "updateInstructor",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateInstructorDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: InstructorDto,
  },
  {
    method: "get",
    path: "/instructor/:id/calendar-events-report.pdf",
    alias: "getInstructorCalendarEventsReportPdf",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "includePlannedAppointments",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "from",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "to",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/instructor/:id/calendar-events-report.xlsx",
    alias: "getInstructorCalendarEventsReportExcel",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "includePlannedAppointments",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "from",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "to",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/instructor/:id/recommendation-settings",
    alias: "getInstructorRecommendationSettings",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: RecommendationSettingsDto,
  },
  {
    method: "put",
    path: "/instructor/:id/recommendation-settings",
    alias: "setInstructorRecommendationSettings",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: RecommendationSettingsDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "patch",
    path: "/instructor/:id/signature-settings",
    alias: "updateSignatureSettings",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateSignatureSettingsDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/instructor/:id/work-times",
    alias: "getInstructorWorkTimes",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: WorkTimesDto,
  },
  {
    method: "put",
    path: "/instructor/:id/work-times",
    alias: "setInstructorWorkTimes",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: WorkTimesDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/instructor/:id/worktime-aggregation.pdf",
    alias: "getInstructorWorktimeAggregationPdf",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "includePlannedAppointments",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "from",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "to",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/instructor/:id/worktime-aggregation.xlsx",
    alias: "getInstructorWorktimeAggregationExcel",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "includePlannedAppointments",
        type: "Query",
        schema: z.boolean().optional(),
      },
      {
        name: "from",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "to",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/instructor/:id/worktime-stats",
    alias: "getInstructorWorktimeStats",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "from",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "to",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: WorktimeStatsDto,
  },
  {
    method: "get",
    path: "/instructor/v2/:id/worktime-stats",
    alias: "getInstructorWorktimeStats_v2",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "from",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "to",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: WorktimeStatsDto2,
  },
  {
    method: "get",
    path: "/notes",
    alias: "listNotes",
    requestFormat: "json",
    parameters: [
      {
        name: "filter",
        type: "Query",
        schema: filter,
      },
    ],
    response: z.void(),
  },
  {
    method: "patch",
    path: "/notes",
    alias: "updateNote",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateNoteDto,
      },
    ],
    response: NoteDto,
  },
  {
    method: "post",
    path: "/notes",
    alias: "createNote",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateNoteDto,
      },
    ],
    response: NoteDto,
  },
  {
    method: "delete",
    path: "/notes/:id",
    alias: "deleteNote",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "type",
        type: "Query",
        schema: z.enum(["DRIVING_SCHOOL", "STUDENT"]),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/notifications",
    alias: "createNotification",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateNotificationDto,
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/oauth2/redirect",
    alias: "handleOAuthRedirect[1]",
    requestFormat: "json",
    parameters: [
      {
        name: "code",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/payment/pay-base-fee",
    alias: "payBaseFee",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: _PayBaseFeeRequestDto,
      },
    ],
    response: _PayBaseFeeResponseDto,
  },
  {
    method: "get",
    path: "/posts",
    alias: "listPosts",
    requestFormat: "json",
    parameters: [
      {
        name: "filter",
        type: "Query",
        schema: z.array(SingleFilter).optional(),
      },
      {
        name: "order",
        type: "Query",
        schema: z.array(SingleOrder).optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().optional(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/posts",
    alias: "createPost",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreatePostDto,
      },
    ],
    response: PostDto,
  },
  {
    method: "delete",
    path: "/posts/:id",
    alias: "deletePost",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "patch",
    path: "/posts/:id",
    alias: "updatePost",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ body: z.string().nullable() }).partial().passthrough(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: PostDto,
  },
  {
    method: "get",
    path: "/promo-codes",
    alias: "listPromoCodes",
    requestFormat: "json",
    parameters: [
      {
        name: "range",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "sort",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "filter",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.array(PromoCodeDto),
  },
  {
    method: "post",
    path: "/promo-codes",
    alias: "createPromoCode",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreatePromoCodeDto,
      },
    ],
    response: PromoCodeDto,
  },
  {
    method: "get",
    path: "/promo-codes/:id",
    alias: "getPromoCode",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: PromoCodeDto,
  },
  {
    method: "put",
    path: "/promo-codes/:id",
    alias: "updatePromoCode",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdatePromoCodeDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: PromoCodeDto,
  },
  {
    method: "post",
    path: "/promo-codes/reserve",
    alias: "reservePromoCode",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ReservePromoCodeRequest,
      },
    ],
    response: ReservePromoCodeResponse,
    errors: [
      {
        status: 404,
        description: `PromoCode not found`,
        schema: z.void(),
      },
      {
        status: 409,
        schema: z.union([PromoCodeExpiredErrorDto, TooManyUsagesErrorDto]),
      },
    ],
  },
  {
    method: "get",
    path: "/public-instructor/:id",
    alias: "getPublicInstructor",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: PublicInstructorDto,
  },
  {
    method: "get",
    path: "/pulse/ai/admin/prompts",
    alias: "getPulseAiPromptBriefings",
    requestFormat: "json",
    response: AiPromptBriefingsItemDto,
  },
  {
    method: "put",
    path: "/pulse/ai/admin/prompts",
    alias: "updatePulseAiPromptBriefings",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: AiPromptBriefingsUpdateDto,
      },
    ],
    response: AiPromptBriefingsItemDto,
  },
  {
    method: "get",
    path: "/pulse/ai/admin/provider",
    alias: "getPulseAiProviderSettings",
    requestFormat: "json",
    response: AiAdminProviderSettingsItemDto,
  },
  {
    method: "put",
    path: "/pulse/ai/admin/provider",
    alias: "updatePulseAiProviderSettings",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: AiAdminProviderSettingsUpdateDto,
      },
    ],
    response: AiAdminProviderSettingsItemDto,
  },
  {
    method: "get",
    path: "/pulse/ai/admin/session-logs",
    alias: "listPulseAiSessionLogs",
    requestFormat: "json",
    parameters: [
      {
        name: "sessionId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "onlyErrors",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "eventType",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: AiSessionLogsResponseDto,
  },
  {
    method: "get",
    path: "/pulse/ai/admin/usage-controls",
    alias: "getPulseAiUsageControls",
    requestFormat: "json",
    response: AiUsageControlsItemDto,
  },
  {
    method: "put",
    path: "/pulse/ai/admin/usage-controls",
    alias: "updatePulseAiUsageControls",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: AiUsageControlsUpdateDto,
      },
    ],
    response: AiUsageControlsItemDto,
  },
  {
    method: "get",
    path: "/pulse/ai/admin/usage-events",
    alias: "listPulseAiUsageEvents",
    requestFormat: "json",
    response: AiUsageEventsResponseDto,
  },
  {
    method: "delete",
    path: "/pulse/ai/admin/warehouse-schema-catalog",
    alias: "deletePulseAiWarehouseSchemaCatalogFile",
    requestFormat: "json",
    parameters: [
      {
        name: "filename",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: AiWarehouseSchemaCatalogResponseDto,
  },
  {
    method: "get",
    path: "/pulse/ai/admin/warehouse-schema-catalog",
    alias: "listPulseAiWarehouseSchemaCatalogFiles",
    requestFormat: "json",
    response: AiWarehouseSchemaCatalogResponseDto,
  },
  {
    method: "post",
    path: "/pulse/ai/admin/warehouse-schema-catalog",
    alias: "uploadPulseAiWarehouseSchemaCatalogFile",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: AiWarehouseSchemaCatalogUploadDto,
      },
    ],
    response: AiWarehouseSchemaCatalogResponseDto,
  },
  {
    method: "get",
    path: "/pulse/analyses",
    alias: "listPulseAnalyses",
    requestFormat: "json",
    response: PulseAnalysisListDto,
  },
  {
    method: "post",
    path: "/pulse/analyses",
    alias: "createPulseAnalysis",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PulseAnalysisDefinitionDto,
      },
    ],
    response: PulseAnalysisDefinitionDto,
  },
  {
    method: "delete",
    path: "/pulse/analyses/:id",
    alias: "deletePulseAnalysis",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ ok: z.boolean() }).passthrough(),
  },
  {
    method: "get",
    path: "/pulse/analyses/:id",
    alias: "getPulseAnalysis",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: PulseAnalysisDefinitionDto,
  },
  {
    method: "put",
    path: "/pulse/analyses/:id",
    alias: "updatePulseAnalysis",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PulseAnalysisDefinitionDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: PulseAnalysisDefinitionDto,
  },
  {
    method: "get",
    path: "/pulse/dashboards",
    alias: "listPulseDashboards",
    requestFormat: "json",
    response: PulseDashboardListDto,
  },
  {
    method: "post",
    path: "/pulse/dashboards",
    alias: "createPulseDashboard",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PulseDashboardDefinitionDto,
      },
    ],
    response: PulseDashboardDefinitionDto,
  },
  {
    method: "delete",
    path: "/pulse/dashboards/:id",
    alias: "deletePulseDashboard",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ ok: z.boolean() }).passthrough(),
  },
  {
    method: "get",
    path: "/pulse/dashboards/:id",
    alias: "getPulseDashboard",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: PulseDashboardDefinitionDto,
  },
  {
    method: "put",
    path: "/pulse/dashboards/:id",
    alias: "updatePulseDashboard",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PulseDashboardDefinitionDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: PulseDashboardDefinitionDto,
  },
  {
    method: "get",
    path: "/pulse/datasource-datasets",
    alias: "listPulseDatasourceDatasets",
    requestFormat: "json",
    parameters: [
      {
        name: "datasourceId",
        type: "Query",
        schema: z.string().min(1).optional(),
      },
      {
        name: "project",
        type: "Query",
        schema: z.string().min(1).optional(),
      },
    ],
    response: PulseDatasourceDatasetsResponseDto,
  },
  {
    method: "get",
    path: "/pulse/datasource-field-uniques",
    alias: "listPulseDatasourceFieldUniques",
    requestFormat: "json",
    parameters: [
      {
        name: "datasourceId",
        type: "Query",
        schema: z.string().min(1),
      },
      {
        name: "fieldId",
        type: "Query",
        schema: z.string().min(1),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.string().regex(/^\d+$/).optional(),
      },
      {
        name: "search",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: PulseDatasourceFieldUniquesResponseDto,
  },
  {
    method: "post",
    path: "/pulse/datasource-field-uniques",
    alias: "listInlinePulseDatasourceFieldUniques",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PulseDatasourceFieldUniquesBodyDto,
      },
    ],
    response: PulseDatasourceFieldUniquesResponseDto,
  },
  {
    method: "get",
    path: "/pulse/datasource-introspect-schema",
    alias: "introspectPulseDatasourceSchema",
    requestFormat: "json",
    parameters: [
      {
        name: "datasourceId",
        type: "Query",
        schema: z.string().min(1).optional(),
      },
      {
        name: "project",
        type: "Query",
        schema: z.string().min(1).optional(),
      },
      {
        name: "dataset",
        type: "Query",
        schema: z.string().min(1),
      },
      {
        name: "object",
        type: "Query",
        schema: z.string().min(1),
      },
    ],
    response: PulseDatasourceIntrospectSchemaResponseDto,
  },
  {
    method: "get",
    path: "/pulse/datasource-preview",
    alias: "previewPulseDatasource",
    requestFormat: "json",
    parameters: [
      {
        name: "datasourceId",
        type: "Query",
        schema: z.string().min(1),
      },
      {
        name: "page",
        type: "Query",
        schema: z.string().regex(/^\d+$/).optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.string().regex(/^\d+$/).optional(),
      },
      {
        name: "columns",
        type: "Query",
        schema: z.string().min(1).optional(),
      },
      {
        name: "dimensions",
        type: "Query",
        schema: z.string().min(1).optional(),
      },
      {
        name: "measures",
        type: "Query",
        schema: z.string().min(1).optional(),
      },
      {
        name: "filters",
        type: "Query",
        schema: z.string().min(1).optional(),
      },
      {
        name: "sort",
        type: "Query",
        schema: z.string().min(1).optional(),
      },
      {
        name: "cacheMode",
        type: "Query",
        schema: z.enum(["cache", "reload"]).optional(),
      },
    ],
    response: PulseDatasourcePreviewResponseDto,
  },
  {
    method: "post",
    path: "/pulse/datasource-preview",
    alias: "previewInlinePulseDatasource",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PulseDatasourcePreviewBodyDto,
      },
    ],
    response: PulseDatasourcePreviewResponseDto,
  },
  {
    method: "get",
    path: "/pulse/datasource-projects",
    alias: "listPulseDatasourceProjects",
    requestFormat: "json",
    parameters: [
      {
        name: "datasourceId",
        type: "Query",
        schema: z.string().min(1).optional(),
      },
    ],
    response: PulseDatasourceProjectsResponseDto,
  },
  {
    method: "get",
    path: "/pulse/datasource-source-objects",
    alias: "listPulseDatasourceSourceObjects",
    requestFormat: "json",
    parameters: [
      {
        name: "datasourceId",
        type: "Query",
        schema: z.string().min(1).optional(),
      },
      {
        name: "project",
        type: "Query",
        schema: z.string().min(1).optional(),
      },
      {
        name: "dataset",
        type: "Query",
        schema: z.string().min(1),
      },
    ],
    response: PulseDatasourceSourceObjectsResponseDto,
  },
  {
    method: "get",
    path: "/pulse/datasources",
    alias: "listPulseDatasources",
    requestFormat: "json",
    response: PulseDatasourceListDto,
  },
  {
    method: "post",
    path: "/pulse/datasources",
    alias: "createPulseDatasource",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PulseDatasourceDefinitionDto,
      },
    ],
    response: PulseDatasourceDefinitionDto,
  },
  {
    method: "delete",
    path: "/pulse/datasources/:id",
    alias: "deletePulseDatasource",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ ok: z.boolean() }).passthrough(),
  },
  {
    method: "get",
    path: "/pulse/datasources/:id",
    alias: "getPulseDatasource",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: PulseDatasourceDefinitionDto,
  },
  {
    method: "put",
    path: "/pulse/datasources/:id",
    alias: "updatePulseDatasource",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PulseDatasourceDefinitionDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: PulseDatasourceDefinitionDto,
  },
  {
    method: "delete",
    path: "/pulse/grants",
    alias: "deletePulseGrantConfigurationItem",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PulseGrantConfigDeleteDto,
      },
    ],
    response: PulseGrantConfigurationDto,
  },
  {
    method: "get",
    path: "/pulse/grants",
    alias: "getPulseGrantConfiguration",
    requestFormat: "json",
    response: PulseGrantConfigurationDto,
  },
  {
    method: "put",
    path: "/pulse/grants",
    alias: "upsertPulseGrantConfigurationItem",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: upsertPulseGrantConfigurationItem_Body,
      },
    ],
    response: PulseGrantConfigurationDto,
  },
  {
    method: "get",
    path: "/pulse/metrics",
    alias: "listPulseMetrics",
    requestFormat: "json",
    response: PulseMetricListDto,
  },
  {
    method: "post",
    path: "/pulse/metrics",
    alias: "createPulseMetric",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PulseMetricDefinitionDto,
      },
    ],
    response: PulseMetricDefinitionDto,
  },
  {
    method: "delete",
    path: "/pulse/metrics/:id",
    alias: "deletePulseMetric",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ ok: z.boolean() }).passthrough(),
  },
  {
    method: "get",
    path: "/pulse/metrics/:id",
    alias: "getPulseMetric",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: PulseMetricDefinitionDto,
  },
  {
    method: "put",
    path: "/pulse/metrics/:id",
    alias: "updatePulseMetric",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PulseMetricDefinitionDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: PulseMetricDefinitionDto,
  },
  {
    method: "get",
    path: "/pulse/themes",
    alias: "listPulseThemes",
    requestFormat: "json",
    response: PulseThemeListDto,
  },
  {
    method: "post",
    path: "/pulse/themes",
    alias: "createPulseTheme",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PulseThemeDefinitionDto,
      },
    ],
    response: PulseThemeDefinitionDto,
  },
  {
    method: "delete",
    path: "/pulse/themes/:id",
    alias: "deletePulseTheme",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.object({ ok: z.boolean() }).passthrough(),
  },
  {
    method: "get",
    path: "/pulse/themes/:id",
    alias: "getPulseTheme",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: PulseThemeDefinitionDto,
  },
  {
    method: "put",
    path: "/pulse/themes/:id",
    alias: "updatePulseTheme",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PulseThemeDefinitionDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: PulseThemeDefinitionDto,
  },
  {
    method: "get",
    path: "/recommendations/simulatorDrivingLesson",
    alias: "getSimulatorDrivingLessonRecommendations",
    requestFormat: "json",
    parameters: [
      {
        name: "studentId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "simulatorIds",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "from",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "to",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "duration",
        type: "Query",
        schema: z.string().optional().default("PT45M"),
      },
    ],
    response: z.array(RecommendationDto),
  },
  {
    method: "get",
    path: "/referral-code-applications",
    alias: "listReferralCodeApplications",
    requestFormat: "json",
    parameters: [
      {
        name: "code",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: z.array(ReferralCodeApplicationDto),
  },
  {
    method: "get",
    path: "/referral-code-usage/:id",
    alias: "getReferralCodeUsage",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ReferralCodeUsageDto,
  },
  {
    method: "post",
    path: "/rewards/claim",
    alias: "claimReward",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ClaimRewardRequestBody,
      },
    ],
    response: ClaimRewardResponseBody,
  },
  {
    method: "post",
    path: "/rewards/gift-voucher",
    alias: "createGiftVoucher",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateGiftVoucherRequestBody,
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/rewards/gift-vouchers",
    alias: "findGiftVouchers",
    requestFormat: "json",
    parameters: [
      {
        name: "filter",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "sort",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "range",
        type: "Query",
        schema: z.string().optional(),
      },
    ],
    response: z.array(GiftVoucherDto),
  },
  {
    method: "get",
    path: "/search",
    alias: "search",
    requestFormat: "json",
    parameters: [
      {
        name: "query",
        type: "Query",
        schema: z.string().min(1),
      },
      {
        name: "includes",
        type: "Query",
        schema: z.array(z.unknown()).optional().default(["students", "instructors", "drivingSchools"]),
      },
    ],
    response: SearchResponseDto,
  },
  {
    method: "get",
    path: "/service-entries",
    alias: "listServiceEntries",
    requestFormat: "json",
    parameters: [
      {
        name: "filter",
        type: "Query",
        schema: z.array(SingleFilter).optional(),
      },
      {
        name: "order",
        type: "Query",
        schema: z.array(SingleOrder).optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().optional(),
      },
    ],
    response: ListServiceEntryDto,
  },
  {
    method: "post",
    path: "/service-entries",
    alias: "createServiceEntry",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateServiceEntryDto,
      },
    ],
    response: ServiceEntryDto,
  },
  {
    method: "delete",
    path: "/service-entries/:id",
    alias: "cancelServiceEntry",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/service-entries/:id",
    alias: "getServiceEntry",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ServiceEntryDto,
  },
  {
    method: "patch",
    path: "/service-entries/:id",
    alias: "updateServiceEntry",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateServiceEntryDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ServiceEntryDto,
  },
  {
    method: "post",
    path: "/service-types",
    alias: "createServiceType",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateServiceTypeDto,
      },
    ],
    response: ServiceTypeDto,
  },
  {
    method: "delete",
    path: "/service-types/:id",
    alias: "softDeleteServiceType",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/service-types/:id",
    alias: "getServiceType",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ServiceTypeDto,
  },
  {
    method: "patch",
    path: "/service-types/:id",
    alias: "updateServiceType",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateServiceTypeDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: ServiceTypeDto,
  },
  {
    method: "patch",
    path: "/sign-up-data",
    alias: "updateSignUpData",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: SignUpDataUpdateDto,
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/sign-up-data/:id",
    alias: "getSignUpData",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: SignUpData,
  },
  {
    method: "get",
    path: "/students",
    alias: "listStudents",
    requestFormat: "json",
    parameters: [
      {
        name: "filter",
        type: "Query",
        schema: z.array(SingleFilter).optional(),
      },
      {
        name: "order",
        type: "Query",
        schema: z.array(SingleOrder).optional(),
      },
      {
        name: "offset",
        type: "Query",
        schema: z.number().optional(),
      },
      {
        name: "limit",
        type: "Query",
        schema: z.number().optional(),
      },
    ],
    response: z.array(StudentDto),
  },
  {
    method: "post",
    path: "/students",
    alias: "createStudent",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateStudentDto,
      },
    ],
    response: z.void(),
  },
  {
    method: "patch",
    path: "/students/:id",
    alias: "updateStudent",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateStudentDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: StudentDto,
  },
  {
    method: "get",
    path: "/students/:id/activities",
    alias: "listStudentActivities",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "range",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "sort",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "filter",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: z.array(StudentActivityDto),
  },
  {
    method: "post",
    path: "/students/:id/attachments",
    alias: "addStudentAttachment",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: AddAttachmentDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "delete",
    path: "/students/:id/attachments/:attachmentId",
    alias: "deleteStudentAttachment",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "attachmentId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "put",
    path: "/students/:id/block",
    alias: "blockStudent",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ reason: z.string().min(1) }),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/students/:id/booked-trainings/:bookedTrainingId/cancel",
    alias: "cancelBookedTraining",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ cancelReason: z.string().min(1).nullable() }).partial(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "bookedTrainingId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 403,
        description: `Forbidden – not allowed to cancel this student&#x27;s booked training`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found – student or booked training not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "post",
    path: "/students/:id/booked-trainings/:bookedTrainingId/reactivate",
    alias: "reactivateBookedTraining",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "bookedTrainingId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 403,
        description: `Forbidden - not allowed to reactivate this student&#x27;s booked training`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not found - student or booked training not found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/students/:id/courses",
    alias: "getStudentCourses",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.array(StudentCourseDto),
  },
  {
    method: "delete",
    path: "/students/:id/documents/:documentName",
    alias: "deleteStudentDocument",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "documentName",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "drivingSchoolId",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/students/:id/external-training-progress",
    alias: "listExternalTrainingProgress",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.array(ExternalTrainingProgressDto),
  },
  {
    method: "delete",
    path: "/students/:id/external-training-progress/:drivingLicenseClass",
    alias: "deleteExternalTrainingProgress",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "drivingLicenseClass",
        type: "Path",
        schema: z.enum([
          "A",
          "A1",
          "A2",
          "A80",
          "AM",
          "B",
          "B196",
          "B197",
          "B78",
          "B96",
          "BE",
          "C",
          "C1",
          "C1E",
          "CE",
          "D",
          "D1",
          "D1E",
          "DE",
          "L",
          "M",
          "T",
        ]),
      },
    ],
    response: z.void(),
  },
  {
    method: "patch",
    path: "/students/:id/external-training-progress/:drivingLicenseClass",
    alias: "updateExternalTrainingProgress",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateExternalTrainingProgressDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "drivingLicenseClass",
        type: "Path",
        schema: z.enum([
          "A",
          "A1",
          "A2",
          "A80",
          "AM",
          "B",
          "B196",
          "B197",
          "B78",
          "B96",
          "BE",
          "C",
          "C1",
          "C1E",
          "CE",
          "D",
          "D1",
          "D1E",
          "DE",
          "L",
          "M",
          "T",
        ]),
      },
    ],
    response: ExternalTrainingProgressDto,
  },
  {
    method: "post",
    path: "/students/:id/external-training-progress/:drivingLicenseClass",
    alias: "createExternalTrainingProgress",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateExternalTrainingProgressDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "drivingLicenseClass",
        type: "Path",
        schema: z.enum([
          "A",
          "A1",
          "A2",
          "A80",
          "AM",
          "B",
          "B196",
          "B197",
          "B78",
          "B96",
          "BE",
          "C",
          "C1",
          "C1E",
          "CE",
          "D",
          "D1",
          "D1E",
          "DE",
          "L",
          "M",
          "T",
        ]),
      },
    ],
    response: ExternalTrainingProgressDto,
  },
  {
    method: "get",
    path: "/students/:id/filled-out-form/:formId",
    alias: "fillOutForm",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "formId",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "quoteId",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "withoutBackground",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.void(),
  },
  {
    method: "put",
    path: "/students/:id/unblock",
    alias: "unblockStudent",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ reason: z.string().min(1) }),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "put",
    path: "/students/:studentId/changePrices",
    alias: "changePrices",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ChangePricesDto,
      },
      {
        name: "studentId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "put",
    path: "/students/:studentId/subscription",
    alias: "updateStudentSubscription",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateStudentSubscriptionDto,
      },
      {
        name: "studentId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/subscription-package",
    alias: "listSubscriptionPackages",
    requestFormat: "json",
    parameters: [
      {
        name: "drivingSchoolId",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: z.array(SubscriptionPackageDto),
  },
  {
    method: "post",
    path: "/subscription-package",
    alias: "createSubscriptionPackage",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateSubscriptionPackageDto,
      },
    ],
    response: SubscriptionPackageDto,
  },
  {
    method: "delete",
    path: "/subscription-package/:id",
    alias: "deleteSubscriptionPackage",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/subscription-package/:id",
    alias: "getSubscriptionPackage",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: SubscriptionPackageDto,
  },
  {
    method: "patch",
    path: "/subscription-package/:id",
    alias: "updateSubscriptionPackage",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateSubscriptionPackageDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: SubscriptionPackageDto,
  },
  {
    method: "get",
    path: "/subscription-package/public",
    alias: "public_listSubscriptionPackages",
    requestFormat: "json",
    parameters: [
      {
        name: "drivingSchoolId",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: z.array(PublicSubscriptionPackageDto),
  },
  {
    method: "get",
    path: "/subscription-package/public/:id",
    alias: "public_getSubscriptionPackage",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: PublicSubscriptionPackageDto,
  },
  {
    method: "post",
    path: "/tableau/jwt",
    alias: "getTableauJwt",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ drivingSchoolUid: z.string() }).partial().passthrough(),
      },
    ],
    response: z.object({ jwt: z.string() }).passthrough(),
  },
  {
    method: "get",
    path: "/timetables",
    alias: "findTimetables",
    requestFormat: "json",
    parameters: [
      {
        name: "drivingSchoolId",
        type: "Query",
        schema: z.string(),
      },
    ],
    response: z.array(TimetableDto),
  },
  {
    method: "post",
    path: "/timetables",
    alias: "createTimetable",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateTimetableDto,
      },
    ],
    response: TimetableDto,
  },
  {
    method: "delete",
    path: "/timetables/:id",
    alias: "deleteTimetable",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/timetables/:id",
    alias: "getTimetable",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: TimetableDto,
  },
  {
    method: "patch",
    path: "/timetables/:id",
    alias: "updateTimetable",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateTimetableDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: TimetableDto,
  },
  {
    method: "get",
    path: "/user/:id",
    alias: "getUser_deprecated",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: UserDto_deprecated,
  },
  {
    method: "patch",
    path: "/user/:id",
    alias: "updateUser",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchUserDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: UserDto_deprecated,
  },
  {
    method: "post",
    path: "/user/:id/disable",
    alias: "disableUser",
    description: `Disables a user in Firebase Auth, revokes all refresh tokens, and updates the user status in both Firebase Auth and Postgres databases.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/user/:id/enable",
    alias: "enableUser",
    description: `Enables a user in Firebase Auth and updates the user status in both Firebase Auth and Postgres databases.`,
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "patch",
    path: "/user/:id/grants",
    alias: "updateGrants",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateUserGrantsDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/user/:id/update-auth-email",
    alias: "updateAuthEmail",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ email: z.string().email() }).passthrough(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "post",
    path: "/user/send-reset-password-email",
    alias: "sendPasswordResetEmail",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ email: z.string().email() }).passthrough(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/user/v2/:id",
    alias: "getUser",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: UserDto,
  },
  {
    method: "get",
    path: "/utils/diff",
    alias: "diffUtil",
    requestFormat: "json",
    response: z.void(),
  },
  {
    method: "get",
    path: "/utils/geo-coordinates",
    alias: "getGeoCoordinates",
    requestFormat: "json",
    parameters: [
      {
        name: "street",
        type: "Query",
        schema: z.string().optional(),
      },
      {
        name: "postalCode",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "city",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "country",
        type: "Query",
        schema: z.string().optional().default("DE"),
      },
    ],
    response: GeoCoordinatesDto,
  },
  {
    method: "get",
    path: "/vehicles",
    alias: "findVehicles",
    requestFormat: "json",
    parameters: [
      {
        name: "drivingSchoolId",
        type: "Query",
        schema: z.string(),
      },
      {
        name: "withDeleted",
        type: "Query",
        schema: z.boolean().optional(),
      },
    ],
    response: z.array(VehicleDto),
  },
  {
    method: "post",
    path: "/vehicles",
    alias: "createVehicle",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateVehicleDto,
      },
    ],
    response: VehicleDto,
  },
  {
    method: "delete",
    path: "/vehicles/:id",
    alias: "deleteVehicle",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "get",
    path: "/vehicles/:id",
    alias: "getVehicle",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: VehicleDto,
  },
  {
    method: "patch",
    path: "/vehicles/:id",
    alias: "updateVehicle",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateVehicleDto,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: VehicleDto,
  },
  {
    method: "patch",
    path: "/vehicles/:id/undelete",
    alias: "undeleteVehicle",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
  },
  {
    method: "put",
    path: "/vogel/fahrschueler",
    alias: "getVogelFahrschueler",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: GetVogelFahrschuelerDto,
      },
    ],
    response: z.array(VogelFahrschuelerDto),
  },
  {
    method: "put",
    path: "/vogel/login",
    alias: "vogelLogin",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: VogelLoginDto,
      },
    ],
    response: VogelFahrschulmanagerContext,
  },
  {
    method: "post",
    path: "/webapp/book-quote",
    alias: "bookQuote",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: BookQuoteDto,
      },
    ],
    response: PayBaseFeeResponseDto,
    errors: [
      {
        status: 461,
        description: `Quote already booked`,
        schema: z.void(),
      },
      {
        status: 462,
        description: `User is already a student of another driving school`,
        schema: z.void(),
      },
      {
        status: 464,
        description: `Provided identity data does not match logged-in user`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/webapp/sign-up/offers/:slug",
    alias: "getSignUpOffer",
    requestFormat: "json",
    parameters: [
      {
        name: "slug",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: SignUpOfferDto,
    errors: [
      {
        status: 404,
        description: `Offer or driving school not found, or offer has no quotes`,
        schema: z.void(),
      },
      {
        status: 502,
        description: `Upstream service is unavailable or returned invalid signup data`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/webapp/sign-up/quotes/:quoteId",
    alias: "getSignUpQuote",
    requestFormat: "json",
    parameters: [
      {
        name: "quoteId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: SignUpQuoteDto,
    errors: [
      {
        status: 404,
        description: `Quote or driving school not found`,
        schema: z.void(),
      },
      {
        status: 502,
        description: `Upstream service is unavailable or returned invalid signup data`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/webapp/student-onboarding-state",
    alias: "getStudentOnboardingState",
    requestFormat: "json",
    response: StudentOnboardingStateDto,
    errors: [
      {
        status: 404,
        description: `Onboarding state has not been initialized`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "patch",
    path: "/webapp/student-onboarding-state",
    alias: "updateStudentOnboardingState",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: StudentOnboardingStateUpdateDto,
      },
    ],
    response: StudentOnboardingStateDto,
  },
  {
    method: "get",
    path: "/webapp/student-onboarding-state/users/:userId",
    alias: "getStudentOnboardingStateForUser",
    requestFormat: "json",
    parameters: [
      {
        name: "userId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: StudentOnboardingStateDto,
    errors: [
      {
        status: 404,
        description: `Onboarding state has not been initialized`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "patch",
    path: "/webapp/student-onboarding-state/users/:userId",
    alias: "updateStudentOnboardingStateForUser",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: StudentOnboardingStateUpdateDto,
      },
      {
        name: "userId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: StudentOnboardingStateDto,
  },
  {
    method: "put",
    path: "/webapp/student-onboarding-state/users/:userId",
    alias: "importStudentOnboardingStateForUser",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: StudentOnboardingStateImportDto,
      },
      {
        name: "userId",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: StudentOnboardingStateDto,
  },
]);

export const api = new Zodios(endpoints);

export function createApiClient(baseUrl: string, options?: ZodiosOptions) {
  return new Zodios(baseUrl, endpoints, options);
}
